import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { localizeOptions, optionLabel } from '@/i18n/chartText';
import {
  NET_UNIT_KEYS,
  OUTER_SHAPE_KEYS,
  OUTER_TYPE_KEYS,
  PACKED_KEYS,
  PIECE_UNIT_KEYS,
  SENSITIVE_KEYS,
  SKU_CLASS_KEYS,
  YES_NO_KEYS,
} from '@/i18n/pkgOptionKeys';
import { labeledSiteOptions } from '@/i18n/siteOptions';
import { localizeRegionOptions, localizeCnProvinceOptions } from '@/i18n/catalogDisplayText';
import { useNavigate } from 'react-router-dom';
import RequiredMark from '@/temu-platform/components/RequiredMark';
import AttributeField from '@/temu-platform/modules/products/components/AttributeField';
import CategoryCascaderSelect from '@/temu-platform/components/CategoryCascaderSelect';
import SearchableSelect from '@/temu-platform/modules/products/components/SearchableSelect';
import SearchableMultiSelect from '@/temu-platform/modules/products/components/SearchableMultiSelect';
import SpecSupplySection, { skcHasColor } from '@/temu-platform/modules/products/components/SpecSupplySection';
import TemuOfficialSizeChartPanel, {
  fetchSizeChartClassRequired,
} from '@/temu-platform/modules/products/components/TemuOfficialSizeChartPanel';
import TemuModelSelectBar from '@/temu-platform/modules/products/components/TemuModelSelectBar';
import TemuVehicleFitmentPanel from '@/temu-platform/modules/products/components/TemuVehicleFitmentPanel';
import TemuGuideFilePanel from '@/temu-platform/modules/products/components/TemuGuideFilePanel';
import FieldHelpMark from '@/components/FieldHelpMark';
import PublishScrollJumpFab from '@/components/publish/PublishScrollJumpFab';
import {
  buildTemuModelTryOnSizeOptions,
  officialSizeChartRecordsHaveSizeNames,
} from '@/temu-platform/utils/temuOfficialSizeChart';
import PackageBatchModal, {
  PACKAGE_TYPE_OPTIONS,
} from '@/temu-platform/modules/products/components/PackageBatchModal';
import {
  TEMU_OUTER_PACKAGE_SHAPE_OPTIONS,
  TEMU_OUTER_PACKAGE_TYPE_OPTIONS,
  coerceOuterPackageFormFields,
} from '@/temu-platform/utils/temuOuterPackage';
import PublishImageUploadZone from '@/temu-platform/modules/products/components/PublishImageUploadZone';
import TitleFieldLoadingShell from '@/temu-platform/modules/products/components/TitleFieldLoadingShell';
import { applyPackageBatchBySize, applyPackageBatchToAllSkus } from '@/temu-platform/utils/temuPackageBatch';
import {
  TEMU_DIM_UNIT,
  TEMU_INDIVIDUALLY_PACKED_OPTIONS,
  TEMU_PIECE_UNIT_OPTIONS,
  TEMU_SENSITIVE_LIST_OPTIONS,
  TEMU_SENSITIVE_YES_NO_OPTIONS,
  TEMU_SKU_CLASSIFICATION_OPTIONS,
  TEMU_NET_CONTENT_UNIT_OPTIONS,
  TEMU_WEIGHT_UNIT,
  sensitiveListFromSlots,
  sensitiveSlotsFromList,
} from '@/temu-platform/utils/temuSkuPackage';
import SaveAttributeTemplateMetaModal from '@/temu-platform/modules/templates/SaveAttributeTemplateMetaModal';
import SaveProductTemplateMetaModal from '@/temu-platform/modules/templates/SaveProductTemplateMetaModal';
import { buildTemuPath } from '@/temu-platform/constants/temuRoutes';
import { useTemuShop } from '@/temu-platform/context/TemuShopContext';
import { TEMU_MODE_FULL, TEMU_MODE_SEMI, temuShopCapabilities } from '@/temu-platform/utils/temuShopCapabilities';
import { flattenLeafCategories, isFullerCategoryPath } from '@/temu-platform/utils/temuCategoryTree';
import {
  calcPublishCompletion,
  parseAttributeTemplate,
  partitionProductAttrsForDisplay,
  attributeDisplayLabel,
  attributeValueDisplayLabel,
  resolveSkcColorAgainstTemplate,
} from '@/temu-platform/utils/temuAttributeTemplate';
import { applyShopBrandsToAttrTemplate } from '@/temu-platform/utils/temuShopBrandAttrs';
import { layoutAttributeFields } from '@/temu-platform/utils/temuAttributeLayout';
import {
  isEditorAttrRequiredMark,
  isEditorAttrVisible,
  editorAttrAllowedValueIds,
  pruneProductAttributesForSubmit,
} from '@/temu-platform/utils/temuAttrVisibility';
import { parseCustomAttributePermission } from '@/temu-platform/utils/temuCustomAttribute';
import {
  applyProductAttributeTemplatePayload,
  countFilledProductAttributes,
  alignAttributesToCategoryTemplate,
  pickFilledProductAttributes,
  remapProductAttributesAcrossCategories,
  remapSizeChartAcrossCategories,
  remapSkcListAcrossCategories,
  alignSkcSizesToSaleAttr,
} from '@/temu-platform/utils/temuProductAttributeTemplate';
import {
  readProductTemplatePublishHandoff,
  scheduleClearProductTemplatePublishHandoff,
} from '@/temu-platform/utils/temuProductTemplatePublishHandoff';
import { pickAutoRuleTemplate, pickHighestPriorityTemplate, sortTemplatesByPriority } from '@/temu-platform/utils/temuTemplateAutoRule';
import { styleNoFromSupplierCode } from '@/temu-platform/utils/temuStyleNo';
import {
  checkTemplateVersions,
  collectAppliedTemplateRefs,
  conflictFieldKey,
  conflictToastMessage,
  extractAttrTemplatePatch,
  extractSizeChartTemplatePatch,
  isMissingTemplateError,
  mergeProductTemplateIntoForm,
  parseTemplateVersion,
  partitionTemplateVersionConflicts,
  TEMPLATE_VERSION_TYPES,
} from '@/temu-platform/utils/temuTemplateVersionGuard';
import {
  diffProductAttributeChanges,
  diffSizeChartChanges,
  indexAttributeChangeHints,
} from '@/temu-platform/utils/temuTemplateChangeDiff';
import TemplateVersionConflictMark from '@/temu-platform/modules/products/components/TemplateVersionConflictMark';
import TemplateChangeDiffList from '@/temu-platform/modules/products/components/TemplateChangeDiffList';
import {
  customLabelFromKey,
  isCustomSpecKey,
  pruneSizeChartForSubmit,
  skuSizeKey,
} from '@/temu-platform/utils/temuSizeChart';
import { buildTemuPublishPayload, samplePublishPayloadCurrency } from '@/temu-platform/utils/buildTemuPublishPayload';
import {
  normalizeTemuOriginRegion,
  resolveTemuRegion1ShortName,
  TEMU_CN_REGION2_OPTIONS,
  TEMU_REGION1_OPTIONS,
} from '@/temu-platform/utils/temuProductOrigin';
import {
  getTemuShipmentLimitOptions,
  isTemuCustomProductMode,
  pickTemuShipmentLimitSecond,
} from '@/temu-platform/utils/temuShipmentLimitOptions';
import {
  emptyTemuCustomizedTechnology,
  listTemuCustomFirstTypeOptions,
  listTemuCustomTwiceTypeIds,
  listTemuCustomTwiceTypeOptions,
  TEMU_CUSTOM_TECHNOLOGY_TYPE_OPTIONS,
} from '@/temu-platform/utils/temuCustomizedTechnology';
import { ensureTemuColorBlocksOnForm } from '@/temu-platform/utils/temuColorBlock';
import { ensureTemuMaterialFromMainOnForm } from '@/temu-platform/utils/temuMaterialFromMain';
import {
  enrichFormWithPlatformSkcIds,
  validateEditPlatformIds,
} from '@/temu-platform/utils/buildTemuEditPayload';
import { buildTemuGoodsUpdatePayload } from '@/temu-platform/utils/buildTemuPublishPayload';
import {
  buildSaveSalePricePayload,
  buildUpdateCostPayload,
  chunkUpdateCostPayload,
  collectSkuPriceChanges,
  extractPriceLocksFromSearch,
  interpretPriceUpdateResponse,
  validatePriceChangesAgainstLocks,
} from '@/temu-platform/utils/buildTemuPriceUpdate';
import PriceUpdateConfirmModal from '@/temu-platform/modules/products/components/PriceUpdateConfirmModal';
import {
  EMPTY_SKU_PACKAGE_CAPS,
  pickPublishCurrency,
  resolveSkuPackageCapabilities,
} from '@/temu-platform/utils/temuPublishFillInStandard';
import {
  extractTemuPublishedSpu,
  extractTemuPublishFailMessage,
  isTemuPublishSuccess,
} from '@/temu-platform/utils/temuPublishResult';
import {
  extractTemuPublishFieldErrors,
  fieldErrorsToMap,
} from '@/temu-platform/utils/mapTemuPublishFieldErrors';
import { loadShopPublishCurrencies } from '@/temu-platform/utils/resolveTemuPublishCurrency';
import { listEnabledSites, pickSiteListCurrency } from '@/temu-platform/utils/temuSiteList';
import { isTemuModelRequiredForCategory } from '@/temu-platform/utils/temuModelRequiredCategories';
import { isTemuNetWeightRequiredForCategory } from '@/temu-platform/utils/temuNetWeightRequiredCategories';
import { isTemuSemiSkuNetContentRequired } from '@/temu-platform/utils/temuSemiNetContentRequiredCategories';
import { isTemuShoeSizeClass } from '@/temu-platform/utils/temuShoeSizeFootMap';
import {
  isTemuVehicleLibraryDataRequired,
  isTemuVehicleLibraryScopeActive,
} from '@/temu-platform/utils/temuVehicleLibraryRequired';
import { validateTemuPublishForm } from '@/temu-platform/utils/validateTemuPublishForm';
import { persistTemuFormImageSlots, sanitizeTemuFormImageSlots } from '@/temu-platform/utils/sanitizeTemuFormImageSlots';
import { resolveUploadThumbSrc } from '@/temu-platform/modules/products/components/publishImageUploadThumbSrc';
import { runTemuPublishPreflight } from '@/temu-platform/utils/temuPublishPreflight';
import { ensureTemuCustomColorSpecs } from '@/temu-platform/utils/ensureTemuCustomColorSpecs';
import { generateTemuPublishAiTitle, translateTemuTitleToEnglish } from '@/temu-platform/utils/temuPublishAiTitle';
import {
  clipTemuProductNameToLimit,
  countTemuProductNameWeight,
  resolveTemuProductNameLimitForForm,
} from '@/temu-platform/utils/temuProductNameLength';
import {
  assessTemuPublishPrintInfringement,
  EMPTY_TEMU_PRINT_INFRINGEMENT,
  normalizeTemuPrintInfringement,
  resolveFirstPrintImageUrl,
} from '@/temu-platform/utils/temuPublishPrintInfringement';
import { buildCreditActionHint } from '@/constants/ipeCredits';
import { confirmIpeCreditAction } from '@/utils/ipeCreditConfirm';
import {
  applyPrintNamesToTemuSkcList,
  enrichTemuFormPrintNames,
  printNamesFromTemuPrintImages,
} from '@/pages/ipe/workbench/utils/applyListingItemToTemuPublishForm';
import { ensureTemuFormImagesHosted } from '@/pages/ipe/workbench/utils/convertListingImageToTemu';
import { temuPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';
import { useUnsavedLeaveGuard } from '@/temu-platform/hooks/useUnsavedLeaveGuard';

function FieldErrorText({ message }) {
  if (!message) return null;
  return <div className="tpo-field__error" role="alert">{message}</div>;
}

/** CN 时 productOrigin.region2Id（Temu 官方省份枚举） */
const CN_REGION2_OPTIONS = TEMU_CN_REGION2_OPTIONS;

/** 半托管常用站点（与官方半托管站点目录 id 对齐） */
const TEMU_SEMI_SITES = [
  ['100', '美国站'],
  ['101', '加拿大站'],
  ['102', '英国站'],
  ['103', '澳大利亚站'],
  ['104', '新西兰站'],
  ['105', '德国站'],
  ['106', '法国站'],
  ['107', '意大利站'],
  ['108', '荷兰站'],
  ['109', '西班牙站'],
  ['110', '墨西哥站'],
  ['118', '日本站'],
  ['125', '智利站'],
  ['163', '秘鲁站'],
  ['164', '哥伦比亚站'],
  ['178', '厄瓜多尔站'],
];

const TEMU_PRODUCT_MODE_OPTIONS = [
  { value: '0', label: '普通品' },
  { value: '1', label: '定制品' },
];

function imageUrlOfSlot(image) {
  if (!image) return '';
  return String(
    image.temuUrl
    || image.publicUrl
    || image.previewUrl
    || image.ipeUrl
    || image.url
    || '',
  ).trim();
}

function hasOfficialSizeChartContent(draft) {
  if (!draft || typeof draft !== 'object') return false;
  if (Array.isArray(draft.selectedBusinessIds) && draft.selectedBusinessIds.some((id) => Number(id) > 0)) {
    return true;
  }
  if (String(draft.selectedLibraryId || '').trim()) return true;
  return !isEmptyOfficialSizeChartDraft(draft);
}

function isEmptyOfficialSizeChartDraft(draft) {
  if (!draft || typeof draft !== 'object') return true;
  const records = Array.isArray(draft.records) ? draft.records : [];
  if (records.length === 0) return true;
  // records: [{ values: { [groupId]: 'S', [elementId]: '40' } }]
  const groupId = String(draft.groupList?.[0]?.id ?? '1');
  return !records.some((row) => {
    if (!row || typeof row !== 'object') return false;
    const values = row.values && typeof row.values === 'object' ? row.values : null;
    if (!values) return false;
    return Object.entries(values).some(([key, val]) => {
      if (String(key) === groupId) return false;
      if (key === 'sizeName' || key === 'groupId' || key === 'name') return false;
      return val != null && String(val).trim() !== '';
    });
  });
}

const SECTIONS = [
  { id: 'basic', titleKey: 'sectionBasic' },
  { id: 'attrs', titleKey: 'sectionAttrs' },
  { id: 'spec', titleKey: 'sectionSpec' },
  { id: 'size', titleKey: 'sectionSize' },
  { id: 'sales', titleKey: 'sectionSales' },
];

function deriveSelectedSizeIds(skcList = []) {
  const ids = [];
  const seen = new Set();
  skcList.forEach((skc) => {
    (skc.skuRows || []).forEach((sku) => {
      const key = skuSizeKey(sku);
      if (key && !seen.has(key)) {
        seen.add(key);
        ids.push(key);
      }
    });
  });
  return ids;
}

function parseSizeSelectionKey(sizeId) {
  const key = String(sizeId || '');
  if (isCustomSpecKey(key)) {
    const label = customLabelFromKey(key);
    return {
      sizeValueId: '',
      sizeIsCustom: true,
      sizeExtraValue: label,
      sizeLabel: label,
    };
  }
  return {
    sizeValueId: key,
    sizeIsCustom: false,
    sizeExtraValue: '',
    sizeLabel: '',
  };
}

function buildSkuRowsForSizes(existingRows = [], sizeIds = [], sizeAttr, defaults = {}) {
  if (!sizeIds.length) {
    // 编辑回填：已有多行 SKU 时绝不能压成一行（模板过滤尺码后 sizeIds 可能暂时为空）
    if (existingRows.length > 1) {
      return existingRows.map((row) => ({
        ...row,
        sizeAttributeId: sizeAttr?.attributeId || row.sizeAttributeId || '',
      }));
    }
    // 未选尺码时保留一行空 SKU，便于仅按颜色填价库存
    const first = existingRows[0];
    return [first
      ? {
        ...first,
        sizeValueId: '',
        sizeIsCustom: false,
        sizeExtraValue: '',
        sizeLabel: '',
        sizeAttributeId: sizeAttr?.attributeId || first.sizeAttributeId || '',
      }
      : createSkuRow({
        sizeAttributeId: sizeAttr?.attributeId || '',
        price: defaults.price || '',
        stock: defaults.stock || '',
        weight: defaults.weight || '',
        netWeight: defaults.netWeight || '',
        currency: defaults.currency || '',
      })];
  }
  return sizeIds.map((sizeId) => {
    const parsed = parseSizeSelectionKey(sizeId);
    const found = existingRows.find((row) => skuSizeKey(row) === String(sizeId));
    if (found) {
      return {
        ...found,
        sizeAttributeId: sizeAttr?.attributeId || found.sizeAttributeId || '',
        sizeValueId: parsed.sizeValueId,
        sizeIsCustom: parsed.sizeIsCustom,
        sizeExtraValue: parsed.sizeExtraValue,
        sizeLabel: parsed.sizeLabel
          || found.sizeLabel
          || sizeAttr?.values?.find((v) => String(v.id) === parsed.sizeValueId)?.name
          || '',
      };
    }
    const platformName = !parsed.sizeIsCustom
      ? (sizeAttr?.values?.find((v) => String(v.id) === parsed.sizeValueId)?.name || '')
      : parsed.sizeLabel;
    return createSkuRow({
      sizeAttributeId: sizeAttr?.attributeId || '',
      sizeValueId: parsed.sizeValueId,
      sizeIsCustom: parsed.sizeIsCustom,
      sizeExtraValue: parsed.sizeExtraValue,
      sizeLabel: platformName,
      price: defaults.price || '',
      stock: defaults.stock || '',
      weight: defaults.weight || '',
      netWeight: defaults.netWeight || '',
      currency: defaults.currency || '',
    });
  });
}

function createEmptyAccessoryRow() {
  return { vid: '', num: '', unitCode: 1, unitName: '件', thumbUrl: '', label: '' };
}

function createSkuRow(partial = {}) {
  return {
    id: `sku-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    sizeAttributeId: '',
    sizeValueId: '',
    sizeIsCustom: false,
    sizeExtraValue: '',
    sizeLabel: '',
    supplierSku: '',
    price: '',
    specialPrice: '',
    currency: '',
    stock: '',
    weight: '',
    netWeight: '',
    netContent: '',
    netContentUnitCode: '5',
    length: '',
    width: '',
    height: '',
    quantity: '1',
    quantityUnit: '1',
    numberOfPieces: '1',
    pieceUnitCode: '1',
    totalContains: '',
    skuClassification: '1',
    individuallyPacked: '',
    packageType: '',
    isSensitive: 0,
    sensitiveList: [],
    suggestedPrice: '',
    suggestedPriceCurrencyType: '',
    accessories: [createEmptyAccessoryRow()],
    mallState: 1,
    skuImage: null,
    skuCode: '',
    siteRrpInfoList: [],
    ...partial,
  };
}

function createSkc(partial = {}) {
  return {
    id: `skc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    colorAttributeId: '',
    colorValueId: '',
    colorLabel: '',
    colorCode: '',
    colorIsCustom: false,
    colorExtraValue: '',
    supplierCode: '',
    skcName: '',
    mainImage: null,
    showroomSlots: {},
    detailImages: [],
    squareImage: null,
    colorBlockImage: null,
    skuRows: [createSkuRow()],
    ...partial,
  };
}

function deriveFormCurrency(initial = {}) {
  const top = String(initial.currency || '').trim();
  if (top) return top;
  for (const skc of initial.skcList || []) {
    for (const sku of skc.skuRows || []) {
      const code = String(sku?.currency || '').trim();
      if (code) return code;
    }
  }
  return '';
}

/** 兼容后端旧形态 {attributeValueId,attributeValueName} → 前端 AttributeField 选中值 */
/** 未选仓库时默认第一个商家仓（提交用 warehouseCode，展示只用名称） */
function withDefaultWarehouse(form, warehouses = []) {
  const next = form && typeof form === 'object' ? { ...form } : {};
  const rows = Array.isArray(warehouses) ? warehouses : [];
  if (!rows.length) return next;
  const codes = rows
    .map((item) => String(item?.id || item?.warehouseCode || '').trim())
    .filter(Boolean);
  const current = String(next.warehouseId || '').trim();
  if (current && codes.includes(current)) {
    if (!String(next.warehouseName || '').trim()) {
      const hit = rows.find((item) => String(item.id || item.warehouseCode || '') === current);
      next.warehouseName = hit?.name || hit?.warehouseName || '';
    }
    return next;
  }
  const first = rows[0];
  const id = String(first?.id || first?.warehouseCode || '').trim();
  if (!id) return next;
  next.warehouseId = id;
  next.warehouseName = first?.name || first?.warehouseName || '';
  return next;
}

function positiveAttrVid(raw) {
  const text = String(raw ?? '').trim();
  if (!text || text === '0') return '';
  const num = Number(text);
  if (Number.isFinite(num) && num <= 0) return '';
  return text;
}

function handInputAttrText(row) {
  if (!row || typeof row !== 'object') return '';
  return String(row.propValue ?? row.name ?? row.attributeValueName ?? '').trim();
}

function normalizeProductAttributes(raw = {}) {
  const out = {};
  Object.entries(raw || {}).forEach(([id, value]) => {
    if (value == null || value === '') return;
    if (Array.isArray(value)) {
      const asComposition = value.some((row) => (
        row && typeof row === 'object' && ('percent' in row || 'attributeExtraValue' in row || 'valueId' in row)
      ));
      if (asComposition) {
        const handTexts = [];
        const rows = [];
        let handOnly = true;
        value.forEach((row) => {
          if (row != null && (typeof row === 'string' || typeof row === 'number')) {
            handOnly = false;
            rows.push({ valueId: String(row), percent: '' });
            return;
          }
          if (!row || typeof row !== 'object') return;
          const valueId = positiveAttrVid(row.valueId ?? row.attributeValueId ?? row.vid);
          if (!valueId) {
            const text = handInputAttrText(row);
            if (text) handTexts.push(text);
            return;
          }
          handOnly = false;
          rows.push({
            valueId,
            percent: String(row.percent ?? row.attributeExtraValue ?? ''),
          });
        });
        if (handOnly) {
          if (handTexts.length === 1) out[id] = handTexts[0];
          else if (handTexts.length > 1) out[id] = handTexts.join(',');
          return;
        }
        out[id] = rows;
        return;
      }
      out[id] = value
        .map((row) => {
          if (row && typeof row === 'object') {
            return positiveAttrVid(row.valueId ?? row.attributeValueId ?? row.vid) || handInputAttrText(row);
          }
          return String(row ?? '');
        })
        .filter(Boolean);
      return;
    }
    if (typeof value === 'object') {
      const valueId = positiveAttrVid(value.valueId ?? value.attributeValueId ?? value.vid);
      const percent = value.percent ?? value.attributeExtraValue;
      const handText = handInputAttrText(value);
      if (!valueId) {
        if (handText) out[id] = handText;
        else if (value.attributeExtraValue != null && String(value.attributeExtraValue).trim() !== '') {
          out[id] = String(value.attributeExtraValue);
        }
        return;
      }
      if (percent != null && String(percent).trim() !== '') {
        out[id] = [{ valueId, percent: String(percent) }];
      } else {
        out[id] = valueId;
      }
      return;
    }
    out[id] = value;
  });
  return out;
}

function emptyForm(initial = {}) {
  const rawTitle = String(initial.title || initial.productName || '').trim();
  const rawTitleEn = String(initial.titleEn || initial.englishTitle || '').trim();
  const titleLooksEnglishOnly = rawTitle && !/[\u3400-\u9fff]/.test(rawTitle);
  return {
    spuName: initial.spuName || '',
    /** 商品名称 → bg.goods.add productName（可中文） */
    title: rawTitle,
    /** 英文名称 → productI18nReqs language=en */
    titleEn: rawTitleEn || (titleLooksEnglishOnly ? rawTitle : ''),
    description: initial.description || '',
    categoryId: initial.categoryId || '',
    productTypeId: initial.productTypeId || '',
    catPathIds: Array.isArray(initial.catPathIds) ? initial.catPathIds : [],
    categoryPathLabel: initial.categoryPathLabel || '',
    supplierCode: initial.supplierCode || '',
    brandCode: initial.brandCode || '',
    originCountry: normalizeTemuOriginRegion(
      initial.originCountry
      || initial.originRegion
      || initial.productOrigin?.region1ShortName
      || initial.productOrigin?.countryShortName,
    ) || '',
    /** Temu productOrigin.region1ShortName（二字简码） */
    originRegion: normalizeTemuOriginRegion(
      initial.originRegion
      || initial.originCountry
      || initial.productOrigin?.region1ShortName
      || initial.productOrigin?.countryShortName,
    ) || 'CN',
    /** CN 时省份 region2Id */
    originRegion2Id: (() => {
      const raw = initial.originRegion2Id ?? initial.region2Id ?? initial.productOrigin?.region2Id;
      return raw != null && String(raw).trim() !== '' ? String(raw) : '';
    })(),
    ipId: initial.ipId || '',
    ipName: initial.ipName || '',
    ipNameCn: initial.ipNameCn || '',
    productAttributes: normalizeProductAttributes(initial.productAttributes || {}),
    skcList: Array.isArray(initial.skcList) ? initial.skcList : [],
    selectedSizeIds: Array.isArray(initial.selectedSizeIds)
      ? initial.selectedSizeIds
      : deriveSelectedSizeIds(initial.skcList),
    printImages: Array.isArray(initial.printImages) ? initial.printImages : [],
    ipePrintImageNames: Array.isArray(initial.ipePrintImageNames)
      ? initial.ipePrintImageNames
      : printNamesFromTemuPrintImages(initial.printImages),
    ipePrintInfringement: normalizeTemuPrintInfringement(
      initial.ipePrintInfringement || EMPTY_TEMU_PRINT_INFRINGEMENT,
    ),
    /** Temu 素材图（materialImgUrl）；须单独上传 1:1 图，勿用 3:4 主图冒充 */
    materialImage: initial.materialImage || null,
    materialImgUrl: initial.materialImgUrl
      || (initial.materialImage?.temuUrl ? String(initial.materialImage.temuUrl).trim() : '')
      || '',
    /** Temu 主图视频 productCarouseVideoReqList */
    temuVideos: Array.isArray(initial.temuVideos) ? initial.temuVideos : [],
    /** Temu 官方尺码表模板 ID */
    temuSizeTemplateIds: Array.isArray(initial.temuSizeTemplateIds)
      ? initial.temuSizeTemplateIds.map(String)
      : [],
    /** Temu 商品模特 goodsModelReqs */
    goodsModels: Array.isArray(initial.goodsModels) ? initial.goodsModels : [],
    /** 兼容车型 goodsVehiclePropertyRelation（适用车型=见详情时可能必填） */
    goodsVehiclePropertyRelation: (
      initial.goodsVehiclePropertyRelation
      && typeof initial.goodsVehiclePropertyRelation === 'object'
    ) ? initial.goodsVehiclePropertyRelation : null,
    /** 电子说明书 productGuideFileReqs */
    guideFiles: Array.isArray(initial.guideFiles) ? initial.guideFiles : [],
    /** 产地证明（全托管非 CN 必填） */
    originCertFiles: Array.isArray(initial.originCertFiles) ? initial.originCertFiles : [],
    /** 货品级外包装（平台必填：shape+type+外包装图） */
    ...(() => {
      const coerced = coerceOuterPackageFormFields(initial);
      return {
        packageShape: coerced.packageShape,
        packageTypeOuter: coerced.packageTypeOuter,
        outerPackageImages: Array.isArray(initial.outerPackageImages)
          ? initial.outerPackageImages
          : [],
      };
    })(),
    freightTemplateId: initial.freightTemplateId || '',
    /** 0 普通品 / 1 定制品 — 影响承诺发货时效可选值与定制工艺必填 */
    isCustomProduct: isTemuCustomProductMode(
      initial.isCustomProduct ?? initial.personalizationSwitch,
    ) ? 1 : 0,
    ...(() => {
      const tech = emptyTemuCustomizedTechnology(initial);
      return {
        customFirstType: tech.firstType,
        customTechnologyType: tech.technologyType,
        customTwiceTypes: tech.twiceTypes,
        customManufacturingLocations: tech.manufacturingLocations,
      };
    })(),
    shipmentLimitSecond: initial.shipmentLimitSecond != null
      ? String(initial.shipmentLimitSecond)
      : '172800',
    isSensitive: initial.isSensitive != null ? Number(initial.isSensitive) : 0,
    sizeChart: initial.sizeChart || {},
    officialSizeChart: initial.officialSizeChart && typeof initial.officialSizeChart === 'object'
      ? initial.officialSizeChart
      : null,
    sizeSaleAttributeId: initial.sizeSaleAttributeId || '',
    supplyPrice: initial.supplyPrice || '',
    currency: deriveFormCurrency(initial),
    stockQty: initial.stockQty || '',
    weightGrams: initial.weightGrams || '',
    netWeightGrams: initial.netWeightGrams || '',
    weightUnit: initial.weightUnit || 'g',
    pkgLength: initial.pkgLength || '10',
    pkgWidth: initial.pkgWidth || '10',
    pkgHeight: initial.pkgHeight || '10',
    dimUnit: initial.dimUnit || 'cm',
    warehouseId: initial.warehouseId || '',
    warehouseName: initial.warehouseName || '',
    selectedSites: Array.isArray(initial.selectedSites) ? initial.selectedSites : [],
    mainSite: initial.mainSite || 'temu',
    shelfWay: initial.shelfWay || '1',
    hopeOnSaleDate: initial.hopeOnSaleDate || '',
  };
}

export default function ProductPublishEditor({
  mode = 'create',
  spuName: editSpuName = '',
  draftId: initialDraftId = '',
  recordId: initialRecordId = '',
  productTemplateId: initialProductTemplateId = '',
  fromProductTemplate = false,
  referenceShopId = '',
}) {
  const { t, i18n } = useTranslation('workbench');
  const isEdit = mode === 'edit';
  const navigate = useNavigate();
  const { activeShop } = useTemuShop();
  const shopId = referenceShopId || activeShop?.id;
  const shopCaps = useMemo(
    () => temuShopCapabilities(activeShop?.shopType),
    [activeShop?.shopType],
  );
  const priceMode = shopCaps.canUpdateSalePrice ? 'sale' : (shopCaps.canUpdateCost ? 'cost' : '');
  const isSemiManaged = shopCaps.mode === TEMU_MODE_SEMI;
  const isFullManaged = shopCaps.mode === TEMU_MODE_FULL;
  // 半托/全托 PA goods.add 都要求 USD，忽略表单里旧默认 CNY
  const forcePublishCurrencyDefault = isSemiManaged || isFullManaged;
  const skipSiteWarehouseFreight = Boolean(shopCaps.skipSiteWarehouseFreight);
  const needsFormBootstrap = Boolean(
    !isEdit && (initialDraftId || initialProductTemplateId || fromProductTemplate),
  );
  const [draftId, setDraftId] = useState(() => (!isEdit && initialDraftId ? String(initialDraftId) : ''));
  const [draftLoading, setDraftLoading] = useState(() => needsFormBootstrap);
  const [draftHydrated, setDraftHydrated] = useState(() => !needsFormBootstrap);
  const [activeSection, setActiveSection] = useState('basic');
  const [form, setForm] = useState(() => emptyForm({}));
  const [meta, setMeta] = useState({ categories: null, sites: null, warehouses: [] });
  const warehouseListRef = useRef(meta.warehouses);
  warehouseListRef.current = meta.warehouses;
  const [metaLoading, setMetaLoading] = useState(false);
  /** 半托管/全托管：发布规范 【currency】；自运营：query-site-list 自营站点币种 */
  const [publishCurrencies, setPublishCurrencies] = useState([]);
  const [publishCurrencyLoading, setPublishCurrencyLoading] = useState(false);
  /** 按末级类目加载的发布规范：门控 SKU 件数 / 包装类型 */
  const [skuPkgCaps, setSkuPkgCaps] = useState(EMPTY_SKU_PACKAGE_CAPS);
  const [skuPkgCapsLoading, setSkuPkgCapsLoading] = useState(false);
  /** 关联属性必填规则：Temu 不走 Shein get-associated-attribute-rules */
  const [attrTpl, setAttrTpl] = useState(null);
  const [attrLoading, setAttrLoading] = useState(false);
  const [attrError, setAttrError] = useState('');
  /** 官方尺码表：bg.goods.sizecharts.class.get */
  const [sizeChartRequired, setSizeChartRequired] = useState(false);
  const [sizeChartClass, setSizeChartClass] = useState(null);
  const [sizeChartClassLoading, setSizeChartClassLoading] = useState(false);
  const [sizeChartClassError, setSizeChartClassError] = useState('');
  const officialSizeChartRef = useRef(null);
  const [attrsExpanded, setAttrsExpanded] = useState(false);
  const [attrTemplates, setAttrTemplates] = useState([]);
  const [selectedAttrTemplateId, setSelectedAttrTemplateId] = useState('');
  const [selectedSizeChartTemplateId, setSelectedSizeChartTemplateId] = useState('');
  const [productTemplates, setProductTemplates] = useState([]);
  const [selectedProductTemplateId, setSelectedProductTemplateId] = useState('');
  const [selectedProductTemplateVersion, setSelectedProductTemplateVersion] = useState(null);
  const [selectedAttrTemplateVersion, setSelectedAttrTemplateVersion] = useState(null);
  const [selectedSizeChartTemplateVersion, setSelectedSizeChartTemplateVersion] = useState(null);
  const [versionConflicts, setVersionConflicts] = useState({});
  const [changePreviews, setChangePreviews] = useState({});
  const [reloadingConflictType, setReloadingConflictType] = useState('');
  const [productTemplateBusy, setProductTemplateBusy] = useState(false);
  const [attrTemplateBusy, setAttrTemplateBusy] = useState(false);
  const [attrSaveModalOpen, setAttrSaveModalOpen] = useState(false);
  const [productTplSaveModalOpen, setProductTplSaveModalOpen] = useState(false);
  /** 分类下可自定义属性值的 attributeId 集合 */
  const [customAttrPerm, setCustomAttrPerm] = useState({});
  /** 类目切换后待自动引用的 productTypeId（空=不自动） */
  const pendingAttrAutoForRef = useRef('');
  /** 类目切换后待自动套用产品模板的 productTypeId */
  const pendingProductTplAutoForRef = useRef('');
  /** 切类目时暂存旧类目已填参数，待新 schema 就绪后按相似字段回填 */
  const pendingCategoryPreserveRef = useRef(null);
  const categorySelectSeqRef = useRef(0);
  const attrTemplatesLoadedForRef = useRef('');
  /** 上次属性库自动套用所用的款号（小写）；用于货号变更时重匹配，避免编辑回填误套 */
  const lastAttrAutoStyleKeyRef = useRef(null);
  const attrTplRef = useRef(null);
  const productTemplateHandoffAppliedRef = useRef('');
  const [submitting, setSubmitting] = useState(false);
  const [aiTitleBusy, setAiTitleBusy] = useState(false);
  const [titleTranslateBusy, setTitleTranslateBusy] = useState(false);
  const titleTranslateSeqRef = useRef(0);
  /** 最近一次自动译出的英文；用户改过后与之不同，防抖翻译不再覆盖 */
  const lastAutoTitleEnRef = useRef('');
  const [printInfringementBusy, setPrintInfringementBusy] = useState(false);
  const [submitHint, setSubmitHint] = useState('');
  /** 编辑改价确认：{ submitForm, changes } */
  const [priceConfirm, setPriceConfirm] = useState(null);
  /** 半托运费模板 */
  const [freightOptions, setFreightOptions] = useState([]);
  const [freightLoading, setFreightLoading] = useState(false);
  /** 包装清单物品类型（发品：bg.glo.goods.accessories.get） */
  const [accessoryOptions, setAccessoryOptions] = useState([]);
  const [accessoryLoading, setAccessoryLoading] = useState(false);
  const [accessoryLoadError, setAccessoryLoadError] = useState('');
  const [accessoryReloadKey, setAccessoryReloadKey] = useState(0);
  const [needGuideFile, setNeedGuideFile] = useState(false);
  const [needGuideLoading, setNeedGuideLoading] = useState(false);
  const [originCertBusy, setOriginCertBusy] = useState(false);

  const [savingDraft, setSavingDraft] = useState(false);
  const [loadingSpu, setLoadingSpu] = useState(false);
  const editBaselineRef = useRef(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [pkgBatchMode, setPkgBatchMode] = useState(null);

  const formBootLoading = !draftHydrated
    || draftLoading
    || Boolean(isEdit && loadingSpu)
    || metaLoading
    || publishCurrencyLoading;
  const { allowLeave: allowUnsavedLeave, runWithoutUnloadPrompt } = useUnsavedLeaveGuard({
    value: form,
    enabled: Boolean(shopId),
    loading: formBootLoading,
    settleMs: 1600,
  });

  const categoryOptions = useMemo(() => flattenLeafCategories(meta.categories), [meta.categories]);
  const siteOptions = useMemo(() => listEnabledSites(meta.sites), [meta.sites]);
  const siteSelectOptions = useMemo(() => {
    void i18n.language;
    if (isSemiManaged) {
      return labeledSiteOptions('temuSite', TEMU_SEMI_SITES);
    }
    return siteOptions.map((site) => ({
      value: site.siteAbbr,
      label: site.siteName || site.siteAbbr,
    }));
  }, [isSemiManaged, siteOptions, i18n.language]);

  /** 半托管/全托管走发布规范币种；自运营走站点列表（随已选站点对齐） */
  const platformCurrency = useMemo(() => {
    if (shopCaps.usesPriceInfo) {
      return pickSiteListCurrency(meta.sites, {
        preferred: form.currency,
        selectedSites: form.selectedSites,
      });
    }
    // 半托/全托 PA goods.add 要求 USD：忽略表单里旧默认 CNY
    return pickPublishCurrency(publishCurrencies, form.currency, {
      forceDefault: forcePublishCurrencyDefault,
      defaultCode: forcePublishCurrencyDefault ? 'USD' : '',
    });
  }, [form.currency, form.selectedSites, forcePublishCurrencyDefault, meta.sites, publishCurrencies, shopCaps.usesPriceInfo]);

  const applyPublishCurrency = (code) => {
    const next = String(code || '').trim().toUpperCase();
    if (!next) return;
    setForm((prev) => {
      if (String(prev.currency || '').trim().toUpperCase() === next) return prev;
      return {
        ...prev,
        currency: next,
        skcList: (prev.skcList || []).map((skc) => ({
          ...skc,
          skuRows: (skc.skuRows || []).map((sku) => ({
            ...sku,
            currency: next,
          })),
        })),
      };
    });
  };

  const selectedSitesKey = useMemo(
    () => (Array.isArray(form.selectedSites) ? form.selectedSites.map(String).filter(Boolean).join('|') : ''),
    [form.selectedSites],
  );

  const shipmentLimitOptions = useMemo(
    () => getTemuShipmentLimitOptions({
      siteIds: selectedSitesKey ? selectedSitesKey.split('|') : [],
      customProduct: form.isCustomProduct,
    }),
    [selectedSitesKey, form.isCustomProduct],
  );

  const customFirstTypeOptions = useMemo(
    () => listTemuCustomFirstTypeOptions(form.customTechnologyType),
    [form.customTechnologyType],
  );
  const customTwiceTypeOptions = useMemo(
    () => listTemuCustomTwiceTypeOptions(form.customTechnologyType, form.customFirstType),
    [form.customTechnologyType, form.customFirstType],
  );
  const customTwiceAllowedIds = useMemo(
    () => listTemuCustomTwiceTypeIds(form.customTechnologyType, form.customFirstType),
    [form.customTechnologyType, form.customFirstType],
  );
  const customTwiceRequired = Array.isArray(customTwiceAllowedIds) && customTwiceAllowedIds.length > 0;

  // 站点/普通品·定制品变化后，校正不在枚举内的时效
  useEffect(() => {
    if (!isSemiManaged || !shipmentLimitOptions.length) return;
    const next = pickTemuShipmentLimitSecond(form.shipmentLimitSecond, shipmentLimitOptions);
    if (!next || String(form.shipmentLimitSecond || '') === next) return;
    setForm((prev) => ({ ...prev, shipmentLimitSecond: next }));
  }, [isSemiManaged, shipmentLimitOptions, form.shipmentLimitSecond]);

  // 币种就绪后回写表单：半托管/全托管按发布规范；自运营按站点列表（已选站点优先）。
  useEffect(() => {
    if (!publishCurrencies.length) return;
    const selectedSites = selectedSitesKey ? selectedSitesKey.split('|') : [];
    const next = shopCaps.usesPriceInfo
      ? pickSiteListCurrency(meta.sites, {
        preferred: form.currency,
        selectedSites,
      })
      : pickPublishCurrency(publishCurrencies, form.currency, {
        // 半托/全托勿粘住历史 CNY，否则 bg.glo.goods.add 报 1000002 要求 USD
        forceDefault: forcePublishCurrencyDefault,
        defaultCode: forcePublishCurrencyDefault ? 'USD' : '',
      });
    if (!next) return;
    if (String(form.currency || '').trim().toUpperCase() === String(next).trim().toUpperCase()) return;
    applyPublishCurrency(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 规范/站点币种或回填变化时对齐
  }, [publishCurrencies, form.currency, selectedSitesKey, meta.sites, shopCaps.usesPriceInfo, forcePublishCurrencyDefault]);

  const warehouseOptions = useMemo(() => {
    const rows = Array.isArray(meta.warehouses) ? meta.warehouses : [];
    const mapped = rows
      .map((item) => {
        const id = String(item.id || item.warehouseCode || '');
        const name = String(item.name || item.warehouseName || '').trim();
        return {
          value: id,
          // 下拉只展示仓库名称；提交仍用 value=warehouseCode
          label: name || id,
          name: name || id,
        };
      })
      .filter((item) => item.value);
    if (form.warehouseId && !mapped.some((item) => item.value === String(form.warehouseId))) {
      const fallbackName = String(form.warehouseName || '').trim();
      mapped.unshift({
        value: String(form.warehouseId),
        label: fallbackName || String(form.warehouseId),
        name: fallbackName || String(form.warehouseId),
      });
    }
    return mapped;
  }, [meta.warehouses, form.warehouseId, form.warehouseName]);
  const multiWarehouse = warehouseOptions.length > 1;

  // 库存仓库：默认第一个。编辑回填可能清空 warehouseId，需在列表就绪或选中失效时再默认一次。
  // 全托管/本土跳过站点·仓·运费，不自动写入 warehouseId。
  useEffect(() => {
    if (skipSiteWarehouseFreight) return;
    const rows = Array.isArray(meta.warehouses) ? meta.warehouses : [];
    if (!rows.length) return;
    setForm((prev) => {
      const current = String(prev.warehouseId || '').trim();
      const codes = rows
        .map((item) => String(item.id || item.warehouseCode || '').trim())
        .filter(Boolean);
      if (current && codes.includes(current)) {
        // 补全名称（仅有 code 时）
        if (!String(prev.warehouseName || '').trim()) {
          const hit = rows.find((item) => String(item.id || item.warehouseCode || '') === current);
          const name = hit?.name || hit?.warehouseName || '';
          if (name) return { ...prev, warehouseName: name };
        }
        return prev;
      }
      const first = rows[0];
      const id = String(first.id || first.warehouseCode || '').trim();
      if (!id) return prev;
      return {
        ...prev,
        warehouseId: id,
        warehouseName: first.name || first.warehouseName || '',
      };
    });
  }, [meta.warehouses, form.warehouseId, skipSiteWarehouseFreight]);
  const colorAttr = attrTpl?.skcSaleAttrs?.[0] || null;
  const sizeAttr = attrTpl?.skuSaleAttrs?.[0] || null;
  const sizeMeasureAttrsBase = attrTpl?.sizeAttrs;
  const sizeMeasureAttrs = useMemo(
    () => (Array.isArray(sizeMeasureAttrsBase) ? sizeMeasureAttrsBase : []),
    [sizeMeasureAttrsBase],
  );
  const selectedSizeLabels = useMemo(() => {
    const labels = [];
    const seen = new Set();
    const push = (key, label) => {
      const k = String(key || '').trim();
      const name = String(label || '').trim();
      if (!k || seen.has(k) || !name) return;
      seen.add(k);
      labels.push(name);
    };
    (form.skcList || []).forEach((skc) => {
      (skc.skuRows || []).forEach((sku) => {
        const key = skuSizeKey(sku);
        const fromOpt = sizeAttr?.values?.find((v) => String(v.id) === String(sku.sizeValueId));
        push(
          key,
          sku.sizeLabel || sku.sizeExtraValue || fromOpt?.name || attributeValueDisplayLabel(fromOpt),
        );
      });
    });
    (form.selectedSizeIds || []).forEach((id) => {
      const fromOpt = sizeAttr?.values?.find((v) => String(v.id) === String(id));
      push(id, fromOpt?.name || attributeValueDisplayLabel(fromOpt) || id);
    });
    return labels;
  }, [form.skcList, form.selectedSizeIds, sizeAttr]);
  const modelTryOnSizeOptions = useMemo(() => buildTemuModelTryOnSizeOptions({
    sizeAttr,
    attrTpl,
    selectedSizeIds: form.selectedSizeIds,
    skcList: form.skcList,
  }), [sizeAttr, attrTpl, form.selectedSizeIds, form.skcList]);
  /** 部分叶子类目模特必填（平台公告清单，与尺码表必填独立） */
  const modelRequired = useMemo(
    () => isTemuModelRequiredForCategory(form.categoryId),
    [form.categoryId],
  );
  /** 发品模特类型由类目锁定：鞋类尺码表 → 鞋类模特，其余（含服装）→ 成衣模特 */
  const publishModelType = useMemo(
    () => (isTemuShoeSizeClass(sizeChartClass?.classId) ? '1' : '0'),
    [sizeChartClass?.classId],
  );
  /** 部分叶子类目货品净重必填 → productSkuNetWeightReq（页面 g，提交转 mg） */
  const netWeightRequired = useMemo(
    () => isTemuNetWeightRequiredForCategory(form.categoryId),
    [form.categoryId],
  );
  /** 半托管 + 清单类目：SKU 分类与净含量必填 */
  const semiSkuNetContentRequired = useMemo(
    () => isTemuSemiSkuNetContentRequired({
      categoryId: form.categoryId,
      shopType: activeShop?.shopType,
    }),
    [form.categoryId, activeShop?.shopType],
  );
  const vehicleLibraryScopeActive = useMemo(
    () => isTemuVehicleLibraryScopeActive({
      categoryId: form.categoryId,
      shopType: activeShop?.shopType,
      selectedSites: form.selectedSites,
    }),
    [form.categoryId, activeShop?.shopType, form.selectedSites],
  );
  const vehicleLibraryRequired = useMemo(
    () => isTemuVehicleLibraryDataRequired({
      categoryId: form.categoryId,
      shopType: activeShop?.shopType,
      selectedSites: form.selectedSites,
      attrTpl,
      productAttributes: form.productAttributes,
    }),
    [
      form.categoryId,
      activeShop?.shopType,
      form.selectedSites,
      attrTpl,
      form.productAttributes,
    ],
  );
  /** 货品名称加权上限：按一级类目 cat1Id（公告清单，默认 500） */
  const titleMaxWeight = useMemo(
    () => resolveTemuProductNameLimitForForm({
      catPathIds: form.catPathIds,
      categoryPathIds: form.categoryPathIds,
      cat1Id: form.cat1Id,
    }),
    [form.catPathIds, form.categoryPathIds, form.cat1Id],
  );
  const titleWeight = useMemo(
    () => countTemuProductNameWeight(form.title),
    [form.title],
  );
  const titleEnWeight = useMemo(
    () => countTemuProductNameWeight(form.titleEn),
    [form.titleEn],
  );
  const colorLabel = attributeDisplayLabel(colorAttr) || t('publishSpec.mainSpec');
  const sizeLabel = attributeDisplayLabel(sizeAttr) || t('publishSpec.otherSpec');
  const completion = useMemo(() => calcPublishCompletion(form, attrTpl), [form, attrTpl]);

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev?.[key] && !prev?.form) return prev;
      const next = { ...prev };
      delete next[key];
      delete next.form;
      return next;
    });
  };

  /** 官方尺码表面板回写：禁止 loading/skip 空 draft 冲掉已回填数据；内容相同则跳过，避免外包装等字段改动时死循环 */
  const onOfficialSizeChartDraftChange = useCallback((draft) => {
    setForm((prev) => {
      const nextDraft = draft && typeof draft === 'object' ? draft : null;
      if (prev.officialSizeChart === nextDraft) return prev;
      const nextGroupList = nextDraft?.groupList || prev.officialSizeChart?.groupList || [];
      if (
        hasOfficialSizeChartContent(prev.officialSizeChart)
        && isEmptyOfficialSizeChartDraft(nextDraft)
        && !officialSizeChartRecordsHaveSizeNames(nextDraft?.records, nextGroupList)
      ) {
        return prev;
      }
      const prevDraft = prev.officialSizeChart;
      if (
        prevDraft
        && nextDraft
        && String(prevDraft.mode || '') === String(nextDraft.mode || '')
        && String(prevDraft.selectedLibraryId || '') === String(nextDraft.selectedLibraryId || '')
        && Number(prevDraft.sizeClass?.classId || 0) === Number(nextDraft.sizeClass?.classId || 0)
        && JSON.stringify(prevDraft.selectedBusinessIds || []) === JSON.stringify(nextDraft.selectedBusinessIds || [])
        && JSON.stringify((prevDraft.records || []).map((row) => row?.values || {}))
        === JSON.stringify((nextDraft.records || []).map((row) => row?.values || {}))
      ) {
        return prev;
      }
      return { ...prev, officialSizeChart: nextDraft };
    });
    setFieldErrors((prev) => {
      if (!prev?.sizeChart && !prev?.form) return prev;
      const next = { ...prev };
      delete next.sizeChart;
      delete next.form;
      return next;
    });
  }, []);

  const setAttrValue = (attributeId, value) => {
    setForm((prev) => {
      const draft = { ...prev.productAttributes, [attributeId]: value };
      const pool = [
        ...(attrTpl?.productAttrs || []),
        ...(attrTpl?.requiredProduct || []),
        ...(attrTpl?.optionalProduct || []),
      ];
      const seen = new Set();
      const allAttrs = pool.filter((a) => {
        const id = String(a?.attributeId || '');
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      const pruned = pruneProductAttributesForSubmit(draft, allAttrs);
      return { ...prev, productAttributes: pruned };
    });
  };

  /** 自定义属性值写入本地属性模板，供下拉即时可选 */
  const appendCustomAttrValue = (attributeId, opt) => {
    if (!attributeId || !opt?.id) return;
    setAttrTpl((prev) => {
      if (!prev) return prev;
      const patchList = (list) => (list || []).map((attr) => {
        if (String(attr.attributeId) !== String(attributeId)) return attr;
        if ((attr.values || []).some((v) => String(v.id) === String(opt.id))) return attr;
        return {
          ...attr,
          values: [...(attr.values || []), {
            id: String(opt.id),
            name: opt.name,
            nameCn: opt.nameCn || opt.name,
          }],
        };
      });
      return {
        ...prev,
        productAttrs: patchList(prev.productAttrs),
        requiredProduct: patchList(prev.requiredProduct),
        mainAttrs: patchList(prev.mainAttrs),
        otherAttrs: patchList(prev.otherAttrs),
        optionalProduct: patchList(prev.optionalProduct),
        skcSaleAttrs: patchList(prev.skcSaleAttrs),
        skuSaleAttrs: patchList(prev.skuSaleAttrs),
      };
    });
  };

  const updateSkc = (skcId, patch) => {
    setForm((prev) => ({
      ...prev,
      skcList: prev.skcList.map((skc) => (skc.id === skcId ? { ...skc, ...patch } : skc)),
    }));
  };

  const updatePrintImages = (imgs) => {
    const list = Array.isArray(imgs) ? imgs : [];
    const names = printNamesFromTemuPrintImages(list);
    // 印花图不提交平台；图案编号拼进货号，不改商家 SKU
    setForm((prev) => ({
      ...prev,
      printImages: list,
      ipePrintImageNames: names,
      skcList: applyPrintNamesToTemuSkcList(prev.skcList, names),
      ipePrintInfringement: { ...EMPTY_TEMU_PRINT_INFRINGEMENT },
    }));
  };

  const updateSku = (skcId, skuId, patch) => {
    setForm((prev) => ({
      ...prev,
      skcList: prev.skcList.map((skc) => {
        if (skc.id !== skcId) return skc;
        return {
          ...skc,
          skuRows: skc.skuRows.map((sku) => (sku.id === skuId ? { ...sku, ...patch } : sku)),
        };
      }),
    }));
  };

  // 运费模板（半托发品 productShipmentReq 必填；全托/本土跳过，不预加载）
  useEffect(() => {
    if (!shopId || skipSiteWarehouseFreight) {
      setFreightOptions([]);
      setFreightLoading(false);
      return undefined;
    }
    let cancelled = false;
    setFreightLoading(true);
    temuPlatformAPI.listShopFreightTemplates(shopId)
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.items)
          ? res.items
          : (Array.isArray(res?.freightTemplates) ? res.freightTemplates : []);
        setFreightOptions(rows.map((t) => {
          const id = String(t.freightTemplateId || t.templateId || t.id || '').trim();
          const name = String(t.templateName || t.name || id).trim();
          return { value: id, label: name || id };
        }).filter((t) => t.value));
      })
      .catch(() => {
        if (!cancelled) setFreightOptions([]);
      })
      .finally(() => {
        if (!cancelled) setFreightLoading(false);
      });
    return () => { cancelled = true; };
  }, [shopId, skipSiteWarehouseFreight]);

  // 包装清单选项（发品下拉：accessories.get；选类目后再拉）
  useEffect(() => {
    if (!shopId || !form.categoryId) {
      setAccessoryOptions([]);
      setAccessoryLoadError('');
      setAccessoryLoading(false);
      return undefined;
    }
    let cancelled = false;
    setAccessoryLoading(true);
    setAccessoryLoadError('');
    temuPlatformAPI.listPublishAccessories(shopId, { page: 1, pageSize: 100 })
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.data)
          ? res.data
          : (Array.isArray(res?.result?.data) ? res.result.data : []);
        const opts = (rows || []).map((row) => ({
          value: String(row.vid),
          label: String(row.value || row.vid),
          unitCode: Number(row.unitCode || 1),
          unitName: String(row.unitName || '件'),
          thumbUrl: String(row.thumbUrl || ''),
        })).filter((o) => o.value);
        setAccessoryOptions(opts);
        if (!opts.length) {
          setAccessoryLoadError(t('publishPkg.accEmpty'));
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setAccessoryOptions([]);
        const msg = String(e?.message || e?.userMessage || t('publishPkg.accLoadFail')).trim();
        setAccessoryLoadError(msg);
        showToast(msg.includes('PA') || msg.includes('7000016') || msg.includes('网关')
          ? t('publishPkg.accLoadFailWrap', { message: msg })
          : t('publishPkg.accLoadFailWrap', { message: msg }), 'warning');
      })
      .finally(() => {
        if (!cancelled) setAccessoryLoading(false);
      });
    return () => { cancelled = true; };
  }, [shopId, form.categoryId, accessoryReloadKey, t]);

  // 类目是否必传说明书
  useEffect(() => {
    const catId = Number(form.categoryId || 0);
    if (!shopId || !(catId > 0)) {
      setNeedGuideFile(false);
      setNeedGuideLoading(false);
      return undefined;
    }
    let cancelled = false;
    setNeedGuideLoading(true);
    temuPlatformAPI.getPublishCatsMandatory(shopId, {
      leafCatId: catId,
      configItems: [1],
      bindSiteIds: isSemiManaged
        ? (form.selectedSites || []).map((id) => Number(id)).filter((id) => id > 0)
        : undefined,
    })
      .then((res) => {
        if (cancelled) return;
        const flag = Boolean(
          res?.needGuideFile
          ?? res?.result?.needGuideFile
          ?? false,
        );
        setNeedGuideFile(flag);
      })
      .catch(() => {
        if (!cancelled) setNeedGuideFile(false);
      })
      .finally(() => {
        if (!cancelled) setNeedGuideLoading(false);
      });
    return () => { cancelled = true; };
  }, [shopId, form.categoryId, isSemiManaged, form.selectedSites]);

  const batchUpdateSku = (patchOrMapper = {}) => {
    if (typeof patchOrMapper === 'function') {
      setForm((prev) => ({
        ...prev,
        skcList: prev.skcList.map((skc) => ({
          ...skc,
          skuRows: (skc.skuRows || []).map((sku) => {
            const patch = patchOrMapper(skc, sku);
            if (!patch || typeof patch !== 'object') return sku;
            return { ...sku, ...patch };
          }),
        })),
      }));
      showToast(t('templateEditor.skuRuleDone'), 'success');
      return;
    }
    const next = {};
    [
      'price',
      'suggestedPrice',
      'suggestedPriceCurrencyType',
      'stock',
      'supplierSku',
      'weight',
      'netWeight',
      'length',
      'width',
      'height',
      'quantity',
      'quantityUnit',
      'packageType',
    ].forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(patchOrMapper, key)) next[key] = patchOrMapper[key];
    });
    if (!Object.keys(next).length) return;
    setForm((prev) => ({
      ...prev,
      skcList: prev.skcList.map((skc) => ({
        ...skc,
        skuRows: (skc.skuRows || []).map((sku) => ({ ...sku, ...next })),
      })),
    }));
    showToast(t('publishPkg.appliedAll'), 'success');
  };

  const removeSku = (skcId, skuId) => {
    setForm((prev) => {
      const nextSkcList = prev.skcList.map((skc) => {
        if (skc.id !== skcId) return skc;
        const nextRows = skc.skuRows.filter((sku) => sku.id !== skuId);
        return {
          ...skc,
          skuRows: nextRows.length
            ? nextRows
            : [createSkuRow({ sizeAttributeId: sizeAttr?.attributeId || '' })],
        };
      });
      return {
        ...prev,
        skcList: nextSkcList,
        selectedSizeIds: deriveSelectedSizeIds(nextSkcList),
      };
    });
  };

  const skuDefaultsFromForm = (prev) => ({
    price: prev.supplyPrice || '',
    stock: prev.stockQty || '',
    weight: prev.weightGrams || '',
    netWeight: prev.netWeightGrams || '',
    currency: prev.currency || '',
  });

  const onColorsChange = (colorKeys) => {
    const platformIds = new Set((colorAttr?.values || []).map((v) => String(v.id)));
    const safeKeys = [...new Set((colorKeys || []).map(String).filter((key) => {
      if (!key) return false;
      if (key.startsWith('custom:')) return Boolean(key.slice('custom:'.length));
      return platformIds.has(key);
    }))];
    setForm((prev) => {
      const defaults = skuDefaultsFromForm(prev);
      const sizeIds = (prev.selectedSizeIds || [])
        .map(String)
        .filter((id) => (sizeAttr?.values || []).some((v) => String(v.id) === id));
      // 一条旧 SKC 只能挂到一个仍选中的颜色上。删掉的颜色不在 safeKeys 里，
      // 其包装行会随 SKC 一起丢掉，避免和新颜色共用 id 后表格行重叠。
      const usedSkcIds = new Set();
      const nextSkcList = safeKeys.map((colorKey) => {
        const isCustom = colorKey.startsWith('custom:');
        let colorExtraValue = '';
        if (isCustom) {
          try {
            colorExtraValue = decodeURIComponent(colorKey.slice('custom:'.length));
          } catch {
            colorExtraValue = colorKey.slice('custom:'.length);
          }
        }
        const existing = prev.skcList.find((skc) => {
          if (!skc?.id || usedSkcIds.has(skc.id)) return false;
          if (isCustom) {
            return Boolean(skc.colorIsCustom || skc.colorExtraValue)
              && String(skc.colorExtraValue || skc.colorLabel || '') === colorExtraValue;
          }
          return String(skc.colorValueId || '') === colorKey && !skc.colorIsCustom;
        });
        const val = !isCustom
          ? colorAttr?.values?.find((v) => String(v.id) === colorKey)
          : null;
        const colorLabel = isCustom
          ? colorExtraValue
          : (val?.nameCn || val?.name || existing?.colorLabel || '');
        if (existing) {
          usedSkcIds.add(existing.id);
          return {
            ...existing,
            colorAttributeId: colorAttr?.attributeId || existing.colorAttributeId,
            colorValueId: isCustom ? '' : colorKey,
            colorLabel,
            colorCode: existing.colorCode || '',
            colorIsCustom: isCustom,
            colorExtraValue: isCustom ? colorExtraValue : '',
            skuRows: buildSkuRowsForSizes(existing.skuRows, sizeIds, sizeAttr, defaults),
          };
        }
        return createSkc({
          colorAttributeId: colorAttr?.attributeId || '',
          colorValueId: isCustom ? '' : colorKey,
          colorLabel,
          colorCode: '',
          colorIsCustom: isCustom,
          colorExtraValue: isCustom ? colorExtraValue : '',
          skuRows: buildSkuRowsForSizes([], sizeIds, sizeAttr, defaults),
        });
      });
      return { ...prev, skcList: nextSkcList, selectedSizeIds: sizeIds };
    });
  };

  const onSizesChange = (sizeIds) => {
    const allowed = new Set((sizeAttr?.values || []).map((v) => String(v.id)));
    // 尺码仅允许平台选项，禁止自定义
    const safeIds = [...new Set((sizeIds || []).map(String).filter((id) => allowed.has(id)))];
    setForm((prev) => {
      const defaults = skuDefaultsFromForm(prev);
      return {
        ...prev,
        selectedSizeIds: safeIds,
        sizeSaleAttributeId: sizeAttr?.attributeId || prev.sizeSaleAttributeId || '',
        skcList: prev.skcList.map((skc) => ({
          ...skc,
          skuRows: buildSkuRowsForSizes(skc.skuRows, safeIds, sizeAttr, defaults),
        })),
      };
    });
  };

  const updateSizeCell = (sizeValueId, attrId, value) => {
    setForm((prev) => ({
      ...prev,
      sizeSaleAttributeId: prev.sizeSaleAttributeId || sizeAttr?.attributeId || '',
      sizeChart: {
        ...prev.sizeChart,
        [sizeValueId]: {
          ...(prev.sizeChart?.[sizeValueId] || {}),
          [attrId]: value,
        },
      },
    }));
  };

  const applySizeChart = (nextChart, options = {}) => {
    const replace = options?.replace === true;
    setForm((prev) => ({
      ...prev,
      sizeSaleAttributeId: prev.sizeSaleAttributeId || sizeAttr?.attributeId || '',
      sizeChart: replace
        ? { ...(nextChart || {}) }
        : {
          ...(prev.sizeChart || {}),
          ...(nextChart || {}),
        },
    }));
  };

  useEffect(() => {
    if (isEdit) {
      setDraftLoading(false);
      setDraftHydrated(true);
      return undefined;
    }

    // 草稿优先
    if (initialDraftId) {
      if (!shopId) {
        setDraftLoading(false);
        setDraftHydrated(true);
        return undefined;
      }
      let cancelled = false;
      setDraftLoading(true);
      setDraftHydrated(false);
      temuPlatformAPI.getPublishDraft(shopId, initialDraftId)
        .then((res) => {
          if (cancelled) return;
          const formData = res?.form && typeof res.form === 'object' ? res.form : {};
          setDraftId(String(res?.id || initialDraftId));
          setForm(sanitizeTemuFormImageSlots(emptyForm(formData)));
          setSelectedAttrTemplateId(String(
            formData.selectedAttrTemplateId || res?.selectedAttrTemplateId || '',
          ).trim());
          setSelectedSizeChartTemplateId(String(
            formData.selectedSizeChartTemplateId || res?.selectedSizeChartTemplateId || '',
          ).trim());
          setSelectedProductTemplateId(String(formData.selectedProductTemplateId || '').trim());
          setDraftHydrated(true);
        })
        .catch((e) => {
          if (cancelled) return;
          const msg = e?.status === 404 || /不存在|not found/i.test(String(e?.message || ''))
            ? t('publishFlow.draftGone')
            : (e?.message || t('publishFlow.draftLoadFail'));
          showToast(msg, 'error');
          setDraftId('');
          setDraftHydrated(true);
        })
        .finally(() => {
          if (!cancelled) setDraftLoading(false);
        });
      return () => { cancelled = true; };
    }

    // 列表「发布」：按产品模板 ID 拉取
    if (initialProductTemplateId) {
      if (!shopId) {
        setDraftLoading(false);
        setDraftHydrated(true);
        return undefined;
      }
      let cancelled = false;
      setDraftLoading(true);
      setDraftHydrated(false);
      temuPlatformAPI.getProductTemplate(shopId, initialProductTemplateId)
        .then((res) => {
          if (cancelled) return;
          const item = res?.item || res || {};
          const rawPayload = item.payload && typeof item.payload === 'object' ? item.payload : {};
          const formData = rawPayload.form && typeof rawPayload.form === 'object'
            ? rawPayload.form
            : rawPayload;
          setForm(sanitizeTemuFormImageSlots(emptyForm({
            ...formData,
            categoryId: formData.categoryId || item.categoryId || '',
            productTypeId: formData.productTypeId || item.productTypeId || '',
            categoryPathLabel: formData.categoryPathLabel || item.categoryPathLabel || '',
          })));
          setSelectedAttrTemplateId(String(
            rawPayload.selectedAttrTemplateId || formData.selectedAttrTemplateId || '',
          ).trim());
          setSelectedSizeChartTemplateId(String(
            rawPayload.selectedSizeChartTemplateId || formData.selectedSizeChartTemplateId || '',
          ).trim());
          setSelectedProductTemplateId(String(item.id || initialProductTemplateId));
          setDraftHydrated(true);
          showToast(t('publishFlow.broughtIn', { name: item.name || initialProductTemplateId }), 'success');
        })
        .catch((e) => {
          if (cancelled) return;
          showToast(e?.message || t('templateEditor.templateLoadFail'), 'error');
          setDraftHydrated(true);
        })
        .finally(() => {
          if (!cancelled) setDraftLoading(false);
        });
      return () => { cancelled = true; };
    }

    // 编辑页「发布」：按 token 读取表单快照（IndexedDB / localStorage）
    if (fromProductTemplate) {
      const handoffToken = String(fromProductTemplate || '').trim();
      if (productTemplateHandoffAppliedRef.current === handoffToken) {
        setDraftLoading(false);
        setDraftHydrated(true);
        return undefined;
      }
      let cancelled = false;
      setDraftLoading(true);
      setDraftHydrated(false);
      readProductTemplatePublishHandoff(handoffToken)
        .then((formData) => {
          if (cancelled) return;
          if (formData) {
            productTemplateHandoffAppliedRef.current = handoffToken;
            setForm(sanitizeTemuFormImageSlots(emptyForm(formData)));
            setSelectedAttrTemplateId(String(formData.selectedAttrTemplateId || '').trim());
            setSelectedSizeChartTemplateId(String(formData.selectedSizeChartTemplateId || '').trim());
            setSelectedProductTemplateId(String(formData.selectedProductTemplateId || '').trim());
            scheduleClearProductTemplatePublishHandoff(handoffToken);
            showToast(t('publishFlow.broughtContent'), 'success');
          } else {
            showToast(t('publishFlow.templateMissing'), 'warning');
          }
          setDraftHydrated(true);
        })
        .catch(() => {
          if (cancelled) return;
          showToast(t('publishFlow.templateMissing'), 'warning');
          setDraftHydrated(true);
        })
        .finally(() => {
          if (!cancelled) setDraftLoading(false);
        });
      return () => { cancelled = true; };
    }

    setDraftLoading(false);
    setDraftHydrated(true);
    return undefined;
  }, [isEdit, initialDraftId, initialProductTemplateId, fromProductTemplate, shopId, t]);

  useEffect(() => {
    if (!shopId) {
      setMeta({ categories: null, sites: null, warehouses: [] });
      setPublishCurrencies([]);
      return undefined;
    }
    let cancelled = false;
    setMetaLoading(true);
    setPublishCurrencyLoading(true);
    Promise.all([
      temuPlatformAPI.getShopCategories(shopId),
      temuPlatformAPI.getShopSiteList(shopId),
      temuPlatformAPI.getShopWarehouses(shopId).catch(() => ({ warehouses: [] })),
      temuPlatformAPI.getPublishFillInStandard(shopId, {}).catch(() => null),
    ])
      .then(async ([catRes, siteRes, warehouseRes, fillInRes]) => {
        if (cancelled) return;
        const warehouses = Array.isArray(warehouseRes?.warehouses)
          ? warehouseRes.warehouses
          : [];
        const loaded = await loadShopPublishCurrencies({
          shopCaps,
          siteList: siteRes?.siteList ?? null,
          fillInStandard: fillInRes,
          refreshSiteList: () => temuPlatformAPI.getShopSiteList(shopId, { refresh: true }),
        });
        if (cancelled) return;
        setMeta({
          categories: catRes?.categories ?? null,
          sites: loaded.siteList,
          warehouses,
        });
        setPublishCurrencies(loaded.currencies);
        if (loaded.emptyWarning) {
          // eslint-disable-next-line no-console
          console.warn('[temu] publish currency empty', loaded.source, loaded.emptyWarning);
          showToast(loaded.emptyWarning, 'warning');
        }
      })
      .catch((e) => {
        if (!cancelled) showToast(e?.message || t('templateEditor.shopOptionsFail'), 'error');
      })
      .finally(() => {
        if (!cancelled) {
          setMetaLoading(false);
          setPublishCurrencyLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [shopId, shopCaps, t]);

  useEffect(() => {
    if (!shopId || !draftHydrated) return undefined;
    const attrId = String(selectedAttrTemplateId || '').trim();
    if (!attrId) return undefined;
    let cancelled = false;
    temuPlatformAPI.getProductAttributeTemplate(shopId, attrId)
      .then((res) => {
        if (cancelled) return;
        setSelectedAttrTemplateVersion((prev) => (
          prev != null ? prev : parseTemplateVersion(res?.item?.version)
        ));
      })
      .catch((e) => {
        if (cancelled || !isMissingTemplateError(e)) return;
        setSelectedAttrTemplateId('');
        setSelectedAttrTemplateVersion(null);
      });
    return () => { cancelled = true; };
  }, [shopId, draftHydrated, selectedAttrTemplateId]);

  useEffect(() => {
    if (!shopId || !draftHydrated) return undefined;
    const sizeId = String(selectedSizeChartTemplateId || '').trim();
    if (!sizeId) return undefined;
    let cancelled = false;
    temuPlatformAPI.getSizeChartTemplate(shopId, sizeId)
      .then((res) => {
        if (cancelled) return;
        setSelectedSizeChartTemplateVersion((prev) => (
          prev != null ? prev : parseTemplateVersion(res?.item?.version)
        ));
      })
      .catch((e) => {
        if (cancelled || !isMissingTemplateError(e)) return;
        setSelectedSizeChartTemplateId('');
        setSelectedSizeChartTemplateVersion(null);
      });
    return () => { cancelled = true; };
  }, [shopId, draftHydrated, selectedSizeChartTemplateId]);

  /**
   * 产品模板版本兜底。
   *
   * 从产品模板列表「发布商品」（?productTemplateId=）或模板页本地传递进入时只带模板 ID、
   * 不带 version，发品前乐观锁会把 expectedVersion=null 当成版本不一致，报「产品模板已被更新，
   * 请重新加载对应区域」；用户点重新加载又会用模板内容覆盖刚填好的表单。
   * 这里在进入发品页时补一次版本（等价于以打开时的版本为基准），
   * 之后模板真的被改，仍然会正常报冲突、仍然提醒重新加载。
   */
  useEffect(() => {
    if (!shopId || !draftHydrated) return undefined;
    const id = String(selectedProductTemplateId || '').trim();
    if (!id || selectedProductTemplateVersion != null) return undefined;
    let cancelled = false;
    temuPlatformAPI.getProductTemplate(shopId, id)
      .then((res) => {
        if (cancelled) return;
        const version = parseTemplateVersion(res?.item?.version);
        if (version != null) setSelectedProductTemplateVersion(version);
      })
      .catch(() => {
        /* 取不到版本时不强制锁定，交给发品前校验与后端兜底 */
      });
    return () => { cancelled = true; };
  }, [shopId, draftHydrated, selectedProductTemplateId, selectedProductTemplateVersion]);

  useEffect(() => {
    if (!shopId || !form.categoryId) {
      setAttrTpl(null);
      setAttrError('');
      return undefined;
    }
    const leafCatId = Number(form.categoryId);
    const typeKey = Number(form.productTypeId || form.categoryId);
    let cancelled = false;
    setAttrLoading(true);
    setAttrError('');
    // TEMU：严格按叶子类目 catId 拉 bg.goods.attrs.get（属性库只填值，不决定 schema）
    Promise.all([
      temuPlatformAPI.getAttributeTemplate(shopId, {
        catId: leafCatId,
        categoryId: leafCatId,
        category_id: leafCatId,
        product_type_id_list: [Number.isFinite(typeKey) && typeKey > 0 ? typeKey : leafCatId],
      }, { forceRefresh: true }),
      temuPlatformAPI.getShopBrands(shopId).catch(() => null),
    ])
      .then(([res, brandRes]) => {
        if (cancelled) return;
        const parsed = applyShopBrandsToAttrTemplate(
          parseAttributeTemplate(res, { productTypeId: String(typeKey || leafCatId) }),
          brandRes?.brands ?? brandRes,
        );
        // 标记本次 schema 对应的叶子类目，便于排查串类目
        if (parsed?.meta) parsed.meta.catId = String(leafCatId);
        setAttrTpl(parsed);
        // 材质/成分等控件要 [{valueId,percent}]；编辑回填常是 string / string[]
        // 切类目时按名称把旧类目相似字段回填到新 schema
        const stash = pendingCategoryPreserveRef.current;
        let preserveToast = '';
        if (stash) {
          pendingCategoryPreserveRef.current = null;
          const remappedAttrs = remapProductAttributesAcrossCategories({
            prevAttributes: stash.productAttributes,
            prevAttrTpl: stash.prevAttrTpl,
            nextAttrTpl: parsed,
          });
          const remappedSize = remapSizeChartAcrossCategories({
            prevSizeChart: stash.sizeChart,
            prevSelectedSizeIds: stash.selectedSizeIds,
            prevAttrTpl: stash.prevAttrTpl,
            nextAttrTpl: parsed,
          });
          const remappedSkc = remapSkcListAcrossCategories({
            prevSkcList: stash.skcList,
            prevAttrTpl: stash.prevAttrTpl,
            nextAttrTpl: parsed,
          });
          const selectedSizeIds = remappedSize.selectedSizeIds.length
            ? remappedSize.selectedSizeIds
            : remappedSkc.selectedSizeIds;
          const alignedRemappedAttrs = alignAttributesToCategoryTemplate(remappedAttrs.attributes, parsed);
          if (
            remappedAttrs.preservedCount > 0
            || remappedSize.preservedCount > 0
            || remappedSkc.preservedCount > 0
          ) {
            pendingAttrAutoForRef.current = '';
            pendingProductTplAutoForRef.current = '';
            const parts = [];
            if (remappedAttrs.preservedCount > 0) parts.push(t('templateEditor.keptSimilar', { count: remappedAttrs.preservedCount }));
            if (remappedSkc.preservedCount > 0) parts.push(t('templateEditor.keptMainSpec', { count: remappedSkc.preservedCount }));
            if (remappedSize.preservedCount > 0) parts.push(t('templateEditor.sizeChartWord'));
            preserveToast = parts.length ? t('templateEditor.keptPrefix', { detail: parts.join('、') }) : '';
          }
          setForm((prev) => ({
            ...prev,
            // remap 无保留内容时不要覆盖：否则会把「选类目后自动套用产品模板」刚写入的属性 /
            // 主规格 / 尺码表抹掉（表现为套用后属性不显示，重选模板才恢复）
            productAttributes: remappedAttrs.preservedCount > 0 ? alignedRemappedAttrs : prev.productAttributes,
            sizeChart: remappedSize.preservedCount > 0 ? remappedSize.sizeChart : prev.sizeChart,
            selectedSizeIds: (remappedSize.preservedCount > 0 || remappedSkc.preservedCount > 0)
              ? selectedSizeIds
              : prev.selectedSizeIds,
            skcList: remappedSkc.preservedCount > 0 ? remappedSkc.skcList : prev.skcList,
            // 切类目 remap 后保留媒体与官方尺码表（onCategoryChange 曾临时清空）
            temuVideos: Array.isArray(stash.temuVideos) ? stash.temuVideos : (prev.temuVideos || []),
            materialImage: stash.materialImage || prev.materialImage || null,
            materialImgUrl: stash.materialImgUrl || prev.materialImgUrl || '',
            officialSizeChart: stash.officialSizeChart || prev.officialSizeChart || null,
          }));
        } else {
          setForm((prev) => {
            const aligned = alignAttributesToCategoryTemplate(prev.productAttributes, parsed);
            if (aligned === prev.productAttributes) return prev;
            return { ...prev, productAttributes: aligned };
          });
        }
        if (preserveToast) showToast(preserveToast, 'success');
        const total = (parsed.productAttrs?.length || 0)
          + (parsed.skcSaleAttrs?.length || 0)
          + (parsed.skuSaleAttrs?.length || 0)
          + (parsed.sizeAttrs?.length || 0);
        if (total === 0) {
          setAttrError(t('templateEditor.attrEmptyCat'));
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setAttrTpl(null);
        setAttrError(e?.message || t('templateEditor.attrLoadFail'));
      })
      .finally(() => {
        if (!cancelled) setAttrLoading(false);
      });
    return () => { cancelled = true; };
  }, [shopId, form.categoryId, form.productTypeId, t]);

  useEffect(() => {
    if (!shopId || !form.categoryId) {
      setSizeChartRequired(false);
      setSizeChartClass(null);
      setSizeChartClassError('');
      setSizeChartClassLoading(false);
      return undefined;
    }
    let cancelled = false;
    setSizeChartClassLoading(true);
    setSizeChartClassError('');
    fetchSizeChartClassRequired(shopId, form.categoryId)
      .then((res) => {
        if (cancelled) return;
        if (!res?.ok) {
          setSizeChartRequired(false);
          setSizeChartClass(null);
          setSizeChartClassError(res?.error || t('templateEditor.sizeClassFailShort'));
          showToast(res?.error || t('templateEditor.sizeClassFail'), 'error');
          return;
        }
        setSizeChartRequired(Boolean(res.required));
        setSizeChartClass(res.sizeClass || null);
        setSizeChartClassError('');
        if (!res.required) {
          setForm((prev) => (
            (prev.temuSizeTemplateIds || []).length
              ? { ...prev, temuSizeTemplateIds: [] }
              : prev
          ));
        }
      })
      .finally(() => {
        if (!cancelled) setSizeChartClassLoading(false);
      });
    return () => { cancelled = true; };
  }, [shopId, form.categoryId, t]);

  useEffect(() => {
    if (!shopId || !form.productTypeId) {
      setAttrTemplates([]);
      attrTemplatesLoadedForRef.current = '';
      // 切类目时由 onCategoryChange 清空选中；此处勿清，避免草稿/模板回填被冲掉
      return undefined;
    }
    const productTypeId = String(form.productTypeId);
    let cancelled = false;
    attrTemplatesLoadedForRef.current = '';
    temuPlatformAPI.listProductAttributeTemplates(shopId, {
      productTypeId,
      page: 1,
      pageSize: 100,
    })
      .then((res) => {
        if (cancelled) return;
        setAttrTemplates(Array.isArray(res?.items) ? res.items : []);
        attrTemplatesLoadedForRef.current = productTypeId;
      })
      .catch(() => {
        if (!cancelled) {
          setAttrTemplates([]);
          attrTemplatesLoadedForRef.current = productTypeId;
        }
      });
    return () => { cancelled = true; };
  }, [shopId, form.productTypeId]);

  useEffect(() => {
    if (!shopId || !form.categoryId) {
      setCustomAttrPerm({});
      return undefined;
    }
    let cancelled = false;
    temuPlatformAPI.getCustomAttributePermission(shopId, {
      category_id_list: [Number(form.categoryId)],
    })
      .then((res) => {
        if (cancelled) return;
        setCustomAttrPerm(parseCustomAttributePermission(res));
      })
      .catch(() => {
        if (!cancelled) setCustomAttrPerm({});
      });
    return () => { cancelled = true; };
  }, [shopId, form.categoryId]);

  // 按末级类目拉取 Temu 发品约束（/publish/standard；非 Shein fill-in-standard）
  useEffect(() => {
    if (!shopId || !form.categoryId) {
      setSkuPkgCaps(EMPTY_SKU_PACKAGE_CAPS);
      setSkuPkgCapsLoading(false);
      return undefined;
    }
    const categoryId = String(form.categoryId);
    let cancelled = false;
    setSkuPkgCapsLoading(true);
    temuPlatformAPI.getPublishFillInStandard(shopId, {
      catId: Number(categoryId),
      category_id: Number(categoryId),
    })
      .then((res) => {
        if (cancelled) return;
        const nextCaps = resolveSkuPackageCapabilities(res);
        setSkuPkgCaps(nextCaps);
        // 能力关闭时清空残留，避免脏数据进预检 / 误展示
        if (!nextCaps.quantityInfo.show || !nextCaps.packageType.show) {
          setForm((prev) => {
            let changed = false;
            const skcList = (prev.skcList || []).map((skc) => {
              let skcChanged = false;
              const skuRows = (skc.skuRows || []).map((sku) => {
                const patch = {};
                if (!nextCaps.quantityInfo.show) {
                  if (sku.quantity != null && String(sku.quantity).trim() !== '') {
                    patch.quantity = '';
                  }
                  if (sku.quantityUnit != null && String(sku.quantityUnit) !== '1') {
                    patch.quantityUnit = '1';
                  }
                }
                if (!nextCaps.packageType.show) {
                  if (sku.packageType != null && String(sku.packageType) !== '' && String(sku.packageType) !== '0') {
                    patch.packageType = '';
                  }
                }
                if (!Object.keys(patch).length) return sku;
                skcChanged = true;
                changed = true;
                return { ...sku, ...patch };
              });
              return skcChanged ? { ...skc, skuRows } : skc;
            });
            return changed ? { ...prev, skcList } : prev;
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setSkuPkgCaps(EMPTY_SKU_PACKAGE_CAPS);
      })
      .finally(() => {
        if (!cancelled) setSkuPkgCapsLoading(false);
      });
    return () => { cancelled = true; };
  }, [shopId, form.categoryId]);

  useEffect(() => {
    if (!isEdit || !shopId || (!editSpuName && !initialRecordId && !initialDraftId)) return undefined;
    let cancelled = false;
    setLoadingSpu(true);
    // 审核中商品 searchProduct 往往只有类目；优先本地草稿 form 整表回填
    temuPlatformAPI.getProductForEdit(shopId, {
      spuName: editSpuName,
      recordId: initialRecordId,
      draftId: initialDraftId,
    })
      .then((res) => {
        if (cancelled) return;
        const info = res || {};
        if (info.draftId != null && info.draftId !== '') {
          setDraftId(String(info.draftId));
        }
        const skcItems = Array.isArray(info.skcItems)
          ? info.skcItems
          : (Array.isArray(info.form?.skcItems) ? info.form.skcItems : []);
        if (info.form && typeof info.form === 'object') {
          const merged = enrichFormWithPlatformSkcIds({
            ...info.form,
            spuName: info.spuName || info.form.spuName || editSpuName,
            title: info.form.title || info.title || '',
            titleEn: info.form.titleEn || info.titleEn || info.englishTitle || '',
            supplierCode: info.form.supplierCode || info.supplierCode || '',
            brandCode: info.form.brandCode || info.brandCode || '',
            categoryId: String(info.form.categoryId || info.categoryId || ''),
            productTypeId: String(info.form.productTypeId || info.productTypeId || ''),
            categoryPathLabel: info.form.categoryPathLabel || info.categoryPathLabel || '',
            description: info.form.description || info.description || '',
            originCountry: info.form.originCountry || info.originCountry || '',
          }, skcItems);
          const nextForm = sanitizeTemuFormImageSlots(
            withDefaultWarehouse(emptyForm(merged), warehouseListRef.current),
          );
          editBaselineRef.current = JSON.parse(JSON.stringify(nextForm));
          setForm(nextForm);
          if (info.platformLoadWarning) {
            showToast(String(info.platformLoadWarning), 'warning');
          }
          return;
        }
        const flatForm = sanitizeTemuFormImageSlots(withDefaultWarehouse(enrichFormWithPlatformSkcIds({
          ...emptyForm({}),
          spuName: info.spuName || editSpuName || '',
          title: info.title || '',
          titleEn: info.titleEn || info.englishTitle || '',
          description: info.description || '',
          supplierCode: info.supplierCode || '',
          brandCode: info.brandCode || '',
          originCountry: info.originCountry || '',
          categoryId: info.categoryId != null ? String(info.categoryId) : '',
          productTypeId: info.productTypeId != null ? String(info.productTypeId) : '',
          categoryPathLabel: info.categoryPathLabel || '',
        }, skcItems), warehouseListRef.current));
        editBaselineRef.current = JSON.parse(JSON.stringify(flatForm));
        setForm(flatForm);
      })
      .catch((e) => {
        if (!cancelled) showToast(e?.message || t('publishFlow.detailLoadFail'), 'error');
      })
      .finally(() => {
        if (!cancelled) setLoadingSpu(false);
      });
    return () => { cancelled = true; };
  }, [isEdit, shopId, editSpuName, initialRecordId, initialDraftId, t]);

  const sizeBindKey = (form.skcList || []).map((skc) => (
    (skc.skuRows || []).map((row) => (
      `${row.sizeValueId || ''}:${row.sizeLabel || ''}:${row.sizeExtraValue || ''}:${row.sizeIsCustom ? 1 : 0}`
    )).join(',')
  )).join(';');

  useEffect(() => {
    if (!colorAttr && !sizeAttr) return;
    setForm((prev) => {
      const colorValues = colorAttr?.values || [];
      const sizeValues = sizeAttr?.values || [];
      const colorIds = new Set(colorValues.map((v) => String(v.id)));
      const sizeIds = new Set(sizeValues.map((v) => String(v.id)));
      const defaults = {
        price: prev.supplyPrice || '',
        stock: prev.stockQty || '',
        weight: prev.weightGrams || '',
        netWeight: prev.netWeightGrams || '',
        currency: prev.currency || '',
      };
      // 供应表已有尺码名时，按类目销售属性挂回主规格；不要丢掉对不上 valueId 的尺码。
      const alignedSizes = alignSkcSizesToSaleAttr(prev.skcList || [], sizeAttr);
      const rowSizeIds = (alignedSizes.selectedSizeIds || []).map(String);
      const prevSizeIds = (prev.selectedSizeIds || []).map(String);
      const nextSizeIds = rowSizeIds.length
        ? rowSizeIds
        : prevSizeIds.filter((id) => !sizeValues.length || sizeIds.has(id) || isCustomSpecKey(id));
      const nextSkcList = (prev.skcList || []).map((skc, index) => {
        const knownColor = !skc.colorValueId || colorIds.has(String(skc.colorValueId));
        const asCustom = Boolean(
          skc.colorIsCustom
          || skc.colorExtraValue
          || (colorValues.length > 0 && skc.colorValueId && !knownColor),
        );
        const colorLabel = asCustom
          ? (skc.colorExtraValue || skc.colorLabel || '')
          : (colorAttr?.values?.find((v) => String(v.id) === String(skc.colorValueId))?.name
            || skc.colorLabel);
        const existingRows = alignedSizes.skcList[index]?.skuRows || skc.skuRows || [];
        // 编辑多 SKU：保留全部行；仅在新建且用户主动勾选尺码时按 sizeIds 重建
        const keepAllRows = isEdit || existingRows.length > 1 || !nextSizeIds.length;
        return {
          ...skc,
          colorAttributeId: skc.colorAttributeId || colorAttr?.attributeId || '',
          colorIsCustom: asCustom,
          colorExtraValue: asCustom
            ? (skc.colorExtraValue || skc.colorLabel || '')
            : (skc.colorExtraValue || ''),
          colorLabel,
          skuRows: keepAllRows
            ? existingRows
            : buildSkuRowsForSizes(existingRows, nextSizeIds, sizeAttr, defaults),
        };
      }).filter((skc) => skcHasColor(skc));
      const nextState = {
        ...prev,
        sizeSaleAttributeId: prev.sizeSaleAttributeId || sizeAttr?.attributeId || '',
        selectedSizeIds: nextSizeIds.length ? nextSizeIds : prev.selectedSizeIds,
        skcList: nextSkcList,
      };
      const sameSizes = JSON.stringify((prev.selectedSizeIds || []).map(String))
        === JSON.stringify((nextState.selectedSizeIds || []).map(String));
      const sameSaleAttr = String(prev.sizeSaleAttributeId || '') === String(nextState.sizeSaleAttributeId || '');
      const sameSkc = prev.skcList === nextSkcList || (
        (prev.skcList || []).length === nextSkcList.length
        && (prev.skcList || []).every((skc, i) => {
          const n = nextSkcList[i];
          return skc === n
            || (
              String(skc.colorAttributeId || '') === String(n.colorAttributeId || '')
              && Boolean(skc.colorIsCustom) === Boolean(n.colorIsCustom)
              && String(skc.colorExtraValue || '') === String(n.colorExtraValue || '')
              && String(skc.colorLabel || '') === String(n.colorLabel || '')
              && (skc.skuRows || []).length === (n.skuRows || []).length
              && (skc.skuRows || []).every((row, j) => (
                String(row.sizeAttributeId || '') === String(n.skuRows[j]?.sizeAttributeId || '')
                && String(row.sizeValueId || '') === String(n.skuRows[j]?.sizeValueId || '')
                && Boolean(row.sizeIsCustom) === Boolean(n.skuRows[j]?.sizeIsCustom)
                && String(row.sizeLabel || '') === String(n.skuRows[j]?.sizeLabel || '')
              ))
            );
        })
      );
      if (sameSizes && sameSaleAttr && sameSkc) return prev;
      return nextState;
    });
  }, [colorAttr, sizeAttr, isEdit, sizeBindKey]);

  useEffect(() => {
    if (!form.categoryId || !categoryOptions.length) return;
    const selected = categoryOptions.find((item) => String(item.categoryId) === String(form.categoryId));
    if (!selected) return;
    const needType = !form.productTypeId && selected.productTypeId;
    const needPath = isFullerCategoryPath(selected.label, form.categoryPathLabel);
    const selectedPathIds = Array.isArray(selected.pathIds) ? selected.pathIds : [];
    const currentPathIds = Array.isArray(form.catPathIds) ? form.catPathIds : [];
    const needCatPath = selectedPathIds.length > currentPathIds.length;
    if (!needType && !needPath && !needCatPath) return;
    setForm((prev) => {
      if (String(prev.categoryId) !== String(selected.categoryId)) return prev;
      const prevPathIds = Array.isArray(prev.catPathIds) ? prev.catPathIds : [];
      return {
        ...prev,
        productTypeId: prev.productTypeId || (selected.productTypeId ? String(selected.productTypeId) : ''),
        categoryPathLabel: isFullerCategoryPath(selected.label, prev.categoryPathLabel)
          ? selected.label
          : (prev.categoryPathLabel || selected.label || ''),
        catPathIds: selectedPathIds.length > prevPathIds.length
          ? selectedPathIds.map(String)
          : prevPathIds,
      };
    });
  }, [form.categoryId, form.productTypeId, form.categoryPathLabel, form.catPathIds, categoryOptions]);

  const applyProductTemplateById = async (templateId, {
    categoryId = '',
    productTypeId = '',
    categoryPathLabel = '',
    silent = false,
  } = {}) => {
    const id = String(templateId || '').trim();
    if (!shopId || !id) return false;
    const res = await temuPlatformAPI.getProductTemplate(shopId, id);
    const item = res?.item || res || {};
    const rawPayload = item.payload && typeof item.payload === 'object' ? item.payload : {};
    const formData = rawPayload.form && typeof rawPayload.form === 'object'
      ? rawPayload.form
      : rawPayload;
    setForm(sanitizeTemuFormImageSlots(emptyForm({
      ...formData,
      categoryId: categoryId || formData.categoryId || item.categoryId || '',
      productTypeId: productTypeId || formData.productTypeId || item.productTypeId || '',
      categoryPathLabel: categoryPathLabel || formData.categoryPathLabel || item.categoryPathLabel || '',
    })));
    setSelectedAttrTemplateId(String(
      rawPayload.selectedAttrTemplateId || formData.selectedAttrTemplateId || '',
    ).trim());
    setSelectedSizeChartTemplateId(String(
      rawPayload.selectedSizeChartTemplateId || formData.selectedSizeChartTemplateId || '',
    ).trim());
    setSelectedProductTemplateId(String(item.id || id));
    setSelectedProductTemplateVersion(parseTemplateVersion(item.version));
    setSelectedAttrTemplateVersion(null);
    setSelectedSizeChartTemplateVersion(null);
    if (!silent) {
      showToast(t('publishFlow.appliedTpl', { name: item.name || id }), 'success');
    }
    return true;
  };

  // 选类目后加载该类目产品模板；用户主动切换类目时自动套用优先级最高的一项
  useEffect(() => {
    if (isEdit || !shopId || !form.productTypeId) {
      if (!form.productTypeId) {
        setProductTemplates([]);
      }
      return undefined;
    }
    const productTypeId = String(form.productTypeId);
    const categoryId = String(form.categoryId || '');
    const categoryPathLabel = String(form.categoryPathLabel || '');
    const shouldAuto = String(pendingProductTplAutoForRef.current || '') === productTypeId;
    pendingProductTplAutoForRef.current = '';
    const seq = categorySelectSeqRef.current + 1;
    categorySelectSeqRef.current = seq;
    setProductTemplateBusy(true);
    let cancelled = false;
    temuPlatformAPI.listProductTemplates(shopId, {
      productTypeId,
      page: 1,
      pageSize: 100,
    })
      .then(async (res) => {
        if (cancelled || categorySelectSeqRef.current !== seq) return;
        const sorted = sortTemplatesByPriority(Array.isArray(res?.items) ? res.items : []);
        setProductTemplates(sorted);
        if (!shouldAuto) return;
        const picked = pickHighestPriorityTemplate(sorted);
        if (!picked?.id) return;
        // 产品模板已带类目属性/尺码库选中项，跳过属性库自动套用，避免覆盖
        pendingAttrAutoForRef.current = '';
        try {
          await applyProductTemplateById(picked.id, {
            categoryId,
            productTypeId,
            categoryPathLabel,
          });
        } catch (e) {
          showToast(e?.message || t('publishFlow.autoApplyFail'), 'error');
        }
      })
      .catch(() => {
        if (cancelled || categorySelectSeqRef.current !== seq) return;
        setProductTemplates([]);
      })
      .finally(() => {
        if (!cancelled && categorySelectSeqRef.current === seq) {
          setProductTemplateBusy(false);
        }
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅随类目类型变化加载/自动套用
  }, [isEdit, shopId, form.productTypeId, form.categoryId]);

  const onCategoryChange = (categoryId, meta) => {
    const nextCategoryId = categoryId == null || categoryId === '' ? '' : String(categoryId);
    // 再点一次当前类目不算切换：否则会清空模板带入的属性/规格，且自动套用不会重跑
    if (String(form.categoryId || '') === nextCategoryId) {
      return;
    }
    const selected = categoryOptions.find((item) => String(item.categoryId) === nextCategoryId);
    const nextProductTypeId = selected?.productTypeId
      ? String(selected.productTypeId)
      : (meta?.productTypeId ? String(meta.productTypeId) : nextCategoryId);
    const nextCategoryPathLabel = selected?.label || meta?.label || '';
    const nextCatPathIds = Array.isArray(selected?.pathIds)
      ? selected.pathIds.map(String)
      : (Array.isArray(meta?.pathIds) ? meta.pathIds.map(String) : []);
    pendingCategoryPreserveRef.current = {
      productAttributes: pickFilledProductAttributes(form.productAttributes),
      sizeChart: form.sizeChart && typeof form.sizeChart === 'object' ? form.sizeChart : {},
      officialSizeChart: form.officialSizeChart && typeof form.officialSizeChart === 'object'
        ? form.officialSizeChart
        : null,
      selectedSizeIds: Array.isArray(form.selectedSizeIds) ? [...form.selectedSizeIds] : [],
      skcList: Array.isArray(form.skcList) ? form.skcList : [],
      temuVideos: Array.isArray(form.temuVideos) ? form.temuVideos : [],
      materialImage: form.materialImage || null,
      materialImgUrl: form.materialImgUrl || '',
      prevAttrTpl: attrTplRef.current,
    };
    pendingAttrAutoForRef.current = nextProductTypeId;
    pendingProductTplAutoForRef.current = nextProductTypeId;
    setSelectedAttrTemplateId('');
    setSelectedSizeChartTemplateId('');
    setSelectedProductTemplateId('');
    setSelectedProductTemplateVersion(null);
    setSelectedAttrTemplateVersion(null);
    setSelectedSizeChartTemplateVersion(null);
    setVersionConflicts({});
    setProductTemplates([]);
    setForm((prev) => ({
      ...prev,
      categoryId: nextCategoryId,
      productTypeId: nextProductTypeId,
      categoryPathLabel: nextCategoryPathLabel,
      catPathIds: nextCatPathIds,
      productAttributes: {},
      sizeChart: {},
      skcList: [],
      selectedSizeIds: [],
      temuSizeTemplateIds: [],
      goodsModels: [],
      // 主图视频 / 素材图 / 官方尺码表由 stash 在属性 remap 后写回，此处勿永久丢弃
    }));
    setAttrsExpanded(false);
  };

  const onProductTemplateSelect = async (templateId) => {
    const id = String(templateId || '').trim();
    setSelectedProductTemplateId(id);
    if (!id) return;
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    setProductTemplateBusy(true);
    try {
      pendingAttrAutoForRef.current = '';
      await applyProductTemplateById(id, {
        categoryId: form.categoryId,
        productTypeId: form.productTypeId,
        categoryPathLabel: form.categoryPathLabel,
      });
    } catch (e) {
      showToast(e?.message || t('publishFlow.applyFail'), 'error');
    } finally {
      setProductTemplateBusy(false);
    }
  };

  const onSitesChange = (abbr) => {
    const next = abbr == null || abbr === '' ? '' : String(abbr);
    const selected = next ? [next] : [];
    const first = isSemiManaged
      ? null
      : siteOptions.find((s) => selected.includes(s.siteAbbr));
    const nextShipmentOptions = getTemuShipmentLimitOptions({
      siteIds: selected,
      customProduct: form.isCustomProduct,
    });
    setForm((prev) => ({
      ...prev,
      selectedSites: selected,
      mainSite: first?.mainSiteCode || prev.mainSite || 'temu',
      shipmentLimitSecond: pickTemuShipmentLimitSecond(
        prev.shipmentLimitSecond,
        nextShipmentOptions,
      ),
    }));
  };

  const onMaterialImagesChange = (next) => {
    // PublishImageUploadZone 单图模式传对象/null；多图模式传数组
    const img = Array.isArray(next) ? (next[0] || null) : (next || null);
    setForm((prev) => ({
      ...prev,
      materialImage: img,
      materialImgUrl: String(img?.temuUrl || '').trim(),
    }));
    setFieldErrors((prev) => {
      if (!prev?.materialImgUrl) return prev;
      const nextErrors = { ...prev };
      delete nextErrors.materialImgUrl;
      return nextErrors;
    });
  };

  const onVideosChange = (videos) => {
    setForm((prev) => ({
      ...prev,
      temuVideos: Array.isArray(videos) ? videos : [],
    }));
    setFieldErrors((prev) => {
      if (!prev?.temuVideos) return prev;
      const next = { ...prev };
      delete next.temuVideos;
      return next;
    });
  };

  const onWarehouseChange = (warehouseId) => {
    const selected = warehouseOptions.find((item) => String(item.value) === String(warehouseId));
    setForm((prev) => ({
      ...prev,
      warehouseId: selected?.value || '',
      warehouseName: selected?.name || '',
    }));
  };

  const scrollToSection = (id) => {
    setActiveSection(id);
    document.getElementById(`tpo-pub-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToFieldError = (field, section) => {
    if (section) scrollToSection(section);
    const el = document.querySelector(`[data-field-error="${field}"]`);
    if (el) {
      window.setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 80);
    }
  };

  const applyFieldErrors = (errors, fallbackMessage = '') => {
    const list = Array.isArray(errors) ? errors : [];
    const map = fieldErrorsToMap(list);
    setFieldErrors(map);
    const first = list[0];
    if (first?.field) {
      scrollToFieldError(first.field, first.section);
    }
    const tip = first?.message || fallbackMessage;
    if (tip) showToast(tip, 'error', Math.min(12000, Math.max(4000, String(tip).length * 50)));
  };

  const buildPublishFormSnapshot = (formSource = form) => {
    const sizeValueLabels = {};
    (sizeAttr?.values || []).forEach((v) => {
      if (v?.id != null && v?.name) sizeValueLabels[String(v.id)] = String(v.name);
    });
    const skcList = (formSource.skcList || []).map((skc) => ({
      ...skc,
      skuRows: (skc.skuRows || []).map((sku) => ({
        ...sku,
        sizeLabel: sizeAttr?.values?.find((v) => String(v.id) === String(sku.sizeValueId))?.name
          || sku.sizeLabel
          || '',
      })),
    }));
    const selectedSizeIds = (formSource.selectedSizeIds?.length
      ? formSource.selectedSizeIds
      : deriveSelectedSizeIds(skcList)
    ).map((id) => String(id));
    return {
      ...formSource,
      skcList,
      selectedSizeIds,
      sizeValueLabels,
      selectedAttrTemplateId: String(selectedAttrTemplateId || '').trim(),
      selectedSizeChartTemplateId: String(selectedSizeChartTemplateId || '').trim(),
      selectedProductTemplateId: String(selectedProductTemplateId || '').trim(),
      ...(skipSiteWarehouseFreight
        ? {
          selectedSites: [],
          warehouseId: '',
          warehouseName: '',
          freightTemplateId: '',
          shipmentLimitSecond: '',
          temuSiteIds: [],
          temuSiteId: '',
          temuFreightTemplateId: '',
        }
        : {}),
    };
  };

  const onSaveDraft = async () => {
    if (!shopId) {
      showToast(t('publishFlow.pickShopDraft'), 'warning');
      return;
    }
    setSavingDraft(true);
    try {
      // 印花图仅站内展示 / 侵权检测，不自动拼接到货号与商家 SKU
      let enriched = await enrichTemuFormPrintNames(form);
      enriched = await ensureTemuColorBlocksOnForm(enriched, { shopId });
      try {
        enriched = await ensureTemuMaterialFromMainOnForm(enriched, { shopId });
      } catch (materialErr) {
        showToast(materialErr?.message || t('publishFlow.materialGenFail'), 'warning');
      }
      if (enriched !== form) setForm(enriched);
      const payload = {
        form: persistTemuFormImageSlots(buildPublishFormSnapshot(enriched)),
      };
      const res = draftId
        ? await temuPlatformAPI.updatePublishDraft(shopId, draftId, payload)
        : await temuPlatformAPI.createPublishDraft(shopId, payload);
      if (res?.id) setDraftId(String(res.id));
      showToast(t('publishFlow.draftSavedLocal'), 'success');
      allowUnsavedLeave();
      navigate(buildTemuPath('products/drafts'));
    } catch (e) {
      showToast(e?.message || t('publishFlow.draftFail'), 'error');
    } finally {
      setSavingDraft(false);
    }
  };

  const openProductTplSaveModal = () => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'error');
      return;
    }
    if (!form.categoryId || !form.productTypeId) {
      showToast(t('publishAttr.pickCategory'), 'error');
      return;
    }
    setProductTplSaveModalOpen(true);
  };

  const showSkuQuantity = !skuPkgCapsLoading && skuPkgCaps.quantityInfo.show === true;
  const showSkuPackageType = !skuPkgCapsLoading && skuPkgCaps.packageType.show === true;
  // 包装表已改为 Temu SKU 分类；下列能力开关仅留给校验兼容
  void showSkuQuantity;
  void showSkuPackageType;

  const { requiredAttrs, optionalAttrs } = useMemo(
    () => partitionProductAttrsForDisplay(attrTpl, null),
    [attrTpl],
  );
  const allProductAttrs = useMemo(
    () => [...requiredAttrs, ...optionalAttrs],
    [requiredAttrs, optionalAttrs],
  );
  const visibleRequiredAttrs = useMemo(
    () => requiredAttrs.filter((attr) => isEditorAttrVisible(
      attr,
      form.productAttributes || {},
      allProductAttrs,
    )),
    [requiredAttrs, allProductAttrs, form.productAttributes],
  );
  const visibleOptionalAttrs = useMemo(
    () => optionalAttrs.filter((attr) => isEditorAttrVisible(
      attr,
      form.productAttributes || {},
      allProductAttrs,
    )),
    [optionalAttrs, allProductAttrs, form.productAttributes],
  );

  const syncTitleEnFromZh = async (zhTitle, { silent = false, force = false } = {}) => {
    const zh = String(zhTitle || '').trim();
    if (!zh) {
      setForm((prev) => (prev.titleEn ? { ...prev, titleEn: '' } : prev));
      lastAutoTitleEnRef.current = '';
      return '';
    }
    const seq = ++titleTranslateSeqRef.current;
    setTitleTranslateBusy(true);
    try {
      const titleEn = await translateTemuTitleToEnglish(zh);
      if (seq !== titleTranslateSeqRef.current) return titleEn;
      setForm((prev) => {
        const currentEn = String(prev.titleEn || '').trim();
        const lastAuto = String(lastAutoTitleEnRef.current || '').trim();
        // 用户已手改英文名：非强制时不覆盖
        if (!force && currentEn && currentEn !== lastAuto) {
          return prev;
        }
        lastAutoTitleEnRef.current = titleEn;
        return { ...prev, titleEn };
      });
      return titleEn;
    } catch (error) {
      if (seq !== titleTranslateSeqRef.current) return '';
      if (!silent) {
        showToast(error?.message || t('templateEditor.translateFail'), 'warning');
      }
      throw error;
    } finally {
      if (seq === titleTranslateSeqRef.current) {
        setTitleTranslateBusy(false);
      }
    }
  };

  useEffect(() => {
    const zh = String(form.title || '').trim();
    if (!zh) {
      if (form.titleEn) {
        setForm((prev) => ({ ...prev, titleEn: '' }));
      }
      lastAutoTitleEnRef.current = '';
      return undefined;
    }
    const timer = window.setTimeout(() => {
      void syncTitleEnFromZh(zh, { silent: true, force: false }).catch(() => { });
    }, 700);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.title]);

  const handleAiTitle = async () => {
    if (aiTitleBusy || submitting) return;
    const confirmed = await confirmIpeCreditAction({
      navId: 'aiCopywriting',
      imageCount: 1,
      title: t('templateEditor.aiTitleDialog'),
      confirmLabel: t('templateEditor.confirmGen'),
    });
    if (!confirmed) return;

    setAiTitleBusy(true);
    showToast(t('templateEditor.aiTitleBusyTemu'), 'info');
    try {
      const title = await generateTemuPublishAiTitle({
        skcList: form.skcList,
        categoryPathLabel: form.categoryPathLabel,
        productAttributes: form.productAttributes,
        attrDefs: allProductAttrs,
        maxChars: titleMaxWeight,
      });
      const zh = clipTemuProductNameToLimit(title, titleMaxWeight);
      titleTranslateSeqRef.current += 1;
      setForm((prev) => ({
        ...prev,
        title: zh,
      }));
      showToast(t('templateEditor.translatingEnToast'), 'info');
      try {
        await syncTitleEnFromZh(zh, { silent: true, force: true });
        showToast(t('templateEditor.aiTitleDoneTemu'), 'success');
      } catch (translateError) {
        showToast(translateError?.message || t('templateEditor.aiTitlePartial'), 'warning');
      }
    } catch (error) {
      showToast(error?.message || t('templateEditor.aiTitleFail'), 'error');
    } finally {
      setAiTitleBusy(false);
    }
  };

  const handleAssessPrintInfringement = async () => {
    if (printInfringementBusy || form.ipePrintInfringement?.assessing) return;
    const imageUrl = resolveFirstPrintImageUrl(form.printImages);
    if (!imageUrl) {
      showToast(t('publishFlow.needPrint'), 'warning');
      return;
    }
    const confirmed = await confirmIpeCreditAction({
      navId: 'infringementDetection',
      imageCount: 1,
      title: t('publishSpec.infringement'),
      confirmLabel: t('publishFlow.confirmDetect'),
    });
    if (!confirmed) return;

    setPrintInfringementBusy(true);
    setForm((prev) => ({
      ...prev,
      ipePrintInfringement: {
        ...normalizeTemuPrintInfringement(prev.ipePrintInfringement),
        assessing: true,
      },
    }));
    showToast(t('publishFlow.printBusy'), 'info');
    try {
      const result = await assessTemuPublishPrintInfringement(imageUrl);
      setForm((prev) => ({
        ...prev,
        ipePrintInfringement: normalizeTemuPrintInfringement({
          risk: result.risk,
          assessing: false,
          riskAdvice: result.riskAdvice,
          assessedAt: result.assessedAt,
        }),
      }));
      showToast(t('publishFlow.printDone'), 'success');
    } catch (error) {
      setForm((prev) => ({
        ...prev,
        ipePrintInfringement: normalizeTemuPrintInfringement({
          risk: 'pending',
          assessing: false,
          riskAdvice: error?.message || t('publishFlow.printFail'),
          assessedAt: new Date().toISOString(),
        }),
      }));
      showToast(error?.message || t('publishFlow.printFail'), 'error');
    } finally {
      setPrintInfringementBusy(false);
    }
  };

  const validate = (targetForm = form) => {
    if (sizeChartClassError) {
      return {
        ok: false,
        message: sizeChartClassError,
        section: 'spec',
        field: 'sizeChart',
      };
    }
    return validateTemuPublishForm(targetForm, {
      isEdit,
      shopId,
      shopType: activeShop?.shopType,
      attrTpl,
      colorAttr,
      sizeAttr,
      sizeMeasureAttrs,
      sizeChartRequired,
      modelRequired,
      netWeightRequired,
      semiSkuNetContentRequired,
      warehouseOptions: meta.warehouses || [],
      skuPkgCaps,
      needGuideFile,
    });
  };

  const clearVersionConflict = (type) => {
    const key = conflictFieldKey(type);
    setVersionConflicts((prev) => {
      if (!prev?.[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setChangePreviews((prev) => {
      if (!prev?.[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const buildAttrChangePreview = async () => {
    const id = String(selectedAttrTemplateId || '').trim();
    if (!shopId || !id) return { title: t('templateEditor.attrUpdated'), changes: [] };
    const res = await temuPlatformAPI.getProductAttributeTemplate(shopId, id);
    const item = res?.item || {};
    const { patch } = applyProductAttributeTemplatePayload(
      item.payload || {},
      (attrTplRef.current?.requiredProduct || [])
        .concat(attrTplRef.current?.mainAttrs || [])
        .concat(attrTplRef.current?.otherAttrs || [])
        .map((attr) => String(attr.attributeId || ''))
        .filter(Boolean),
    );
    const nextAttrs = alignAttributesToCategoryTemplate({ ...patch }, attrTplRef.current || attrTpl);
    const attrDefs = [
      ...(attrTplRef.current?.requiredProduct || attrTpl?.requiredProduct || []),
      ...(attrTplRef.current?.mainAttrs || attrTpl?.mainAttrs || []),
      ...(attrTplRef.current?.otherAttrs || attrTpl?.otherAttrs || []),
    ];
    const { changes, count } = diffProductAttributeChanges({
      current: form.productAttributes || {},
      next: nextAttrs,
      attrDefs,
    });
    return {
      title: t('templateEditor.attrUpdated'),
      intro: t('templateEditor.attrDiffIntro', { name: item.name || id, count }),
      changes,
      count,
    };
  };

  const buildSizeChartChangePreview = async () => {
    const id = String(selectedSizeChartTemplateId || '').trim();
    if (!shopId || !id) return { title: t('templateEditor.sizeUpdated'), changes: [] };
    const res = await temuPlatformAPI.getSizeChartTemplate(shopId, id);
    const item = res?.item || {};
    const patch = extractSizeChartTemplatePatch(item);
    const { changes, count } = diffSizeChartChanges({
      current: form.sizeChart || {},
      next: patch.sizeChart || {},
      measureAttrs: sizeMeasureAttrs,
    });
    return {
      title: t('templateEditor.sizeUpdated'),
      intro: t('templateEditor.sizeDiffIntro', { name: item.name || id, count }),
      changes,
      count,
    };
  };

  const reloadConflictingTemplateRegion = async (type) => {
    if (!shopId) return;
    setReloadingConflictType(type);
    try {
      if (type === TEMPLATE_VERSION_TYPES.product) {
        const id = String(selectedProductTemplateId || '').trim();
        if (!id) return;
        const res = await temuPlatformAPI.getProductTemplate(shopId, id);
        const item = res?.item || res || {};
        const merged = mergeProductTemplateIntoForm(form, item);
        setForm((prev) => mergeProductTemplateIntoForm(prev, item).form);
        if (merged.selectedAttrTemplateId) {
          setSelectedAttrTemplateId(merged.selectedAttrTemplateId);
        }
        if (merged.selectedSizeChartTemplateId) {
          setSelectedSizeChartTemplateId(merged.selectedSizeChartTemplateId);
        }
        setSelectedProductTemplateVersion(merged.version);
        clearVersionConflict(type);
        showToast(t('publishFlow.reloadedTpl'), 'success');
        return;
      }
      if (type === TEMPLATE_VERSION_TYPES.attribute) {
        const id = String(selectedAttrTemplateId || '').trim();
        if (!id) return;
        const res = await temuPlatformAPI.getProductAttributeTemplate(shopId, id);
        const patch = extractAttrTemplatePatch(res?.item || res || {});
        setForm((prev) => {
          const aligned = alignAttributesToCategoryTemplate(
            { ...(patch.productAttributes || {}) },
            attrTplRef.current || attrTpl,
          );
          return { ...prev, productAttributes: aligned };
        });
        setSelectedAttrTemplateVersion(patch.version);
        clearVersionConflict(type);
        showToast(t('publishFlow.reloadedAttrs'), 'success');
        return;
      }
      if (type === TEMPLATE_VERSION_TYPES.sizeChart) {
        const id = String(selectedSizeChartTemplateId || '').trim();
        if (!id) return;
        const res = await temuPlatformAPI.getSizeChartTemplate(shopId, id);
        const patch = extractSizeChartTemplatePatch(res?.item || res || {});
        if (patch.kind === 'temu-official' || patch.officialSizeChart) {
          setForm((prev) => ({
            ...prev,
            officialSizeChart: patch.officialSizeChart,
            temuSizeTemplateIds: (patch.platformBusinessIds || []).map(String),
          }));
        } else {
          applySizeChart(patch.sizeChart || {}, { replace: true });
        }
        setSelectedSizeChartTemplateVersion(patch.version);
        clearVersionConflict(type);
        showToast(t('publishFlow.reloadedSize'), 'success');
      }
    } catch (e) {
      if (isMissingTemplateError(e)
        && (type === TEMPLATE_VERSION_TYPES.attribute || type === TEMPLATE_VERSION_TYPES.sizeChart)) {
        if (type === TEMPLATE_VERSION_TYPES.attribute) {
          setSelectedAttrTemplateId('');
          setSelectedAttrTemplateVersion(null);
        } else {
          setSelectedSizeChartTemplateId('');
          setSelectedSizeChartTemplateVersion(null);
        }
        clearVersionConflict(type);
        showToast(
          type === TEMPLATE_VERSION_TYPES.sizeChart
            ? t('publishFlow.refSizeOut')
            : t('publishFlow.refAttrOut'),
          'warning',
        );
        return;
      }
      showToast(e?.message || t('templateEditor.reloadFail'), 'error');
    } finally {
      setReloadingConflictType('');
    }
  };

  const onSubmit = async () => {
    // 印花图仅站内展示 / 侵权检测；货号在上传印花图时已拼图案编号
    let enriched = await enrichTemuFormPrintNames(form);
    try {
      enriched = await ensureTemuFormImagesHosted(enriched, { shopId });
    } catch (imageErr) {
      applyFieldErrors([{
        field: 'skcList',
        section: 'spec',
        message: imageErr?.message || t('publishFlow.hostFail'),
      }]);
      return;
    }
    enriched = await ensureTemuColorBlocksOnForm(enriched, { shopId });
    try {
      enriched = await ensureTemuMaterialFromMainOnForm(enriched, { shopId });
    } catch (materialErr) {
      applyFieldErrors([{
        field: 'materialImgUrl',
        section: 'spec',
        message: materialErr?.message || t('publishFlow.materialSquare'),
      }]);
      return;
    }
    let formWithPrintNames = enriched;
    if (formWithPrintNames !== form) {
      setForm(formWithPrintNames);
    }

    const zhTitle = String(formWithPrintNames.title || '').trim();
    if (zhTitle && !String(formWithPrintNames.titleEn || '').trim()) {
      setSubmitting(true);
      setSubmitHint(t('templateEditor.translatingEnToast'));
      try {
        const titleEn = await syncTitleEnFromZh(zhTitle, { silent: true, force: true });
        formWithPrintNames = { ...formWithPrintNames, titleEn };
      } catch (error) {
        applyFieldErrors([{
          field: 'title',
          section: 'basic',
          message: error?.message || t('templateEditor.translateRetry'),
        }]);
        setSubmitting(false);
        setSubmitHint('');
        return;
      }
      setSubmitting(false);
      setSubmitHint('');
    }

    const result = validate(formWithPrintNames);
    if (!result.ok) {
      applyFieldErrors([{
        field: result.field || 'form',
        section: result.section || 'basic',
        message: result.message,
      }]);
      return;
    }
    if (sizeChartRequired) {
      const sizeCheck = officialSizeChartRef.current?.validate?.();
      if (sizeCheck && !sizeCheck.ok) {
        applyFieldErrors([{
          field: 'sizeChart',
          section: 'spec',
          message: sizeCheck.message,
        }]);
        return;
      }
    }
    setFieldErrors({});
    setSubmitting(true);
    try {
      // 新建发品 / 部分编辑：均按平台规范做发品前核验
      setSubmitHint(isEdit
        ? t('publishFlow.checkingSku')
        : t('publishFlow.checkingSku'));
      const preflight = await runTemuPublishPreflight({
        shopId,
        form: formWithPrintNames,
        isEdit,
        attrTpl,
        shopType: activeShop?.shopType,
        shopCaps,
        siteList: meta.sites,
      });
      if (!preflight.ok) {
        applyFieldErrors([{
          field: preflight.field || 'form',
          section: preflight.section || 'basic',
          message: preflight.message,
        }]);
        return;
      }

      let resolvedSizeTemplateIds = formWithPrintNames.temuSizeTemplateIds || [];
      if (sizeChartRequired && !isEdit) {
        setSubmitHint(t('publishFlow.creatingSizeTemu'));
        try {
          if (!officialSizeChartRef.current?.resolveForSubmit) {
            throw new Error(t('publishFlow.sizePanelNotReady'));
          }
          resolvedSizeTemplateIds = await officialSizeChartRef.current.resolveForSubmit();
          formWithPrintNames = {
            ...formWithPrintNames,
            temuSizeTemplateIds: resolvedSizeTemplateIds.map(String),
          };
          setForm((prev) => ({
            ...prev,
            temuSizeTemplateIds: resolvedSizeTemplateIds.map(String),
          }));
        } catch (sizeErr) {
          applyFieldErrors([{
            field: 'sizeChart',
            section: 'spec',
            message: sizeErr?.message || t('publishFlow.sizeHandleFail'),
          }]);
          showToast(sizeErr?.message || t('publishFlow.sizeHandleFail'), 'error');
          return;
        }
      }

      // 自定义颜色：stub addCustomAttributeValue 不可用，须 createPublishSpec 拿 specId
      if (colorAttr && !isEdit) {
        setSubmitHint(t('publishFlow.creatingColorSpec'));
        try {
          const nextSkc = await ensureTemuCustomColorSpecs({
            shopId,
            skcList: formWithPrintNames.skcList,
            colorAttr,
            attrTpl,
            api: temuPlatformAPI,
          });
          formWithPrintNames = { ...formWithPrintNames, skcList: nextSkc };
          setForm((prev) => ({ ...prev, skcList: nextSkc }));
        } catch (specErr) {
          applyFieldErrors([{
            field: 'skcList',
            section: 'spec',
            message: specErr?.message || t('publishFlow.colorSpecFail'),
          }]);
          showToast(specErr?.message || t('publishFlow.colorSpecFail'), 'error');
          return;
        }
      }

      const templateRefs = collectAppliedTemplateRefs({
        productTemplateId: selectedProductTemplateId,
        productTemplateVersion: selectedProductTemplateVersion,
        attrTemplateId: selectedAttrTemplateId,
        attrTemplateVersion: selectedAttrTemplateVersion,
        sizeChartTemplateId: selectedSizeChartTemplateId,
        sizeChartTemplateVersion: selectedSizeChartTemplateVersion,
      });
      if (templateRefs.length > 0) {
        setSubmitHint(t('publishFlow.checkingTemplates'));
        const versionCheck = await checkTemplateVersions(temuPlatformAPI, shopId, templateRefs);
        if (!versionCheck.ok) {
          const conflicts = versionCheck.conflicts || [];
          const { missingLibraryRefs, blocking } = partitionTemplateVersionConflicts(conflicts);
          if (missingLibraryRefs.length) {
            missingLibraryRefs.forEach((c) => {
              if (c.type === TEMPLATE_VERSION_TYPES.attribute) {
                setSelectedAttrTemplateId('');
                setSelectedAttrTemplateVersion(null);
              } else if (c.type === TEMPLATE_VERSION_TYPES.sizeChart) {
                setSelectedSizeChartTemplateId('');
                setSelectedSizeChartTemplateVersion(null);
              }
            });
          }
          if (blocking.length) {
            const nextMarks = {};
            blocking.forEach((c) => {
              const key = conflictFieldKey(c.type);
              if (key) nextMarks[key] = true;
            });
            setVersionConflicts(nextMarks);
            const nextPreviews = {};
            await Promise.all(blocking.map(async (c) => {
              try {
                if (c.type === TEMPLATE_VERSION_TYPES.attribute) {
                  nextPreviews.productAttributes = await buildAttrChangePreview();
                } else if (c.type === TEMPLATE_VERSION_TYPES.sizeChart) {
                  nextPreviews.sizeChart = await buildSizeChartChangePreview();
                }
              } catch {
                /* ignore */
              }
            }));
            setChangePreviews(nextPreviews);
            const attrCount = nextPreviews.productAttributes?.count;
            const sizeCount = nextPreviews.sizeChart?.count;
            const detailBits = [];
            if (attrCount != null) detailBits.push(t('templateEditor.attrCount', { count: attrCount }));
            if (sizeCount != null) detailBits.push(t('templateEditor.sizeCount', { count: sizeCount }));
            showToast(
              detailBits.length
                ? `${conflictToastMessage(blocking)}${t('templateEditor.conflictDetail', { detail: detailBits.join('，') })}`
                : conflictToastMessage(blocking),
              'warning',
              3000,
            );
            const firstField = conflictFieldKey(blocking[0]?.type) || 'form';
            const el = document.querySelector(`[data-field-error="${firstField}"]`);
            if (el?.scrollIntoView) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
          }
        }
        setVersionConflicts({});
        setChangePreviews({});
      }

      // 提交币种：半托管/全托管对齐发布规范；自运营对齐站点列表（已写入 publishCurrencies）
      const alignedCurrency = pickPublishCurrency(
        publishCurrencies,
        formWithPrintNames.currency || platformCurrency,
        {
          forceDefault: forcePublishCurrencyDefault,
          defaultCode: forcePublishCurrencyDefault ? 'USD' : '',
        },
      );
      if (!alignedCurrency) {
        applyFieldErrors([{
          field: 'currency',
          section: 'sales',
          message: shopCaps.usesPriceInfo
            ? t('publishFlow.needSiteCurrency')
            : t('publishFlow.needRuleCurrency'),
        }]);
        return;
      }
      const sizeValueLabels = { ...(formWithPrintNames.sizeValueLabels || {}) };
      (sizeAttr?.values || []).forEach((v) => {
        if (v?.id != null && v?.name) sizeValueLabels[String(v.id)] = String(v.name);
      });
      const resolvedSizeSaleAttrId = formWithPrintNames.sizeSaleAttributeId || sizeAttr?.attributeId || '';
      // 提交纵轴必须与供应表 SKU 一致，勿用可能含重复行的 selectedSizeIds
      const fromSkcSizes = deriveSelectedSizeIds(formWithPrintNames.skcList).map((id) => String(id));
      const resolvedSelectedSizeIds = fromSkcSizes.length
        ? fromSkcSizes
        : (formWithPrintNames.selectedSizeIds || []).map((id) => String(id));
      const submitFormBase = {
        ...formWithPrintNames,
        sizeValueLabels,
        // 尺码表关联次销售：缺 id 时平台会报「尺寸表填写数值不能为空 / 单选多值」
        sizeSaleAttributeId: resolvedSizeSaleAttrId,
        selectedSizeIds: resolvedSelectedSizeIds,
        // 从已发布商品另存草稿再发：按新建品处理，勿带平台 SPU
        spuName: isEdit ? formWithPrintNames.spuName : '',
        currency: alignedCurrency || formWithPrintNames.currency,
        skcList: (formWithPrintNames.skcList || []).map((skc) => {
          const resolved = resolveSkcColorAgainstTemplate({
            ...skc,
            colorAttributeId: skc.colorAttributeId || colorAttr?.attributeId || '',
          }, colorAttr);
          return {
            ...resolved,
            temuSpecId: skc.temuSpecId || resolved.temuSpecId,
            // 确保主规格属性 ID 齐全，否则 sale_attribute 组不出来
            colorLabel: resolved.colorIsCustom || resolved.colorExtraValue
              ? (resolved.colorExtraValue || resolved.colorLabel)
              : (colorAttr?.values?.find((v) => String(v.id) === String(resolved.colorValueId))?.name
                || resolved.colorLabel),
            skcName: isEdit ? skc.skcName : '',
            skuRows: (skc.skuRows || []).map((sku) => ({
              ...sku,
              sizeAttributeId: sku.sizeAttributeId || sizeAttr?.attributeId || resolvedSizeSaleAttrId || '',
              sizeLabel: sizeAttr?.values?.find((v) => String(v.id) === String(sku.sizeValueId))?.name
                || sku.sizeLabel
                || sizeValueLabels[String(sku.sizeValueId || '')]
                || '',
              currency: alignedCurrency || sku.currency || formWithPrintNames.currency,
              skuCode: isEdit ? sku.skuCode : '',
            })),
          };
        }),
      };
      // 丢掉模板残留规格行；mode=3（如周长）收敛为单值，避免平台报「单选多值」
      const submitForm = {
        ...submitFormBase,
        sizeChart: pruneSizeChartForSubmit(submitFormBase, sizeMeasureAttrs),
        shopType: activeShop?.shopType,
        siteList: meta.sites,
        ...(skipSiteWarehouseFreight
          ? {
            selectedSites: [],
            warehouseId: '',
            warehouseName: '',
            freightTemplateId: '',
            shipmentLimitSecond: '',
            temuSiteIds: [],
            temuSiteId: '',
            temuFreightTemplateId: '',
          }
          : {}),
      };
      if (isEdit) {
        const idCheck = validateEditPlatformIds(submitForm, editBaselineRef.current);
        if (!idCheck.ok) {
          applyFieldErrors([{
            field: 'skcList',
            section: 'spec',
            message: idCheck.message,
          }]);
          return;
        }
        const priceChanges = collectSkuPriceChanges(submitForm, editBaselineRef.current, shopCaps);
        if (!priceChanges.empty && priceChanges.mode === 'none') {
          applyFieldErrors([{
            field: 'supply',
            section: 'spec',
            message: t('publishFlow.priceUnsupported'),
          }]);
          return;
        }
        // 成本价任意变更、售价涨价：先确认涨价原因
        if (!priceChanges.empty && (priceChanges.mode === 'cost' || priceChanges.hasIncrease)) {
          setPriceConfirm({ submitForm, changes: priceChanges });
          return;
        }
        await finishEditSubmit(submitForm, priceChanges, {});
        return;
      }

      let payload;
      try {
        payload = buildTemuPublishPayload(submitForm, {
          isEdit: false,
          sizeAttrs: sizeMeasureAttrs,
          skuPkgCaps,
          shopType: activeShop?.shopType,
          siteList: meta.sites,
          attrTpl,
          categories: meta.categories,
        });
      } catch (buildErr) {
        applyFieldErrors([{
          field: 'form',
          section: 'basic',
          message: buildErr?.message || t('publishFlow.buildFail'),
        }]);
        return;
      }
      const sampleCurrency = samplePublishPayloadCurrency(payload);
      if (!sampleCurrency) {
        applyFieldErrors([{
          field: 'supply',
          section: 'sales',
          message: shopCaps.usesPriceInfo
            ? t('publishFlow.needSaleCurrency')
            : t('publishFlow.needSupplyCurrency'),
        }]);
        return;
      }
      // 提交前落一份完整草稿：发布记录可关联；编辑页回填不依赖审核中不全的平台详情
      let submitDraftId = draftId;
      try {
        setSubmitHint(t('publishFlow.savingDraft'));
        const draftPayload = {
          form: {
            ...submitForm,
            selectedSizeIds: (submitForm.selectedSizeIds?.length
              ? submitForm.selectedSizeIds
              : deriveSelectedSizeIds(submitForm.skcList)
            ).map((id) => String(id)),
          },
        };
        const draftRes = submitDraftId
          ? await temuPlatformAPI.updatePublishDraft(shopId, submitDraftId, draftPayload)
          : await temuPlatformAPI.createPublishDraft(shopId, draftPayload);
        if (draftRes?.id != null) {
          submitDraftId = String(draftRes.id);
          setDraftId(submitDraftId);
        }
      } catch (draftErr) {
        showToast(draftErr?.message || t('publishFlow.draftFailContinue'), 'warning');
      }
      const params = submitDraftId ? { draftId: Number(submitDraftId) || submitDraftId } : {};
      setSubmitHint(t('publishFlow.submittingJob'));
      let job = await temuPlatformAPI.publishOrEditProduct(shopId, payload, params);
      const jobId = job?.jobId;
      let platformRes = job?.response;
      if (jobId && !job?.done) {
        setSubmitHint(job?.message || t('publishFlow.queueWait'));
        const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
        let latest = job;
        while (latest && !latest.done) {
          await sleep(700);
          latest = await temuPlatformAPI.getGoodsSubmitJob(shopId, jobId);
          if (!latest) break;
          setSubmitHint(latest.message || t('publishFlow.publishing'));
          job = latest;
        }
        platformRes = latest?.response;
        if (!platformRes && latest?.status === 'failed') {
          throw new Error(latest?.error || latest?.message || t('publishExtra.submitFail'));
        }
      } else if (!platformRes && job && (job.code != null || job.info != null)) {
        platformRes = job;
      }
      const res = platformRes || job?.response || job;
      const ok = isTemuPublishSuccess(res)
        || job?.ok === true
        || String(job?.status || '').toLowerCase() === 'success';
      if (!ok) {
        const fieldErrs = extractTemuPublishFieldErrors(res, extractTemuPublishFailMessage(res) || job?.error);
        applyFieldErrors(fieldErrs, extractTemuPublishFailMessage(res) || job?.message || job?.error);
        return;
      }
      setFieldErrors({});
      showToast(t('publishFlow.submittedReview'), 'success');
      const publishedSpu = extractTemuPublishedSpu(res, payload, form.spuName)
        || job?.publishedSpu
        || '';
      if (shopId) {
        try {
          await temuPlatformAPI.createPublishRecord(shopId, {
            form: submitForm,
            platformRes: res,
            publishedSpu,
            draftId: submitDraftId,
            action: 'publish',
          });
        } catch {
          /* 本地记录失败不阻断跳转 */
        }
      }
      // 已发布成功：清理本地草稿，避免草稿箱残留
      if (shopId && submitDraftId) {
        try {
          await temuPlatformAPI.deletePublishDraft(shopId, submitDraftId);
          setDraftId('');
        } catch {
          /* 清理失败不阻断跳转 */
        }
      }
      if (!publishedSpu) {
        showToast(t('publishFlow.noSpu'), 'warning');
      }
      if (shopId && publishedSpu) {
        temuPlatformAPI.syncCertificates(shopId, { spuName: publishedSpu }).catch(() => { });
        showToast(t('publishFlow.certSynced'), 'info');
      }
      allowUnsavedLeave();
      navigate(buildTemuPath('products/records'), {
        replace: true,
        state: { refreshPublishRecords: true, at: Date.now() },
      });
    } catch (e) {
      const msg = e?.message || t('publishExtra.submitFail');
      const fieldErrs = extractTemuPublishFieldErrors(
        { message: msg, msg, errorMsg: msg },
        msg,
      );
      applyFieldErrors(
        fieldErrs.length ? fieldErrs : [{ field: 'form', section: 'basic', message: msg }],
        msg,
      );
    } finally {
      setSubmitting(false);
      setSubmitHint('');
    }
  };

  /** 编辑提交：partialEdit（非价字段）+ 按店铺类型专用改价接口 */
  const finishEditSubmit = async (submitForm, priceChanges, priceMeta = {}) => {
    setSubmitting(true);
    setPriceConfirm(null);
    try {
      // 涨价前查活动锁（priceLockType=1 强拦截）
      if (!priceChanges.empty && priceChanges.hasIncrease && shopId && submitForm.spuName) {
        setSubmitHint(t('publishFlow.checkingSiteLock'));
        try {
          const searchRes = await temuPlatformAPI.searchProduct(shopId, {
            spuNameList: [submitForm.spuName],
            pageNum: 1,
            pageSize: 10,
          });
          const lockMap = extractPriceLocksFromSearch(searchRes);
          const lockCheck = validatePriceChangesAgainstLocks(
            priceChanges,
            lockMap,
            submitForm.selectedSites || [],
          );
          if (!lockCheck.ok) {
            throw new Error(lockCheck.message);
          }
        } catch (lockErr) {
          // search 失败不阻断降价；涨价时若明确锁失败则抛出
          if (/涨价锁定|不可涨价/.test(String(lockErr?.message || ''))) {
            throw lockErr;
          }
        }
      }

      const payload = buildTemuGoodsUpdatePayload(submitForm, {
        sizeAttrs: sizeMeasureAttrs,
        skuPkgCaps,
        shopType: activeShop?.shopType,
        siteList: meta.sites,
        attrTpl,
        categories: meta.categories,
      });

      let submitDraftId = draftId;
      try {
        setSubmitHint(t('publishFlow.savingDraft'));
        const draftPayload = {
          form: {
            ...submitForm,
            selectedSizeIds: (submitForm.selectedSizeIds?.length
              ? submitForm.selectedSizeIds
              : deriveSelectedSizeIds(submitForm.skcList)
            ).map((id) => String(id)),
          },
        };
        const draftRes = submitDraftId
          ? await temuPlatformAPI.updatePublishDraft(shopId, submitDraftId, draftPayload)
          : await temuPlatformAPI.createPublishDraft(shopId, draftPayload);
        if (draftRes?.id != null) {
          submitDraftId = String(draftRes.id);
          setDraftId(submitDraftId);
        }
      } catch (draftErr) {
        showToast(draftErr?.message || t('publishFlow.draftFailContinue'), 'warning');
      }

      let partialOk = false;
      let publishedSpu = submitForm.spuName || '';
      if (payload) {
        const params = submitDraftId ? { draftId: Number(submitDraftId) || submitDraftId } : {};
        setSubmitHint(t('publishFlow.submittingEdit'));
        let job = await temuPlatformAPI.partialEditProduct(shopId, payload, params);
        const jobId = job?.jobId;
        let platformRes = job?.response;
        if (jobId && !job?.done) {
          setSubmitHint(job?.message || t('publishFlow.queueWait'));
          const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
          let latest = job;
          while (latest && !latest.done) {
            await sleep(700);
            latest = await temuPlatformAPI.getGoodsSubmitJob(shopId, jobId);
            if (!latest) break;
            setSubmitHint(latest.message || t('publishFlow.editing'));
            job = latest;
          }
          platformRes = latest?.response;
          if (!platformRes && latest?.status === 'failed') {
            throw new Error(latest?.error || latest?.message || t('publishFlow.editSubmitFail'));
          }
        } else if (!platformRes && job && (job.code != null || job.info != null)) {
          platformRes = job;
        }
        const res = platformRes || job?.response || job;
        const ok = isTemuPublishSuccess(res)
          || job?.ok === true
          || String(job?.status || '').toLowerCase() === 'success';
        if (!ok) {
          const fieldErrs = extractTemuPublishFieldErrors(res, extractTemuPublishFailMessage(res) || job?.error);
          applyFieldErrors(fieldErrs, extractTemuPublishFailMessage(res) || job?.message || job?.error);
          return;
        }
        partialOk = true;
        publishedSpu = extractTemuPublishedSpu(res, payload, submitForm.spuName)
          || job?.publishedSpu
          || publishedSpu;
      }

      let priceMsg = '';
      if (!priceChanges.empty) {
        setSubmitHint(priceChanges.mode === 'sale' ? t('publishFlow.updatingSale') : t('publishFlow.updatingCost'));
        if (priceChanges.mode === 'cost') {
          const costBody = buildUpdateCostPayload(priceChanges, {
            reasonBySkc: priceMeta.reasonBySkc || {},
            remarkBySkc: priceMeta.remarkBySkc || {},
            fileBySkc: priceMeta.fileBySkc || {},
          });
          const chunks = chunkUpdateCostPayload(costBody);
          for (let i = 0; i < chunks.length; i += 1) {
            if (chunks.length > 1) {
              setSubmitHint(t('publishFlow.updatingCostChunk', { current: i + 1, total: chunks.length }));
            }
            const costRes = await temuPlatformAPI.updateProductCost(shopId, chunks[i]);
            const interpreted = interpretPriceUpdateResponse(costRes, 'cost');
            if (!interpreted.ok) {
              throw new Error(interpreted.message);
            }
            priceMsg = interpreted.message;
          }
        } else if (priceChanges.mode === 'sale') {
          const saleBody = buildSaveSalePricePayload(priceChanges, {
            sites: submitForm.selectedSites || [],
            riseReason: priceMeta.saleRiseReason || '',
            siteList: submitForm.siteList || meta.sites,
          });
          const saleRes = await temuPlatformAPI.saveProductSalePrice(shopId, saleBody);
          const interpreted = interpretPriceUpdateResponse(saleRes, 'sale');
          if (!interpreted.ok) {
            throw new Error(interpreted.message);
          }
          priceMsg = interpreted.message;
          if (interpreted.pending) {
            showToast(interpreted.message, 'warning');
          }
        }
      }

      if (!partialOk && priceChanges.empty) {
        throw new Error(t('publishFlow.noChanges'));
      }

      setFieldErrors({});
      if (partialOk && !priceChanges.empty) {
        showToast(priceMsg ? t('publishFlow.editSubmittedWith', { detail: priceMsg }) : t('publishFlow.editPriceUpdated'), 'success');
      } else if (!priceChanges.empty) {
        showToast(priceMsg || t('publishFlow.priceUpdated'), 'success');
      } else {
        showToast(t('publishFlow.editSubmitted'), 'success');
      }
      if (shopId && (partialOk || !priceChanges.empty)) {
        try {
          await temuPlatformAPI.createPublishRecord(shopId, {
            form: submitForm,
            platformRes: null,
            publishedSpu,
            draftId: submitDraftId,
            action: 'edit',
          });
        } catch {
          /* ignore */
        }
      }
      if (!publishedSpu) {
        showToast(t('publishFlow.noSpu'), 'warning');
      }
      allowUnsavedLeave();
      navigate(buildTemuPath('products/records'), {
        replace: true,
        state: { refreshPublishRecords: true, at: Date.now() },
      });
      return { ok: true };
    } catch (e) {
      const msg = e?.message || t('publishExtra.submitFail');
      const fieldErrs = extractTemuPublishFieldErrors(
        { message: msg, msg, errorMsg: msg },
        msg,
      );
      applyFieldErrors(
        fieldErrs.length ? fieldErrs : [{ field: 'form', section: 'basic', message: msg }],
        msg,
      );
      return { ok: false, message: msg };
    } finally {
      setSubmitting(false);
      setSubmitHint('');
    }
  };

  const allowedAttrIds = useMemo(
    () => allProductAttrs.map((attr) => String(attr.attributeId || '')).filter(Boolean),
    [allProductAttrs],
  );
  attrTplRef.current = attrTpl;

  const resolvedAttrStyleNo = useMemo(
    () => {
      const fromSkc = (form.skcList || [])
        .map((skc) => String(skc?.supplierCode || '').trim())
        .find(Boolean);
      return styleNoFromSupplierCode(
        fromSkc || form.supplierCode,
        attrTemplates.map((item) => item?.styleNo).filter(Boolean),
      );
    },
    [form.skcList, form.supplierCode, attrTemplates],
  );

  const primarySupplierCode = useMemo(() => {
    const fromSkc = (form.skcList || [])
      .map((skc) => String(skc?.supplierCode || '').trim())
      .find(Boolean);
    return fromSkc || String(form.supplierCode || '').trim();
  }, [form.skcList, form.supplierCode]);

  useEffect(() => {
    const target = String(form.productTypeId || '');
    if (!target || attrLoading || !allowedAttrIds.length) return undefined;
    if (attrTemplatesLoadedForRef.current !== target) return undefined;

    const styleKey = String(resolvedAttrStyleNo || '').trim().toLowerCase();
    const pendingCategory = String(pendingAttrAutoForRef.current || '') === target;
    // 首次进入编辑/回填：锁定当前款号，避免立刻重套覆盖已有属性
    if (lastAttrAutoStyleKeyRef.current === null) {
      lastAttrAutoStyleKeyRef.current = styleKey;
      if (!pendingCategory) return undefined;
    }
    const styleChanged = styleKey !== lastAttrAutoStyleKeyRef.current;
    if (!pendingCategory && !styleChanged) return undefined;

    const picked = pickAutoRuleTemplate(attrTemplates, { styleNo: resolvedAttrStyleNo });
    pendingAttrAutoForRef.current = '';
    lastAttrAutoStyleKeyRef.current = styleKey;
    if (!picked?.id) return undefined;

    let cancelled = false;
    setSelectedAttrTemplateId(String(picked.id));
    setAttrTemplateBusy(true);
    temuPlatformAPI.getProductAttributeTemplate(shopId, picked.id)
      .then((res) => {
        if (cancelled) return;
        const payload = res?.item?.payload || {};
        const { patch, filled } = applyProductAttributeTemplatePayload(payload, allowedAttrIds);
        setForm((prev) => {
          if (String(prev.productTypeId) !== target) return prev;
          // 整表替换：空模板则清空类目属性
          const aligned = alignAttributesToCategoryTemplate({ ...patch }, attrTplRef.current);
          return { ...prev, productAttributes: aligned };
        });
        const styleHint = resolvedAttrStyleNo ? t('templateEditor.styleHint', { styleNo: resolvedAttrStyleNo }) : '';
        showToast(
          filled
            ? t('templateEditor.autoNamed', { name: picked.name || picked.id, hint: styleHint })
            : t('templateEditor.autoEmpty'),
          'success',
        );
      })
      .catch(() => {
        /* 自动套用失败不打断发品 */
      })
      .finally(() => {
        if (!cancelled) setAttrTemplateBusy(false);
      });
    return () => { cancelled = true; };
  }, [
    shopId,
    form.productTypeId,
    resolvedAttrStyleNo,
    attrLoading,
    allowedAttrIds,
    attrTemplates,
    t,
  ]);

  const applyAttrTemplate = async (templateId) => {
    const id = String(templateId || '').trim();
    if (!shopId || !id) return;
    if (!allowedAttrIds.length) {
      showToast(t('templateEditor.attrsNotLoaded'), 'error');
      return;
    }
    setAttrTemplateBusy(true);
    try {
      const res = await temuPlatformAPI.getProductAttributeTemplate(shopId, id);
      const item = res?.item || {};
      const tplCatId = String(item.categoryId || item.payload?.categoryId || '').trim();
      const tplTypeId = String(item.productTypeId || item.payload?.productTypeId || '').trim();
      const curCatId = String(form.categoryId || '').trim();
      const curTypeId = String(form.productTypeId || '').trim();
      if (tplCatId && curCatId && tplCatId !== curCatId) {
        showToast(
          fixedUiTpl('该属性库属于类目 {0}，与当前类目 {1} 不一致，已取消套用。请选用当前类目下的属性库', tplCatId, curCatId),
          'error',
        );
        setSelectedAttrTemplateId('');
        return;
      }
      if (!tplCatId && tplTypeId && curTypeId && tplTypeId !== curTypeId) {
        showToast(t('publishFlow.attrTypeMismatch'), 'error');
        setSelectedAttrTemplateId('');
        return;
      }
      const payload = item.payload || {};
      const { patch, filled } = applyProductAttributeTemplatePayload(payload, allowedAttrIds);
      setForm((prev) => {
        // 整表替换：空模板则清空类目属性；仍按当前平台 schema 对齐
        const aligned = alignAttributesToCategoryTemplate({ ...patch }, attrTplRef.current || attrTpl);
        return { ...prev, productAttributes: aligned };
      });
      setSelectedAttrTemplateVersion(parseTemplateVersion(item.version));
      showToast(filled ? t('templateEditor.attrApplied', { count: filled }) : t('templateEditor.attrEmptyCleared'), 'success');
    } catch (e) {
      showToast(e?.message || t('templateEditor.attrApplyFail'), 'error');
    } finally {
      setAttrTemplateBusy(false);
    }
  };

  const onAttrTemplateSelect = (templateId) => {
    const id = String(templateId || '').trim();
    setSelectedAttrTemplateId(id);
    if (id) {
      applyAttrTemplate(id);
      return;
    }
    setSelectedAttrTemplateVersion(null);
    // 下拉选空：直接清除类目属性
    setForm((prev) => ({ ...prev, productAttributes: {} }));
  };

  const openAttrSaveModal = () => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'error');
      return;
    }
    if (!form.categoryId || !form.productTypeId) {
      showToast(t('publishAttr.pickCategory'), 'error');
      return;
    }
    if (!countFilledProductAttributes(form.productAttributes)) {
      showToast(t('templateEditor.fillAttrsFirst'), 'error');
      return;
    }
    setAttrSaveModalOpen(true);
  };

  const refreshAttrTemplates = async () => {
    if (!shopId || !form.productTypeId) return;
    try {
      const listRes = await temuPlatformAPI.listProductAttributeTemplates(shopId, {
        productTypeId: form.productTypeId,
        page: 1,
        pageSize: 50,
      });
      setAttrTemplates(Array.isArray(listRes?.items) ? listRes.items : []);
    } catch {
      /* ignore */
    }
  };
  const visibleOptional = useMemo(() => {
    if (attrsExpanded) return visibleOptionalAttrs;
    const hintIds = new Set(Object.keys(indexAttributeChangeHints(
      changePreviews.productAttributes,
      !!versionConflicts.productAttributes,
    )));
    const head = visibleOptionalAttrs.slice(0, 9);
    const headIds = new Set(head.map((a) => String(a.attributeId)));
    const extraTail = visibleOptionalAttrs
      .slice(9)
      .filter((a) => hintIds.has(String(a.attributeId)))
      .filter((a) => !headIds.has(String(a.attributeId)));
    return extraTail.length ? [...head, ...extraTail] : head;
  }, [
    attrsExpanded,
    visibleOptionalAttrs,
    changePreviews.productAttributes,
    versionConflicts.productAttributes,
  ]);

  const attrVersionHintById = useMemo(
    () => indexAttributeChangeHints(
      changePreviews.productAttributes,
      !!versionConflicts.productAttributes,
    ),
    [changePreviews.productAttributes, versionConflicts.productAttributes],
  );

  return (
    <div className={`tpo-wizard tpo-publish${submitting ? ' is-publishing' : ''}`}>
      {submitting ? (
        <div className="tpo-publish-loading" role="status" aria-live="polite" aria-busy="true">
          <div className="tpo-publish-loading__card">
            <i className="tpo-publish-loading__spinner" aria-hidden />
            <strong>
              {isEdit ? t('publishChrome.submittingEdit') : t('publishChrome.publishing')}
            </strong>
            <span>
              {submitHint || t('publishChrome.submitWait', { platform: 'TEMU' })}
            </span>
          </div>
        </div>
      ) : null}
      <aside className="tpo-wizard__steps" aria-label={t('publishChrome.sectionsAria')}>
        <div className="tpo-wizard__steps-label">
          {isEdit ? t('publishChrome.modeEdit') : t('publishChrome.modePublish')}
        </div>
        {SECTIONS.map((item, idx) => (
          <button
            key={item.id}
            type="button"
            className={`tpo-wizard__step${activeSection === item.id ? ' tpo-wizard__step--active' : ''}`}
            onClick={() => scrollToSection(item.id)}
          >
            <span className="tpo-wizard__step-index">{idx + 1}</span>
            <span className="tpo-wizard__step-title">{t(`publishChrome.${item.titleKey}`)}</span>
          </button>
        ))}
        <div className="tpo-publish__progress">
          <div className="tpo-publish__progress-label">{t('publishChrome.progress', { percent: completion })}</div>
          <div className="tpo-publish__progress-bar">
            <i style={{ width: `${completion}%` }} />
          </div>
        </div>
      </aside>

      <section className="tpo-wizard__panel">
        <div className="tpo-wizard__panel-scroll">
          {draftLoading ? (
            <p className="tpo-wizard__inline-hint">
              {initialProductTemplateId || fromProductTemplate ? t('publishChrome.loadingTemplate') : t('publishChrome.loadingDraft')}
            </p>
          ) : null}
          {loadingSpu ? <p className="tpo-wizard__inline-hint">{t('publishChrome.loadingDetail')}</p> : null}
          {fieldErrors.form ? (
            <div className="tpo-publish-form-error" role="alert" data-field-error="form">
              {fieldErrors.form}
            </div>
          ) : null}

          {/* —— 基础信息 —— */}
          <div id="tpo-pub-basic" className="tpo-wizard__block" onFocusCapture={() => setActiveSection('basic')}>
            <h3>{t('publishChrome.sectionBasic')}</h3>

            <div className="tpo-wizard__form">
              <label className={`tpo-field tpo-field--full${fieldErrors.categoryId ? ' tpo-field--invalid' : ''}`} data-field-error="categoryId">
                <span className="tpo-field__label">{t('publishBasic.category')} <RequiredMark /></span>
                <CategoryCascaderSelect
                  shopId={shopId}
                  categories={meta.categories}
                  value={form.categoryId}
                  label={form.categoryPathLabel}
                  disabled={!shopId || metaLoading || isEdit}
                  placeholder={!shopId ? t('returnList.pickShop') : t('publishBasic.pickCategory')}
                  onChange={onCategoryChange}
                />
                {!isEdit && !metaLoading && shopId && !(Array.isArray(meta.categories) ? meta.categories.length : 0) ? (
                  <p className="tpo-wizard__inline-hint">{t('publishBasic.noCategoryCache')}</p>
                ) : null}

                {isEdit ? (
                  <p className="tpo-wizard__inline-hint">{t('publishBasic.categoryLocked')}</p>
                ) : null}
                <FieldErrorText message={fieldErrors.categoryId} />
              </label>

              {!isEdit && form.categoryId ? (
                <label
                  className={`tpo-field tpo-field--full${versionConflicts.productTemplate ? ' tpo-field--version-conflict' : ''}`}
                  data-field-error="productTemplate"
                >
                  <span className="tpo-field__label">
                    {t('publishBasic.productTemplate')}
                    <TemplateVersionConflictMark
                      active={!!versionConflicts.productTemplate}
                      reloading={reloadingConflictType === TEMPLATE_VERSION_TYPES.product}
                      onReload={() => reloadConflictingTemplateRegion(TEMPLATE_VERSION_TYPES.product)}
                    />
                  </span>
                  <SearchableSelect
                    value={selectedProductTemplateId}
                    disabled={!shopId || productTemplateBusy || !form.productTypeId}
                    placeholder={
                      productTemplateBusy
                        ? t('publishBasic.loadingTemplates')
                        : (!form.productTypeId
                          ? t('publishBasic.noProductType')
                          : (productTemplates.length ? t('publishBasic.pickTemplate') : t('publishBasic.noTemplateForCat')))
                    }
                    emptyText={t('publishBasic.noMatchTemplate')}
                    onChange={onProductTemplateSelect}
                    options={productTemplates.map((item) => ({
                      value: String(item.id),
                      label: item.sortOrder != null
                        ? t('publishBasic.priorityLabel', { name: item.name || item.id, order: item.sortOrder })
                        : (item.name || String(item.id)),
                    }))}
                  />
                  <p className="tpo-wizard__inline-hint">
                    {productTemplates.length > 1
                      ? t('publishBasic.defaultHighest')
                      : (productTemplates.length === 1
                        ? t('publishBasic.autoApplied')
                        : t('publishBasic.createThenPick'))}
                  </p>
                </label>
              ) : (!isEdit && selectedProductTemplateId && versionConflicts.productTemplate) ? (
                <div className="tpo-field tpo-field--full tpo-field--version-conflict" data-field-error="productTemplate">
                  <TemplateVersionConflictMark
                    className="tpo-version-conflict-mark--block"
                    active
                    label={t('publishBasic.templateUpdated')}
                    reloading={reloadingConflictType === TEMPLATE_VERSION_TYPES.product}
                    onReload={() => reloadConflictingTemplateRegion(TEMPLATE_VERSION_TYPES.product)}
                  />
                </div>
              ) : null}

              <label className={`tpo-field tpo-field--full${fieldErrors.title ? ' tpo-field--invalid' : ''}`} data-field-error="title">
                <span className="tpo-field__label tpo-field__label-row">
                  <span>{t('publishBasic.productName')} <RequiredMark /></span>
                  <button
                    type="button"
                    className="tpo-btn tpo-btn--danger tpo-btn--sm"
                    disabled={aiTitleBusy || submitting || attrLoading}
                    title={buildCreditActionHint('aiCopywriting', 1) || undefined}
                    onClick={() => { void handleAiTitle(); }}
                  >
                    {aiTitleBusy ? t('publishBasic.generating') : t('publishBasic.aiTitle')}
                  </button>
                </span>
                <TitleFieldLoadingShell
                  loading={aiTitleBusy && !titleTranslateBusy}
                  label={t('publishBasic.aiTitleLoadingTemu')}
                >
                  <textarea
                    className="tpo-textarea tpo-textarea--title"
                    value={form.title}
                    rows={2}
                    readOnly={aiTitleBusy && !titleTranslateBusy}
                    onChange={(e) => update('title', clipTemuProductNameToLimit(e.target.value, titleMaxWeight))}
                    placeholder={t('publishBasic.titlePhTemu')}
                  />
                </TitleFieldLoadingShell>
                <span className="tpo-publish__counter" title={t('publishBasic.weightTitle')}>
                  {titleWeight}/{titleMaxWeight}
                </span>
                <FieldErrorText message={fieldErrors.title} />
              </label>

              <label className={`tpo-field tpo-field--full${fieldErrors.titleEn ? ' tpo-field--invalid' : ''}`} data-field-error="titleEn">
                <span className="tpo-field__label">
                  {t('publishBasic.titleEn')}
                </span>
                <TitleFieldLoadingShell
                  loading={titleTranslateBusy || aiTitleBusy}
                  label={titleTranslateBusy ? t('publishBasic.translateEn') : t('publishBasic.waitTitle')}
                >
                  <textarea
                    className="tpo-textarea tpo-textarea--title"
                    value={form.titleEn}
                    rows={2}
                    readOnly={titleTranslateBusy || aiTitleBusy}
                    onChange={(e) => update('titleEn', clipTemuProductNameToLimit(e.target.value, titleMaxWeight))}
                    placeholder={t('publishBasic.titleEnPh')}
                  />
                </TitleFieldLoadingShell>
                <span className="tpo-publish__counter" title={t('publishBasic.weightTitle')}>
                  {titleEnWeight}/{titleMaxWeight}
                </span>

                <FieldErrorText message={fieldErrors.titleEn} />
              </label>

              <label className={`tpo-field${fieldErrors.originRegion ? ' tpo-field--invalid' : ''}`} data-field-error="originRegion">
                <span className="tpo-field__label">{t('publishBasic.origin')} <RequiredMark /></span>
                <SearchableSelect
                  value={form.originRegion || 'CN'}
                  placeholder={t('publishBasic.pickOrigin')}
                  allowClear={false}
                  onChange={(v) => {
                    const nextOrigin = normalizeTemuOriginRegion(v) || 'CN';
                    setForm((prev) => ({
                      ...prev,
                      originRegion: nextOrigin,
                      originCountry: nextOrigin,
                      originRegion2Id: nextOrigin === 'CN' ? prev.originRegion2Id : '',
                    }));
                  }}
                  options={localizeRegionOptions(TEMU_REGION1_OPTIONS)}
                />
                <FieldErrorText message={fieldErrors.originRegion} />
              </label>
              {String(normalizeTemuOriginRegion(form.originRegion) || 'CN') === 'CN' ? (
                <label className={`tpo-field${fieldErrors.originRegion2Id ? ' tpo-field--invalid' : ''}`} data-field-error="originRegion2Id">
                  <span className="tpo-field__label">{t('publishBasic.originProvince')} <RequiredMark /></span>
                  <SearchableSelect
                    value={form.originRegion2Id}
                    placeholder={t('publishBasic.pickProvince')}
                    onChange={(v) => update('originRegion2Id', v)}
                    options={localizeCnProvinceOptions(CN_REGION2_OPTIONS)}
                  />
                  <FieldErrorText message={fieldErrors.originRegion2Id} />
                </label>
              ) : (
                <div className={`tpo-field tpo-field--full${fieldErrors.originCertFiles ? ' tpo-field--invalid' : ''}`} data-field-error="originCertFiles">
                  <span className="tpo-field__label">
                    {t('publishBasic.originCert')}
                    {isFullManaged ? <RequiredMark /> : null}
                  </span>
                  <p className="tpo-wizard__inline-hint">
                    {t('publishBasic.originCertHint')}
                  </p>
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                    disabled={originCertBusy || !shopId}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (!file || !shopId) return;
                      setOriginCertBusy(true);
                      try {
                        const res = await temuPlatformAPI.uploadPublishGoodsFile(shopId, file);
                        const url = String(res?.url || res?.fileUrl || '').trim();
                        if (!url) throw new Error(t('publishBasic.uploadNoUrl'));
                        update('originCertFiles', [{
                          fileName: String(res?.fileName || file.name),
                          fileUrl: url,
                        }]);
                        showToast(t('publishBasic.originUploaded'), 'success');
                      } catch (err) {
                        showToast(err?.message || t('publishBasic.originUploadFail'), 'error');
                      } finally {
                        setOriginCertBusy(false);
                      }
                    }}
                  />
                  {(form.originCertFiles || []).map((f, idx) => (
                    <div key={`${f.fileUrl}-${idx}`} className="tpo-guide-file__list" style={{ marginTop: 8 }}>
                      <span>{f.fileName || f.fileUrl}</span>
                      {' '}
                      <button
                        type="button"
                        className="tpo-btn tpo-btn--ghost tpo-btn--sm"
                        onClick={() => update('originCertFiles', [])}
                      >
                        {t('shop.delete')}
                      </button>
                    </div>
                  ))}
                  <FieldErrorText message={fieldErrors.originCertFiles} />
                </div>
              )}
              <div className={`tpo-field tpo-field--full${fieldErrors.customManufacturingLocations ? ' tpo-field--invalid' : ''}`} data-field-error="customManufacturingLocations">
                <span className="tpo-field__label">
                  {t('publishBasic.mfg')}
                  {isFullManaged ? <RequiredMark /> : null}
                </span>
                <SearchableMultiSelect
                  value={(form.customManufacturingLocations || []).map(String)}
                  onChange={(next) => update(
                    'customManufacturingLocations',
                    (Array.isArray(next) ? next : [])
                      .map((item) => resolveTemuRegion1ShortName(item))
                      .filter(Boolean),
                  )}
                  placeholder={isFullManaged ? t('publishBasic.mfgPh') : t('publishBasic.mfgOptional')}
                  options={localizeRegionOptions(TEMU_REGION1_OPTIONS)}
                />

                <FieldErrorText message={fieldErrors.customManufacturingLocations} />
              </div>
            </div>
          </div>

          {/* —— 类目属性 —— */}
          <div
            id="tpo-pub-attrs"
            className="tpo-wizard__block"
            onFocusCapture={() => setActiveSection('attrs')}
            data-field-error="productAttributes"
          >
            <div className="tpo-publish__block-head">
              <h3>{t('publishChrome.sectionAttrs')}</h3>
            </div>
            <FieldErrorText message={fieldErrors.productAttributes} />
            {!form.categoryId ? (
              <p className="tpo-wizard__inline-hint">{t('publishAttr.pickCategory')}</p>
            ) : attrLoading ? (
              <div className="tpo-attr-loading" role="status" aria-live="polite" aria-busy="true">
                <div className="tpo-attr-loading__banner">
                  <span className="tpo-attr-loading__spinner" aria-hidden />
                  <div className="tpo-attr-loading__copy">
                    <strong>{t('publishAttr.loadingTitle')}</strong>
                    <span>{t('publishAttr.loadingHint')}</span>
                  </div>
                </div>
                <div className="tpo-wizard__form tpo-wizard__form--attrs tpo-attr-loading__skeleton">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="tpo-attr-loading__card">
                      <span className="tpo-attr-loading__line tpo-attr-loading__line--label" />
                      <span className="tpo-attr-loading__line tpo-attr-loading__line--field" />
                    </div>
                  ))}
                </div>
              </div>
            ) : attrError && !(visibleRequiredAttrs.length || visibleOptionalAttrs.length) ? (
              <p className="tpo-wizard__inline-error" role="alert">{attrError}</p>
            ) : (
              <>
                {attrError ? <p className="tpo-wizard__inline-hint">{attrError}</p> : null}
                {allProductAttrs.length > 0 ? (
                  <div className={`tpo-size-chart-template-bar tpo-attr-template-bar${versionConflicts.productAttributes ? ' tpo-attr-template-bar--version-conflict' : ''}`}>
                    <TemplateVersionConflictMark
                      active={!!versionConflicts.productAttributes}
                      label={
                        changePreviews.productAttributes?.count != null
                          ? t('publishAttr.updatedCount', { count: changePreviews.productAttributes.count })
                          : t('photoLib.updatedMark')
                      }
                      reloading={reloadingConflictType === TEMPLATE_VERSION_TYPES.attribute}
                      resolveChangePreview={async () => (
                        changePreviews.productAttributes || buildAttrChangePreview()
                      )}
                      onReload={() => reloadConflictingTemplateRegion(TEMPLATE_VERSION_TYPES.attribute)}
                    />
                    <select
                      className="tpo-select"
                      value={selectedAttrTemplateId}
                      disabled={attrTemplateBusy || !shopId}
                      onChange={(e) => onAttrTemplateSelect(e.target.value)}
                    >
                      <option value="">{t('publishAttr.pickLib')}</option>
                      {attrTemplates.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                          {item.filledAttrCount != null ? t('publishAttr.filledCount', { count: item.filledAttrCount }) : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="tpo-btn tpo-btn--primary tpo-btn--sm"
                      disabled={attrTemplateBusy || !shopId || !form.categoryId}
                      onClick={openAttrSaveModal}
                    >
                      {t('shop.save')}
                    </button>
                  </div>
                ) : null}
                {visibleRequiredAttrs.length > 0 ? (
                  <>
                    <h4 className="tpo-publish__subhead">{t('publishAttr.requiredAttrs')}</h4>
                    <div className="tpo-wizard__form tpo-wizard__form--attrs">
                      {layoutAttributeFields(visibleRequiredAttrs).map(({ attr, fullWidth }) => (
                        <AttributeField
                          key={attr.attributeId}
                          attr={attr}
                          required={isEditorAttrRequiredMark(
                            attr,
                            form.productAttributes || {},
                            allProductAttrs,
                          )}
                          fullWidth={fullWidth}
                          shopId={shopId}
                          categoryId={form.categoryId}
                          allowCustomValue={!!customAttrPerm[String(attr.attributeId)]}
                          value={form.productAttributes[attr.attributeId]}
                          onChange={(v) => setAttrValue(attr.attributeId, v)}
                          onCustomValueAdded={appendCustomAttrValue}
                          versionUpdateHint={attrVersionHintById[String(attr.attributeId)] || null}
                          allowedValueIds={editorAttrAllowedValueIds(
                            attr,
                            form.productAttributes || {},
                            allProductAttrs,
                          )}
                        />
                      ))}
                    </div>
                  </>
                ) : null}

                {visibleOptionalAttrs.length > 0 ? (
                  <>
                    <h4 className="tpo-publish__subhead">{t('publishAttr.optionalAttrs')}</h4>
                    <div className="tpo-wizard__form tpo-wizard__form--attrs">
                      {layoutAttributeFields(visibleOptional).map(({ attr, fullWidth }) => (
                        <AttributeField
                          key={attr.attributeId}
                          attr={attr}
                          fullWidth={fullWidth}
                          shopId={shopId}
                          categoryId={form.categoryId}
                          allowCustomValue={!!customAttrPerm[String(attr.attributeId)]}
                          value={form.productAttributes[attr.attributeId]}
                          onChange={(v) => setAttrValue(attr.attributeId, v)}
                          onCustomValueAdded={appendCustomAttrValue}
                          versionUpdateHint={attrVersionHintById[String(attr.attributeId)] || null}
                          allowedValueIds={editorAttrAllowedValueIds(
                            attr,
                            form.productAttributes || {},
                            allProductAttrs,
                          )}
                        />
                      ))}
                    </div>
                    {visibleOptionalAttrs.length > 9 ? (
                      <div className="tpo-publish__expand">
                        <button type="button" className="tpo-btn tpo-btn--ghost tpo-btn--sm" onClick={() => setAttrsExpanded((v) => !v)}>
                          {attrsExpanded ? t('returnDetail.collapse') : t('publishAttr.expand', { count: visibleOptionalAttrs.length - 9 })}
                        </button>
                      </div>
                    ) : null}
                  </>
                ) : null}

                {!visibleRequiredAttrs.length && !visibleOptionalAttrs.length ? (
                  <p className="tpo-wizard__inline-hint">{t('publishAttr.none')}</p>
                ) : null}
              </>
            )}
          </div>

          {/* —— 规格供应 —— */}
          <div id="tpo-pub-spec" className="tpo-wizard__block" onFocusCapture={() => setActiveSection('spec')} data-field-error="skcList">
            <h3>{t('publishChrome.sectionSpecTitle')}</h3>
            <FieldErrorText message={fieldErrors.skcList || fieldErrors.supplierSku || fieldErrors.supply} />
            <SpecSupplySection
              colorAttr={colorAttr}
              sizeAttr={sizeAttr}
              skcList={form.skcList}
              selectedSizeIds={form.selectedSizeIds || []}
              shopId={shopId}
              categoryId={form.categoryId}
              customAttrPerm={customAttrPerm}
              onCustomValueAdded={appendCustomAttrValue}
              currency={platformCurrency}
              currencyPending={publishCurrencyLoading || !platformCurrency}
              attrLoading={attrLoading}
              isEdit={isEdit}
              priceMode={priceMode}
              priceEditable={!isEdit || Boolean(priceMode)}
              priceLockedHint={
                isEdit && !priceMode
                  ? t('publishSpec.priceLocked')
                  : ''
              }
              defaultSupplierSkuSpu={primarySupplierCode}
              printImages={form.printImages || []}
              onPrintImagesChange={updatePrintImages}
              printInfringement={form.ipePrintInfringement}
              printInfringementBusy={printInfringementBusy}
              onAssessPrintInfringement={handleAssessPrintInfringement}
              infringementCreditHint={buildCreditActionHint('infringementDetection', 1) || ''}
              materialImage={form.materialImage}
              materialImgError={fieldErrors.materialImgUrl}
              onMaterialImagesChange={onMaterialImagesChange}
              temuVideos={form.temuVideos || []}
              videoError={fieldErrors.temuVideos}
              onVideosChange={onVideosChange}
              onColorsChange={onColorsChange}
              onSizesChange={onSizesChange}
              onUpdateSkc={updateSkc}
              onUpdateSku={updateSku}
              onRemoveSku={removeSku}
              onBatchUpdateSku={batchUpdateSku}
              afterMaterialContent={(modelRequired || sizeChartRequired || sizeChartClassLoading || sizeChartClassError) ? (
                <>
                  <TemuModelSelectBar
                    shopId={shopId}
                    visible
                    required={modelRequired}
                    modelType={publishModelType}
                    value={form.goodsModels || []}
                    onChange={(next) => update('goodsModels', next)}
                    errorMessage={fieldErrors.goodsModels}
                    sizeOptions={modelTryOnSizeOptions}
                  />
                  {(sizeChartRequired && sizeChartClass?.classId && !sizeChartClassLoading) ? (
                    <TemuOfficialSizeChartPanel
                      key={`official-size-${shopId}-${form.categoryId}-${sizeChartClass?.classId || ''}-${selectedProductTemplateId || 'none'}`}
                      ref={officialSizeChartRef}
                      shopId={shopId}
                      categoryId={form.categoryId}
                      productTypeId={form.productTypeId}
                      categoryPathLabel={form.categoryPathLabel}
                      selectedSizeIds={form.selectedSizeIds}
                      selectedSizeLabels={selectedSizeLabels}
                      defaultStyleNo={resolvedAttrStyleNo}
                      required={sizeChartRequired}
                      sizeClass={sizeChartClass}
                      loading={sizeChartClassLoading}
                      errorMessage={fieldErrors.sizeChart}
                      initialDraft={form.officialSizeChart}
                      onDraftChange={onOfficialSizeChartDraftChange}
                    />
                  ) : null}
                  {sizeChartClassError ? (
                    <p className="tpo-wizard__inline-error" role="alert">{sizeChartClassError}</p>
                  ) : null}
                  {sizeChartClassLoading ? (
                    <p className="tpo-wizard__hint" aria-live="polite">{fixedUi("正在加载尺码表规则…")}</p>
                  ) : null}
                </>
              ) : null}
            />
          </div>

          <div className="tpo-wizard__block" onFocusCapture={() => setActiveSection('spec')}>
            <TemuGuideFilePanel
              shopId={shopId}
              needGuideFile={needGuideFile}
              needGuideLoading={needGuideLoading}
              files={form.guideFiles || []}
              onChange={(next) => update('guideFiles', next)}
              errorMessage={fieldErrors.guideFiles}
            />
          </div>

          {vehicleLibraryScopeActive ? (
            <div className="tpo-wizard__block" onFocusCapture={() => setActiveSection('attrs')}>
              <TemuVehicleFitmentPanel
                visible
                required={vehicleLibraryRequired}
                attrTpl={attrTpl}
                productAttributes={form.productAttributes}
                value={form.goodsVehiclePropertyRelation}
                onChange={(next) => update('goodsVehiclePropertyRelation', next)}
                errorMessage={fieldErrors.goodsVehiclePropertyRelation}
              />
            </div>
          ) : null}

          {/* —— 包装信息 —— */}
          <div
            id="tpo-pub-size"
            className="tpo-wizard__block"
            onFocusCapture={() => setActiveSection('size')}
          >
            <h3>{t('publishChrome.sectionSize')}</h3>

            <p className="tpo-wizard__inline-hint">
              {accessoryLoading ? ` ${t('publishPkg.loadingAcc')}` : null}
            </p>
            <div className="tpo-publish__table-wrap tpo-publish__table-wrap--scroll">
              <table className="tpo-publish__table tpo-pkg-table">
                <colgroup>
                  <col className="tpo-pkg-col tpo-pkg-col--color" />
                  <col className="tpo-pkg-col tpo-pkg-col--size" />
                  <col className="tpo-pkg-col tpo-pkg-col--sensitive" />
                  <col className="tpo-pkg-col tpo-pkg-col--dims" />
                  <col className="tpo-pkg-col tpo-pkg-col--weight" />
                  <col className="tpo-pkg-col tpo-pkg-col--net-weight" />
                  {semiSkuNetContentRequired ? (
                    <col className="tpo-pkg-col tpo-pkg-col--net-content" />
                  ) : null}
                  <col className="tpo-pkg-col tpo-pkg-col--sku-class" />
                  <col className="tpo-pkg-col tpo-pkg-col--accessories" />
                </colgroup>
                <thead>
                  <tr>
                    <th>{colorLabel}</th>
                    <th>{sizeLabel}</th>
                    <th className="tpo-pkg-table__sensitive">
                      <span className="tpo-supply-th">
                        <span className="tpo-th-with-help">
                          {t('publishPkg.sensitive')} <RequiredMark />
                        </span>
                        <button
                          type="button"
                          className="tpo-supply-th__batch"
                          onClick={() => setPkgBatchMode('sensitive')}
                        >
                          {t('publishSpec.batch')}
                        </button>
                      </span>
                    </th>
                    <th className="tpo-pkg-table__dims">
                      <span className="tpo-supply-th">
                        <span className="tpo-th-with-help">
                          {t('publishPkg.volume', { unit: TEMU_DIM_UNIT })} <RequiredMark />
                          <FieldHelpMark text={t('publishPkg.volumeHelpTemu')} />
                        </span>
                        <button
                          type="button"
                          className="tpo-supply-th__batch"
                          onClick={() => setPkgBatchMode('dims')}
                        >
                          {t('publishSpec.batch')}
                        </button>
                      </span>
                    </th>
                    <th className="tpo-pkg-table__weight">
                      <span className="tpo-supply-th">
                        <span>{t('publishPkg.weight')} <RequiredMark /></span>
                        <button
                          type="button"
                          className="tpo-supply-th__batch"
                          onClick={() => setPkgBatchMode('weight')}
                        >
                          {t('publishSpec.batch')}
                        </button>
                      </span>
                    </th>
                    <th className="tpo-pkg-table__net-weight">
                      <span className="tpo-supply-th">
                        <span className="tpo-th-with-help">
                          {t('publishPkg.goodsNet')}
                          {netWeightRequired ? <RequiredMark /> : null}
                          <FieldHelpMark text={netWeightRequired
                            ? t('publishPkg.netReqHelp')
                            : t('publishPkg.netOptHelp')}
                          />
                        </span>
                        <button
                          type="button"
                          className="tpo-supply-th__batch"
                          onClick={() => setPkgBatchMode('weight')}
                        >
                          {t('publishSpec.batch')}
                        </button>
                      </span>
                    </th>
                    {semiSkuNetContentRequired ? (
                      <th className="tpo-pkg-table__net-content">
                        <span className="tpo-supply-th">
                          <span className="tpo-th-with-help">
                            {t('publishPkg.netContent')}
                            <RequiredMark />
                            <FieldHelpMark text={t('publishPkg.netContentHelp')} />
                          </span>
                        </span>
                      </th>
                    ) : null}
                    <th className="tpo-pkg-table__sku-class">
                      <span className="tpo-supply-th">
                        <span className="tpo-th-with-help">
                          {t('publishPkg.skuClass')}
                          {semiSkuNetContentRequired ? <RequiredMark /> : null}
                          <FieldHelpMark text={t('publishPkg.skuClassHelpTemu')} />
                        </span>
                        <button
                          type="button"
                          className="tpo-supply-th__batch"
                          onClick={() => setPkgBatchMode('quantity')}
                        >
                          {t('publishSpec.batch')}
                        </button>
                      </span>
                    </th>
                    <th className="tpo-pkg-table__accessories">
                      <span className="tpo-supply-th">
                        <span className="tpo-th-with-help">
                          {t('publishPkg.accessories')}
                          <FieldHelpMark text={t('publishPkg.accHelp')} />
                        </span>
                        <button
                          type="button"
                          className="tpo-supply-th__batch"
                          onClick={() => setPkgBatchMode('accessories')}
                          disabled={accessoryLoading || !accessoryOptions.length}
                        >
                          {t('publishSpec.batch')}
                        </button>
                      </span>
                      {accessoryLoadError ? (
                        <div className="tpo-wizard__inline-hint" role="status">
                          {accessoryLoading ? t('publishPkg.loadingAcc') : accessoryLoadError}
                          {!accessoryLoading && accessoryLoadError ? (
                            <button
                              type="button"
                              className="tpo-supply-th__batch"
                              style={{ marginLeft: 8 }}
                              onClick={() => setAccessoryReloadKey((k) => k + 1)}
                            >
                              {t('commerce.retry')}
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {form.skcList.filter((skc) => skcHasColor(skc)).flatMap((skc) => {
                    const rows = skc.skuRows?.length ? skc.skuRows : [];
                    return rows.map((sku, rowIndex) => {
                      const sizeName = sku.sizeIsCustom || sku.sizeExtraValue
                        ? (attributeValueDisplayLabel(sku.sizeLabel || sku.sizeExtraValue) || '—')
                        : (
                          attributeValueDisplayLabel(
                            sizeAttr?.values?.find((v) => String(v.id) === String(sku.sizeValueId))
                            || sku.sizeLabel,
                          ) || '—'
                        );
                      const skuClass = String(sku.skuClassification || '1');
                      const isMulti = skuClass === '2' || skuClass === '3';
                      const sensitiveOn = Number(sku.isSensitive || 0) === 1;
                      const sensitiveSlots = sensitiveSlotsFromList(sku.sensitiveList, 3);
                      const pieceUnit = String(sku.pieceUnitCode || sku.quantityUnit || '1');
                      const pieceCount = sku.numberOfPieces ?? sku.quantity ?? '1';
                      const accessories = Array.isArray(sku.accessories) && sku.accessories.length
                        ? sku.accessories
                        : [createEmptyAccessoryRow()];
                      const filledAccessories = accessories.filter(
                        (row) => Number(row?.vid) > 0 && Number(row?.num) > 0,
                      );
                      const needAccessories = skuClass === '3';
                      return (
                        <tr key={`${skc.id}-${sku.id}-${rowIndex}`}>
                          {rowIndex === 0 ? (
                            <td rowSpan={rows.length} className="tpo-pkg-table__color">
                              {skc.colorLabel || '—'}
                            </td>
                          ) : null}
                          <td className="tpo-pkg-table__size">{sizeName}</td>
                          <td className="tpo-pkg-table__sensitive">
                            <div className="tpo-pkg-sensitive">
                              <SearchableSelect
                                value={String(sku.isSensitive ?? 0)}
                                allowClear={false}
                                onChange={(v) => updateSku(skc.id, sku.id, {
                                  isSensitive: Number(v) === 1 ? 1 : 0,
                                  ...(Number(v) !== 1 ? { sensitiveList: [] } : {}),
                                })}
                                options={localizeOptions(TEMU_SENSITIVE_YES_NO_OPTIONS, YES_NO_KEYS)}
                              />
                              {sensitiveOn ? (
                                <div className="tpo-pkg-sensitive__types">
                                  {sensitiveSlots.map((slotVal, slotIdx) => (
                                    <SearchableSelect
                                      key={`sens-${sku.id}-${slotIdx}`}
                                      value={slotVal}
                                      allowClear
                                      placeholder={t('shop.pleaseSelect')}
                                      onChange={(v) => {
                                        const nextSlots = [...sensitiveSlots];
                                        nextSlots[slotIdx] = v == null ? '' : String(v);
                                        updateSku(skc.id, sku.id, {
                                          sensitiveList: sensitiveListFromSlots(nextSlots),
                                        });
                                      }}
                                      options={localizeOptions(TEMU_SENSITIVE_LIST_OPTIONS, SENSITIVE_KEYS)}
                                    />
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          </td>
                          <td className="tpo-pkg-table__dims">
                            <div className="tpo-pkg-volume">
                              <label className="tpo-pkg-volume__item">
                                <span>{t('publishPkg.longest')}</span>
                                <input
                                  className="tpo-input"
                                  value={sku.length ?? ''}
                                  inputMode="decimal"
                                  onChange={(e) => updateSku(skc.id, sku.id, { length: e.target.value })}
                                  placeholder={t('publishPkg.input')}
                                />
                                <em>{TEMU_DIM_UNIT}</em>
                              </label>
                              <label className="tpo-pkg-volume__item">
                                <span>{t('publishPkg.mid')}</span>
                                <input
                                  className="tpo-input"
                                  value={sku.width ?? ''}
                                  inputMode="decimal"
                                  onChange={(e) => updateSku(skc.id, sku.id, { width: e.target.value })}
                                  placeholder={t('publishPkg.input')}
                                />
                                <em>{TEMU_DIM_UNIT}</em>
                              </label>
                              <label className="tpo-pkg-volume__item">
                                <span>{t('publishPkg.shortest')}</span>
                                <input
                                  className="tpo-input"
                                  value={sku.height ?? ''}
                                  inputMode="decimal"
                                  onChange={(e) => updateSku(skc.id, sku.id, { height: e.target.value })}
                                  placeholder={t('publishPkg.input')}
                                />
                                <em>{TEMU_DIM_UNIT}</em>
                              </label>
                            </div>
                          </td>
                          <td className="tpo-pkg-table__weight">
                            <div className="tpo-pkg-inline tpo-pkg-inline--weight">
                              <input
                                className="tpo-input tpo-pkg-inline__input"
                                value={sku.weight ?? ''}
                                inputMode="decimal"
                                onChange={(e) => updateSku(skc.id, sku.id, { weight: e.target.value })}
                                placeholder={t('publishPkg.input')}
                              />
                              <span className="tpo-pkg-inline__unit">{TEMU_WEIGHT_UNIT}</span>
                            </div>
                          </td>
                          <td className="tpo-pkg-table__net-weight">
                            <div className="tpo-pkg-inline tpo-pkg-inline--weight">
                              <input
                                className="tpo-input tpo-pkg-inline__input"
                                value={sku.netWeight ?? ''}
                                inputMode="decimal"
                                onChange={(e) => updateSku(skc.id, sku.id, { netWeight: e.target.value })}
                                placeholder={netWeightRequired ? t('publishPkg.input') : t('publishPkg.optionalPh')}
                              />
                              <span className="tpo-pkg-inline__unit">{TEMU_WEIGHT_UNIT}</span>
                            </div>
                          </td>
                          {semiSkuNetContentRequired ? (
                            <td className="tpo-pkg-table__net-content">
                              <div className="tpo-pkg-net-content">
                                <input
                                  className="tpo-input tpo-pkg-inline__input"
                                  value={sku.netContent ?? sku.netContentNumber ?? ''}
                                  inputMode="numeric"
                                  onChange={(e) => updateSku(skc.id, sku.id, { netContent: e.target.value })}
                                  placeholder={t('publishPkg.input')}
                                />
                                <SearchableSelect
                                  value={String(sku.netContentUnitCode || '5')}
                                  allowClear={false}
                                  onChange={(v) => updateSku(skc.id, sku.id, {
                                    netContentUnitCode: String(v || '5'),
                                  })}
                                  options={localizeOptions(TEMU_NET_CONTENT_UNIT_OPTIONS, NET_UNIT_KEYS)}
                                />
                              </div>
                            </td>
                          ) : null}
                          <td className="tpo-pkg-table__sku-class">
                            <div className="tpo-pkg-sku-class">
                              <SearchableSelect
                                value={skuClass}
                                allowClear={false}
                                onChange={(v) => updateSku(skc.id, sku.id, {
                                  skuClassification: String(v || '1'),
                                  ...(String(v || '1') === '1'
                                    ? { individuallyPacked: '', numberOfPieces: pieceCount || '1', quantity: pieceCount || '1' }
                                    : {}),
                                })}
                                options={localizeOptions(TEMU_SKU_CLASSIFICATION_OPTIONS, SKU_CLASS_KEYS)}
                              />
                              <div className="tpo-pkg-sku-class__row">
                                <span>{t('publishPkg.pieceQty')}</span>
                                <input
                                  className="tpo-input tpo-pkg-inline__input--qty"
                                  value={pieceCount}
                                  inputMode="numeric"
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    updateSku(skc.id, sku.id, {
                                      numberOfPieces: val,
                                      quantity: val,
                                    });
                                  }}
                                  placeholder={t('publishPkg.input')}
                                />
                                <SearchableSelect
                                  className="tpo-select--unit"
                                  value={pieceUnit}
                                  allowClear={false}
                                  onChange={(v) => updateSku(skc.id, sku.id, {
                                    pieceUnitCode: String(v || '1'),
                                    quantityUnit: String(v || '1'),
                                  })}
                                  options={localizeOptions(TEMU_PIECE_UNIT_OPTIONS, PIECE_UNIT_KEYS)}
                                />
                              </div>
                              <div className="tpo-pkg-sku-class__row">
                                <span>{t('publishPkg.totalContains')}</span>
                                <input
                                  className="tpo-input tpo-pkg-inline__input--qty"
                                  value={sku.totalContains ?? ''}
                                  inputMode="numeric"
                                  onChange={(e) => updateSku(skc.id, sku.id, {
                                    totalContains: e.target.value,
                                  })}
                                  placeholder={t('publishPkg.input')}
                                />
                                <span className="tpo-pkg-inline__unit">
                                  {optionLabel(TEMU_PIECE_UNIT_OPTIONS, PIECE_UNIT_KEYS, pieceUnit, t('publishPkg.piece'))}
                                </span>
                              </div>
                              {isMulti ? (
                                <SearchableSelect
                                  value={sku.individuallyPacked === 0 || sku.individuallyPacked === '0'
                                    ? '0'
                                    : (sku.individuallyPacked === 1 || sku.individuallyPacked === '1' ? '1' : '')}
                                  allowClear
                                  placeholder={t('publishPkg.indivPh')}
                                  onChange={(v) => updateSku(skc.id, sku.id, {
                                    individuallyPacked: v == null || v === '' ? '' : String(v),
                                  })}
                                  options={localizeOptions(TEMU_INDIVIDUALLY_PACKED_OPTIONS, PACKED_KEYS)}
                                />
                              ) : null}
                            </div>
                          </td>
                          <td className="tpo-pkg-table__accessories">
                            <div className="tpo-pkg-accessories">
                              {accessories.map((item, idx) => (
                                <div key={`${sku.id}-acc-${idx}`} className="tpo-pkg-accessories__row">
                                  <SearchableSelect
                                    value={item.vid ? String(item.vid) : ''}
                                    allowClear
                                    placeholder={
                                      accessoryLoading
                                        ? t('publishPkg.accLoading')
                                        : (accessoryOptions.length ? t('publishPkg.accSearch') : t('publishPkg.accNone'))
                                    }
                                    disabled={accessoryLoading || !accessoryOptions.length}
                                    onChange={(v) => {
                                      const opt = accessoryOptions.find((o) => o.value === String(v));
                                      const next = accessories.map((row, i) => (
                                        i === idx
                                          ? {
                                            ...row,
                                            vid: v ? Number(v) : '',
                                            label: opt?.label || '',
                                            unitCode: opt?.unitCode || row.unitCode || 1,
                                            unitName: opt?.unitName || row.unitName || '件',
                                            thumbUrl: opt?.thumbUrl || row.thumbUrl || '',
                                          }
                                          : row
                                      ));
                                      updateSku(skc.id, sku.id, { accessories: next });
                                    }}
                                    options={accessoryOptions}
                                  />
                                  <input
                                    className="tpo-input tpo-pkg-inline__input--qty"
                                    value={item.num ?? ''}
                                    inputMode="numeric"
                                    placeholder={t('publishPkg.qty')}
                                    onChange={(e) => {
                                      const next = accessories.map((row, i) => (
                                        i === idx ? { ...row, num: e.target.value } : row
                                      ));
                                      updateSku(skc.id, sku.id, { accessories: next });
                                    }}
                                  />
                                  <span className="tpo-pkg-inline__unit">
                                    {item.unitName
                                      || accessoryOptions.find((o) => o.value === String(item.vid))?.unitName
                                      || t('publishPkg.piece')}
                                  </span>
                                  <button
                                    type="button"
                                    className="tpo-pkg-accessories__del"
                                    title={t('publishPkg.deleteItem')}
                                    aria-label={t('publishPkg.deleteItem')}
                                    onClick={() => {
                                      const next = accessories.filter((_, i) => i !== idx);
                                      updateSku(skc.id, sku.id, {
                                        accessories: next.length ? next : [createEmptyAccessoryRow()],
                                      });
                                    }}
                                  >
                                    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
                                      <path
                                        fill="currentColor"
                                        d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v10h-2V9zm4 0h2v10h-2V9zM7 9h2v10H7V9zm-1 13h12a1 1 0 0 0 1-1V7H5v14a1 1 0 0 0 1 1z"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              ))}
                              <button
                                type="button"
                                className="tpo-pkg-accessories__add"
                                disabled={accessories.length >= 20 || accessoryLoading}
                                onClick={() => {
                                  updateSku(skc.id, sku.id, {
                                    accessories: [
                                      ...accessories,
                                      createEmptyAccessoryRow(),
                                    ],
                                  });
                                }}
                              >
                                {t('publishPkg.add')}
                              </button>
                              {needAccessories && !filledAccessories.length ? (
                                <span className="tpo-wizard__inline-hint">{t('publishPkg.mixNeed')}</span>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    });
                  })}
                </tbody>
              </table>
            </div>

            <h4 className="tpo-publish__subhead">{t('publishPkg.productPkg')}</h4>

            <div className="tpo-wizard__form tpo-wizard__form--2">
              <label className={`tpo-field${fieldErrors.packageShape ? ' tpo-field--invalid' : ''}`} data-field-error="packageShape">
                <span className="tpo-field__label">{t('publishPkg.outerShape')} <RequiredMark /></span>
                <SearchableSelect
                  value={form.packageShape || ''}
                  allowClear
                  placeholder={t('shop.pleaseSelect')}
                  onChange={(v) => update('packageShape', v == null ? '' : String(v))}
                  options={localizeOptions(TEMU_OUTER_PACKAGE_SHAPE_OPTIONS, OUTER_SHAPE_KEYS)}
                />
                {fieldErrors.packageShape ? (
                  <span className="tpo-field__error" role="alert">{fieldErrors.packageShape}</span>
                ) : null}
              </label>
              <label className={`tpo-field${fieldErrors.packageTypeOuter ? ' tpo-field--invalid' : ''}`} data-field-error="packageTypeOuter">
                <span className="tpo-field__label">{t('publishPkg.outerType')} <RequiredMark /></span>
                <SearchableSelect
                  value={form.packageTypeOuter || ''}
                  allowClear
                  placeholder={t('shop.pleaseSelect')}
                  onChange={(v) => update('packageTypeOuter', v == null ? '' : String(v))}
                  options={localizeOptions(TEMU_OUTER_PACKAGE_TYPE_OPTIONS, OUTER_TYPE_KEYS)}
                />
                {fieldErrors.packageTypeOuter ? (
                  <span className="tpo-field__error" role="alert">{fieldErrors.packageTypeOuter}</span>
                ) : null}
              </label>
            </div>

            <div
              className={`tpo-field tpo-outer-pkg-images${fieldErrors.outerPackageImages ? ' tpo-field--invalid' : ''}`}
              data-field-error="outerPackageImages"
            >
              <span className="tpo-field__label tpo-field__label-with-help">
                {t('publishPkg.outerImages')} <RequiredMark />
                <FieldHelpMark text={t('publishPkg.outerHelpTemu')} />
              </span>
              <p className="tpo-outer-pkg-images__hint">
                {t('publishPkg.outerHint')}
              </p>
              <PublishImageUploadZone
                compact
                labelInside
                label={t('publishPkg.outerImg')}
                imageType={1}
                imageBizType={1}
                imageRule="package"
                multiple
                maxCount={6}
                value={Array.isArray(form.outerPackageImages) ? form.outerPackageImages : []}
                shopId={shopId}
                onChange={(next) => {
                  update('outerPackageImages', Array.isArray(next) ? next : (next ? [next] : []));
                }}
              />
              {fieldErrors.outerPackageImages ? (
                <span className="tpo-field__error" role="alert">{fieldErrors.outerPackageImages}</span>
              ) : null}
            </div>

            {pkgBatchMode ? (
              <PackageBatchModal
                mode={pkgBatchMode}
                skcList={form.skcList}
                sizeAttr={sizeAttr}
                accessoryOptions={accessoryOptions}
                weightUnit={TEMU_WEIGHT_UNIT}
                dimUnit={TEMU_DIM_UNIT}
                netWeightRequired={netWeightRequired}
                onClose={() => setPkgBatchMode(null)}
                onConfirm={(result) => {
                  if (!result?.applyAll && !result?.bySize) {
                    setPkgBatchMode(null);
                    return;
                  }
                  setForm((prev) => ({
                    ...prev,
                    weightUnit: TEMU_WEIGHT_UNIT,
                    dimUnit: TEMU_DIM_UNIT,
                    skcList: result.applyAll
                      ? applyPackageBatchToAllSkus(prev.skcList, result.patch)
                      : applyPackageBatchBySize(prev.skcList, result.bySize),
                  }));
                  showToast(
                    result.applyAll
                      ? t('publishPkg.appliedAll')
                      : t('publishPkg.appliedBySizeTt'),
                    'success',
                  );
                  setPkgBatchMode(null);
                }}
              />
            ) : null}
          </div>

          {/* —— 销售发货：半托管填站点/仓/运费；定制品工艺全模式可见 —— */}
          <div id="tpo-pub-sales" className="tpo-wizard__block" onFocusCapture={() => setActiveSection('sales')}>
            <h3>{t('publishChrome.sectionSales')}</h3>

            {skipSiteWarehouseFreight ? (
              <p className="tpo-wizard__inline-hint">
                {t('publishSales.salesSkipTemu')}
              </p>
            ) : null}

            <div className="tpo-wizard__form">
              {!skipSiteWarehouseFreight ? (
                <>
                  {(isSemiManaged || shopCaps.usesPublishSiteList) ? (
                    <div className={`tpo-field tpo-field--full${fieldErrors.selectedSites ? ' tpo-field--invalid' : ''}`} data-field-error="selectedSites">
                      <span className="tpo-field__label">{t('tiktokPublish.listingSites')}{isEdit ? null : <RequiredMark />}</span>
                      {isEdit ? (
                        <>
                          <div className="tpo-readonly-sites" aria-label={t('tiktokPublish.listedSites')}>
                            {(form.selectedSites || []).length ? (
                              (form.selectedSites || []).map((abbr) => {
                                const site = isSemiManaged
                                  ? siteSelectOptions.find((s) => String(s.value) === String(abbr))
                                  : siteOptions.find((s) => String(s.siteAbbr) === String(abbr));
                                return (
                                  <span key={abbr} className="tpo-tag tpo-tag--ok">
                                    {site?.label || site?.siteName || abbr}
                                  </span>
                                );
                              })
                            ) : (
                              <span className="tpo-wizard__inline-hint">{t('tiktokPublish.noSiteInfo')}</span>
                            )}
                          </div>
                          <p className="tpo-wizard__inline-hint">{t('tiktokPublish.sitesLocked')}</p>
                        </>
                      ) : (
                        <SearchableSelect
                          value={(form.selectedSites || [])[0] || ''}
                          onChange={onSitesChange}
                          placeholder={
                            isSemiManaged
                              ? t('publishSales.pickTemu')
                              : (metaLoading ? t('tiktokPublish.loading') : t('tiktokPublish.pickOrSearch'))
                          }
                          disabled={!isSemiManaged && (metaLoading || siteSelectOptions.length === 0)}
                          emptyText={isSemiManaged ? t('tiktokPublish.noSites') : (metaLoading ? t('tiktokPublish.loading') : t('tiktokPublish.noSitesYet'))}
                          options={siteSelectOptions}
                        />
                      )}
                      <FieldErrorText message={fieldErrors.selectedSites} />
                    </div>
                  ) : (
                    <div className="tpo-field tpo-field--full">
                      <span className="tpo-field__label">{t('tiktokPublish.listingSites')}</span>
                      <p className="tpo-wizard__inline-hint">{t('tiktokPublish.sitesNotNeeded')}</p>
                    </div>
                  )}

                  <div className={`tpo-field tpo-field--full${fieldErrors.warehouseId ? ' tpo-field--invalid' : ''}`} data-field-error="warehouseId">
                    <span className="tpo-field__label">
                      {t('tiktokPublish.warehouse')}{multiWarehouse || isSemiManaged ? <RequiredMark /> : null}
                    </span>
                    {metaLoading ? (
                      <p className="tpo-wizard__inline-hint">{t('tiktokPublish.loadingWarehouses')}</p>
                    ) : warehouseOptions.length === 0 ? (
                      <p className="tpo-wizard__inline-hint">
                        {t('publishSales.noWarehousesTemu')}
                      </p>
                    ) : (
                      <SearchableSelect
                        value={form.warehouseId}
                        onChange={onWarehouseChange}
                        allowClear={false}
                        placeholder={t('tiktokPublish.pickWarehouse')}
                        emptyText={t('tiktokPublish.noWarehouseOption')}
                        options={warehouseOptions}
                      />
                    )}
                    <FieldErrorText message={fieldErrors.warehouseId} />
                  </div>

                  <div className={`tpo-field tpo-field--full${fieldErrors.freightTemplateId ? ' tpo-field--invalid' : ''}`} data-field-error="freightTemplateId">
                    <span className="tpo-field__label">
                      {t('tiktokPublish.freight')}{isSemiManaged ? <RequiredMark /> : null}
                    </span>
                    {freightLoading ? (
                      <p className="tpo-wizard__inline-hint">{t('tiktokPublish.loadingFreight')}</p>
                    ) : freightOptions.length === 0 ? (
                      <p className="tpo-wizard__inline-hint">{t('publishSales.noFreightTemu')}</p>
                    ) : (
                      <SearchableSelect
                        value={form.freightTemplateId}
                        onChange={(v) => update('freightTemplateId', v)}
                        allowClear={!isSemiManaged}
                        placeholder={t('publishSales.pickFreight')}
                        options={freightOptions}
                        placement="top"
                      />
                    )}
                    <FieldErrorText message={fieldErrors.freightTemplateId} />
                  </div>
                </>
              ) : null}

              <div className="tpo-field tpo-field--full">
                <span className="tpo-field__label">
                  {t('publishSales.productMode')} <RequiredMark />
                </span>
                <SearchableSelect
                  value={String(form.isCustomProduct ? 1 : 0)}
                  allowClear={false}
                  onChange={(v) => {
                    const custom = Number(v) === 1 ? 1 : 0;
                    const nextOpts = getTemuShipmentLimitOptions({
                      siteIds: form.selectedSites,
                      customProduct: custom,
                    });
                    setForm((prev) => ({
                      ...prev,
                      isCustomProduct: custom,
                      personalizationSwitch: custom,
                      shipmentLimitSecond: pickTemuShipmentLimitSecond(
                        prev.shipmentLimitSecond,
                        nextOpts,
                      ),
                      ...(custom ? {} : {
                        customFirstType: '',
                        customTechnologyType: '',
                        customTwiceTypes: [],
                      }),
                    }));
                  }}
                  options={TEMU_PRODUCT_MODE_OPTIONS.map((opt) => ({
                    ...opt,
                    label: opt.value === '1' ? t('publishSales.modeCustom') : t('publishSales.modeNormal'),
                  }))}
                  placement="top"
                />
              </div>

              {Number(form.isCustomProduct) === 1 ? (
                <>

                  <div className={`tpo-field tpo-field--full${fieldErrors.customTechnologyType ? ' tpo-field--invalid' : ''}`} data-field-error="customTechnologyType">
                    <span className="tpo-field__label">{t('publishSales.techType')}</span>
                    <SearchableSelect
                      value={String(form.customTechnologyType || '')}
                      allowClear
                      placeholder={t('publishSales.techPh')}
                      onChange={(v) => {
                        const nextType = v == null ? '' : String(v);
                        const nextFirstOpts = listTemuCustomFirstTypeOptions(nextType);
                        setForm((prev) => {
                          const keepFirst = nextFirstOpts.some((o) => String(o.value) === String(prev.customFirstType))
                            ? String(prev.customFirstType || '')
                            : '';
                          const allowed = listTemuCustomTwiceTypeIds(nextType, keepFirst);
                          const allowSet = allowed ? new Set(allowed.map(String)) : null;
                          const twice = (Array.isArray(prev.customTwiceTypes) ? prev.customTwiceTypes : [])
                            .map(String)
                            .filter((id) => (allowSet ? allowSet.has(id) : false));
                          return {
                            ...prev,
                            customTechnologyType: nextType,
                            customFirstType: keepFirst,
                            customTwiceTypes: nextType === '1' ? twice.slice(0, 1) : twice,
                          };
                        });
                      }}
                      options={TEMU_CUSTOM_TECHNOLOGY_TYPE_OPTIONS.map((opt) => ({
                        ...opt,
                        label: opt.value === '2' ? t('publishSales.techCombo') : t('publishSales.techSingle'),
                      }))}
                      placement="top"
                    />
                    <FieldErrorText message={fieldErrors.customTechnologyType} />
                  </div>
                  <div className={`tpo-field tpo-field--full${fieldErrors.customFirstType ? ' tpo-field--invalid' : ''}`} data-field-error="customFirstType">
                    <span className="tpo-field__label">{t('publishSales.firstTech')}</span>
                    <SearchableSelect
                      value={String(form.customFirstType || '')}
                      allowClear
                      placeholder={form.customTechnologyType ? t('publishSales.pickFirst') : t('publishSales.pickTechFirst')}
                      disabled={!form.customTechnologyType}
                      onChange={(v) => {
                        const nextFirst = v == null ? '' : String(v);
                        setForm((prev) => {
                          const allowed = listTemuCustomTwiceTypeIds(prev.customTechnologyType, nextFirst);
                          const allowSet = allowed ? new Set(allowed.map(String)) : null;
                          const twice = (Array.isArray(prev.customTwiceTypes) ? prev.customTwiceTypes : [])
                            .map(String)
                            .filter((id) => (allowSet ? allowSet.has(id) : false));
                          return {
                            ...prev,
                            customFirstType: nextFirst,
                            customTwiceTypes: String(prev.customTechnologyType) === '1'
                              ? twice.slice(0, 1)
                              : twice,
                          };
                        });
                      }}
                      options={customFirstTypeOptions}
                      placement="top"
                    />
                    <FieldErrorText message={fieldErrors.customFirstType} />
                  </div>
                  {customTwiceRequired ? (
                    <div className={`tpo-field tpo-field--full${fieldErrors.customTwiceTypes ? ' tpo-field--invalid' : ''}`} data-field-error="customTwiceTypes">
                      <span className="tpo-field__label">{t('publishSales.secondTech')}</span>
                      <SearchableMultiSelect
                        value={(form.customTwiceTypes || []).map(String)}
                        onChange={(next) => {
                          const list = Array.isArray(next) ? next.map(String) : [];
                          update(
                            'customTwiceTypes',
                            String(form.customTechnologyType) === '1' ? list.slice(0, 1) : list,
                          );
                        }}
                        placeholder={
                          String(form.customTechnologyType) === '2'
                            ? t('publishSales.twiceCombo')
                            : t('publishSales.twiceSingle')
                        }
                        options={customTwiceTypeOptions}
                      />
                      <p className="tpo-wizard__inline-hint">
                        {t('publishSales.twiceHint')}
                      </p>
                      <FieldErrorText message={fieldErrors.customTwiceTypes} />
                    </div>
                  ) : (
                    form.customTechnologyType && form.customFirstType ? (
                      <p className="tpo-wizard__inline-hint">{t('publishSales.noSecond')}</p>
                    ) : null
                  )}
                </>
              ) : null}

              {!skipSiteWarehouseFreight ? (
                <div className={`tpo-field tpo-field--full${fieldErrors.shipmentLimitSecond ? ' tpo-field--invalid' : ''}`} data-field-error="shipmentLimitSecond">
                  <span className="tpo-field__label">
                    {t('tiktokPublish.shipLimit')}{isSemiManaged ? <RequiredMark /> : null}
                  </span>
                  <SearchableSelect
                    value={pickTemuShipmentLimitSecond(form.shipmentLimitSecond, shipmentLimitOptions)}
                    allowClear={false}
                    onChange={(v) => update('shipmentLimitSecond', v)}
                    options={shipmentLimitOptions}
                    placement="top"
                    emptyText={form.selectedSites?.length ? t('publishSales.noShipLimit') : t('publishSales.pickSiteFirst')}
                  />
                  <FieldErrorText message={fieldErrors.shipmentLimitSecond} />
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="tpo-wizard__footer">
          <button
            type="button"
            className="tpo-btn tpo-btn--ghost"
            onClick={onSaveDraft}
            disabled={submitting || savingDraft || draftLoading || isEdit}
          >
            {savingDraft ? t('publishChrome.saving') : t('publishChrome.saveDraft')}
          </button>
          <div className="tpo-wizard__footer-right">
            <button
              type="button"
              className="tpo-btn tpo-btn--secondary"
              onClick={openProductTplSaveModal}
              disabled={submitting || loadingSpu || draftLoading || !draftHydrated || !shopId}
            >
              {t('publishChrome.saveTemplate')}
            </button>
            <button
              type="button"
              className="tpo-btn tpo-btn--primary"
              onClick={onSubmit}
              disabled={submitting || loadingSpu || draftLoading || !draftHydrated}
            >
              {submitting ? t('publishChrome.submitting') : (isEdit ? t('publishChrome.submitEdit') : t('publishChrome.publishProduct'))}
            </button>
          </div>
        </div>
        <PublishScrollJumpFab prefix="tpo" />
      </section>

      <SaveAttributeTemplateMetaModal
        open={attrSaveModalOpen}
        shopId={shopId}
        categoryId={form.categoryId}
        productTypeId={form.productTypeId}
        categoryPathLabel={form.categoryPathLabel}
        productAttributes={form.productAttributes}
        attrDefs={allProductAttrs}
        runWithoutUnloadPrompt={runWithoutUnloadPrompt}
        onClose={() => setAttrSaveModalOpen(false)}
        onSaved={refreshAttrTemplates}
      />
      <SaveProductTemplateMetaModal
        open={productTplSaveModalOpen}
        shopId={shopId}
        categoryId={form.categoryId}
        productTypeId={form.productTypeId}
        categoryPathLabel={form.categoryPathLabel}
        getFormSnapshot={() => persistTemuFormImageSlots(
          buildPublishFormSnapshot(form),
        )}
        runWithoutUnloadPrompt={runWithoutUnloadPrompt}
        onClose={() => setProductTplSaveModalOpen(false)}
      />
      <PriceUpdateConfirmModal
        open={Boolean(priceConfirm)}
        mode={priceConfirm?.changes?.mode || priceMode || 'cost'}
        shopId={shopId}
        changes={priceConfirm?.changes}
        onCancel={() => {
          if (!submitting) setPriceConfirm(null);
        }}
        onConfirm={async (meta) => {
          const result = await finishEditSubmit(
            priceConfirm.submitForm,
            priceConfirm.changes,
            meta,
          );
          if (!result?.ok) {
            throw new Error(result?.message || t('publishFlow.priceSubmitFail'));
          }
        }}
      />
    </div>
  );
}
